// web-frontend/src/shared/services/i18n.js
// 🔥 순환 참조 방지를 위해 독립적인 모듈로 수정됨 + 누락된 키 대거 추가
// 🔥 한글 DB 데이터 -> 다국어 키 매핑 기능 추가
// DSK - DSK 프로젝트

import logger from '../utils/logger';
import { formatDateInAppTimeZone, getLocaleFromLanguage } from '../utils/dateTime';
import { getEventName, getVenueName, getVenueFullName, getDefaultLanguage, getTenantId } from '../config/tenant';

const STORAGE_KEY = 'dsk_language';
// 🔥 [2026-05-21] v3: 테넌트별 기본 언어 적용
//   DSK = 'ko' (한국 행사), AP = 'en' (홍콩 영어 비즈니스 공용)
//   v2 에서는 모두 'en' 으로 강제 전환했으나, DSK 한국 사용자에게 부적절했음.
//   v3 마이그레이션: 이전에 v2 가 강제로 바꾼 'en' 값을 1회 한해 테넌트 기본값으로 리셋.
const DEFAULT_LANG_MIGRATION_V2 = 'dsk_language_default_v2';
const DEFAULT_LANG_MIGRATION_V3 = 'dsk_language_default_v3';

// 🔥 [추가] 한글 DB 데이터 -> i18n 키 매핑 (Reverse Map)
// 🔥 [2026-01-29 보완] 시드 데이터의 괄호 포함 label 형식도 완벽 대응
const REVERSE_CATEGORY_MAP = {
  // 기존 짧은 형식 (하위 호환성 유지)
  '드론 기체 및 하드웨어': 'drone_platforms',
  '군·국방·보안 드론': 'military_defense_uavs',
  '산업·상업용 드론 활용': 'industrial_uavs',
  '자율비행·관제·UAM': 'autonomous_flight_utm',
  '배터리·전원·충전 솔루션': 'battery_power_charging',
  '모터·프로펠러·부품': 'motors_propellers_components',
  'AI·소프트웨어·데이터': 'ai_software_data',
  '카메라·센서·통신·항법 기술': 'sensors_navigation_cameras',
  '무인이동체 확장 분야': 'ugv_usv_robotics',
  '우주·항공우주 기술': 'space_aerospace',
  '교육·훈련·서비스': 'training_education_services',
  '기타': 'other_interest',
  '지속가능한 항공기술': 'sustainable_aviation',
  '전체': 'all',
  
  // 🔥 시드 데이터의 괄호 포함 label 형식 (seed_onboarding_survey.py, seed_buyer_survey.py)
  '드론 기체 및 하드웨어 (Drone Platforms & Hardware)': 'drone_platforms',
  '군·국방·보안 드론 (Defense & Security UAVs)': 'military_defense_uavs',
  '산업·상업용 드론 활용 (Industrial UAVs: Inspection, Delivery, Filming)': 'industrial_uavs',
  '자율비행·관제·UAM (Autonomous Flight / UTM / UAM)': 'autonomous_flight_utm',
  '배터리·전원·충전 솔루션 (Battery / Power / Charging Solutions)': 'battery_power_charging',
  '모터·프로펠러·부품 (Motors / Propellers / Components)': 'motors_propellers_components',
  'AI·소프트웨어·데이터 (AI / Software / Data)': 'ai_software_data',
  '카메라·센서·통신·항법 기술 (Sensors / Navigation / Cameras, LiDAR, GNSS, Communications)': 'sensors_navigation_cameras',
  '무인이동체 확장 분야 (UGV / USV / Robotics)': 'ugv_usv_robotics',
  '우주·항공우주 기술 (Space & Aerospace)': 'space_aerospace',
  '교육·훈련·서비스 (Education / Training / Services)': 'training_education_services',
  '지속가능한 항공기술 (Sustainable Aviation)': 'sustainable_aviation',
  '기타 (Other)': 'other_interest'
};

Object.assign(REVERSE_CATEGORY_MAP, {
  'All': 'all',
  'Drone Platforms & Hardware': 'drone_platforms',
  'Military, Defense & Security Drones': 'military_defense_uavs',
  'Industrial & Commercial Drone Applications': 'industrial_uavs',
  'Autonomous Flight, UTM & UAM': 'autonomous_flight_utm',
  'Battery, Power & Charging Solutions': 'battery_power_charging',
  'Motors, Propellers & Components': 'motors_propellers_components',
  'AI, Software & Data': 'ai_software_data',
  'Cameras, Sensors, Communications & Navigation': 'sensors_navigation_cameras',
  'Unmanned Vehicle Expansion (UGV/USV/Robotics)': 'ugv_usv_robotics',
  'Space & Aerospace Technology': 'space_aerospace',
  'Training, Education & Services': 'training_education_services',
  'Sustainable Aviation': 'sustainable_aviation',
  'Other': 'other_interest'
});

/**
 * 다국어 지원 서비스
 * - 순환 참조 방지를 위해 storage 의존성 제거
 * - 표준 localStorage 직접 사용
 * - 번역 데이터 내장으로 즉시 사용 가능
 */
class I18nService {
  constructor() {
    // currentLanguage 는 _loadInitialLanguage 에서 테넌트 기본값으로 설정됨.
    // 초기 placeholder 만 'en' 으로 두고, _loadInitialLanguage 가 즉시 덮어씀.
    this.currentLanguage = 'en';
    this.fallbackLanguage = 'en';  // fallback 은 글로벌 'en' 유지 (번역 누락 시 영어로)
    this.initialized = false;
    this.listeners = [];  // 🔥 [추가] 언어 변경 리스너 배열

    // 🔥 생성 시점에 즉시 동기적으로 언어 설정 (비동기 의존성 제거)
    this._loadInitialLanguage();
    
    // 번역 데이터 로드
    this.translations = this.getDefaultTranslations();
    
    logger.debug('🌐 I18nService: 초기화 완료 (순환 참조 방지 버전)');
  }

  /**
   * 🔥 초기 언어 로드 (localStorage 직접 사용)
   * v3 (2026-05-21): 테넌트별 기본 언어 적용
   *   - DSK 도메인: 한글 기본
   *   - AP 도메인: 영어 기본
   *   - v2 가 잘못 강제했던 'en' 을 1회 리셋 (사용자 명시 변경은 보존)
   */
  _loadInitialLanguage() {
    try {
      const tenantDefault = (typeof window !== 'undefined') ? getDefaultLanguage() : 'en';
      const tenantId = (typeof window !== 'undefined') ? getTenantId() : 'DSK';

      // v3 마이그레이션: v2 에서 강제로 'en' 으로 바꾼 값을 1회 한해
      // 테넌트 기본 언어로 리셋 (사용자가 v2 이후 명시적으로 다른 언어를 골랐다면 보존).
      const v3Done = typeof window !== 'undefined'
        ? localStorage.getItem(DEFAULT_LANG_MIGRATION_V3)
        : null;
      if (typeof window !== 'undefined' && !v3Done) {
        const v2Done = localStorage.getItem(DEFAULT_LANG_MIGRATION_V2);
        const prev = localStorage.getItem(STORAGE_KEY);
        // v2 가 실행된 적이 있고 (= 자동으로 'en' 으로 설정됨) 사용자가 그 이후
        // 명시 변경 안 했다면 (값이 'en' 그대로) → 테넌트 기본값으로 복귀.
        if (v2Done === '1' && prev === 'en' && tenantDefault !== 'en') {
          localStorage.setItem(STORAGE_KEY, tenantDefault);
          logger.debug(`🌐 디폴트 언어 마이그레이션 v3: en → ${tenantDefault} (tenant=${tenantId})`);
        }
        localStorage.setItem(DEFAULT_LANG_MIGRATION_V3, '1');
      }

      // 1. 저장된 언어 확인
      const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;

      if (stored) {
        this.currentLanguage = stored;
        logger.debug(`✅ 저장된 언어: ${stored}`);
      } else {
        // 저장된 언어가 없으면 테넌트 기본 언어 사용
        this.currentLanguage = tenantDefault;
        logger.debug(`🌐 저장된 언어 없음 → 테넌트 기본 언어 적용: ${tenantDefault} (tenant=${tenantId})`);
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEY, tenantDefault);
        }
      }

      this.initialized = true;
    } catch (e) {
      logger.warn('⚠️ I18n 초기화 경고:', e);
      this.currentLanguage = 'en';
      this.initialized = true;
    }
  }

  /**
   * 초기화 (호환성 유지용, 실제로는 생성자에서 완료됨)
   */
  async initialize() {
    if (this.initialized) {
      logger.debug('✅ I18nService: 이미 초기화됨');
      return true;
    }
    
    this._loadInitialLanguage();
    return true;
  }

  /**
   * 언어 변경
   */
  async changeLanguage(language) {
    try {
      const supportedLanguages = ['ko', 'en', 'ja', 'zh'];
      const langCode = language.split('-')[0].toLowerCase();
      const supportedLanguage = supportedLanguages.includes(langCode) ? langCode : this.fallbackLanguage;
      
      if (supportedLanguage === this.currentLanguage) {
        logger.debug('ℹ️ 이미 동일한 언어입니다:', supportedLanguage);
        return true;
      }
      
      const prevLang = this.currentLanguage;  // 🔥 [추가] 이전 언어 저장
      this.currentLanguage = supportedLanguage;
      
      // localStorage에 저장
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, supportedLanguage);
      }
      
      // 🔥 [추가] 리스너들에게 알림
      this.listeners.forEach(listener => {
        try {
          listener(supportedLanguage, prevLang);
        } catch (e) {
          logger.error('언어 변경 리스너 오류:', e);
        }
      });
      
      // 🔥 [수정] CustomEvent로 변경하여 detail 객체 포함
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('languageChanged', {
          detail: { language: supportedLanguage, previousLanguage: prevLang }
        }));
      }
      
      logger.debug(`✅ 언어 변경 완료: ${prevLang} → ${supportedLanguage}`);
      return true;
    } catch (error) {
      logger.error('❌ 언어 변경 실패:', error);
      return false;
    }
  }

  /**
   * 현재 언어 가져오기
   */
  getLanguage() {
    return this.currentLanguage;
  }

  /**
   * 현재 언어 가져오기 (별칭)
   */
  getCurrentLanguage() {
    return this.getLanguage();
  }

  /**
   * 지원하는 언어 목록 조회
   */
  getSupportedLanguages() {
    return [
      { code: 'ko', name: 'Korean', nativeName: '한국어' },
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'ja', name: 'Japanese', nativeName: '日本語' },
      { code: 'zh', name: 'Chinese', nativeName: '中文' },
    ];
  }
  /**
   * 🔥 [추가] 언어 변경 리스너 추가
   * @param {Function} callback - 언어 변경 시 호출될 콜백 (newLang, prevLang)
   */
  addListener(callback) {
    if (typeof callback === 'function' && !this.listeners.includes(callback)) {
      this.listeners.push(callback);
    }
  }
  /**
   * 🔥 [추가] 언어 변경 리스너 제거
   * @param {Function} callback - 제거할 콜백 함수
   */
  removeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * 번역 가져오기
   * @param {string} key - 번역 키 (예: "common.loading", "auth.login")
   * @param {object} params - 치환할 파라미터 (예: {name: "John"})
   * @param {string} [fallback] - 키 미스 시 반환할 문구 (미지정 시 키 문자열 반환 — 기존 동작 유지).
   *   [2026-07-02] 기존 코드의 `t(key, {}, '폴백')` 3인자 호출을 정식 지원 (FABLE W-28).
   */
  t(key, params = {}, fallback = undefined) {
    try {
      const lang = this.currentLanguage;

      // 1. 현재 언어 -> 2. 폴백 언어 -> 3. 호출자 지정 fallback -> 4. 키 그대로 반환
      let translation = this.translations[lang]?.[key]
        || this.translations[this.fallbackLanguage]?.[key]
        || (fallback !== undefined && fallback !== null ? fallback : key);

      // 멀티테넌트: 활성 tenant 의 브랜드/행사장 정보를 자동 치환.
      //   {eventName}     → DSK: 'DSK 2026' / AP: 'AI Power +'
      //   {venueName}     → DSK: 'BEXCO'    / AP: 'HKCEC'
      //   {venueFullName} → DSK: '부산 BEXCO 제1전시장' / AP: '홍콩 컨벤션 전시센터' (현재 언어 적용)
      // 호출자가 명시한 params 가 우선.
      const allParams = {
        eventName: getEventName(),
        venueName: getVenueName(),
        venueFullName: getVenueFullName(lang),
        ...params,
      };
      Object.keys(allParams).forEach(param => {
        translation = translation.replace(
          new RegExp('\\{' + param + '\\}', 'g'),
          allParams[param],
        );
      });

      return translation;
    } catch (error) {
      logger.error('❌ 번역 가져오기 실패:', error);
      return (fallback !== undefined && fallback !== null) ? fallback : key;
    }
  }

  /**
   * 번역 가져오기 (별칭)
   */
  translate(key, params = {}, fallback = undefined) {
    return this.t(key, params, fallback);
  }

  /**
   * 🔥 [추가] 한글 카테고리명을 입력받아 현재 언어에 맞는 번역을 반환
   * @param {string} koLabel - DB에 저장된 한글 라벨 (예: "드론기체")
   * @returns {string} - 현재 언어에 맞게 번역된 카테고리명
   */
  translateCategory(label) {
    if (!label) return '';
    const rawLabel = String(label).trim();
    if (!rawLabel) return '';

    const directKey = rawLabel.startsWith('category.')
      ? rawLabel
      : `category.${rawLabel}`;
    if (this.translations[this.currentLanguage]?.[directKey] || this.translations[this.fallbackLanguage]?.[directKey]) {
      return this.t(directKey);
    }
    // 괄호와 영문이 포함된 경우(예: "한글 (English)") 한글 부분만 추출 시도
    const cleanLabel = rawLabel.split('(')[0].trim();
    
    const key = REVERSE_CATEGORY_MAP[rawLabel] || REVERSE_CATEGORY_MAP[cleanLabel];
    
    if (key) {
      return this.t(`category.${key}`);
    }
    return cleanLabel; // 매핑 실패 시 괄호를 제거한 한글만이라도 반환
  }

  /**
   * 특정 섹션의 모든 텍스트 가져오기
   * @param {string} section - 섹션 이름 (예: "common", "auth")
   */
  getTexts(section) {
    try {
      const lang = this.currentLanguage;
      const prefix = `${section}.`;
      const sectionTexts = {};
      
      const translations = this.translations[lang] || {};
      
      Object.keys(translations).forEach(key => {
        if (key.startsWith(prefix)) {
          const shortKey = key.substring(prefix.length);
          sectionTexts[shortKey] = translations[key];
        }
      });
      
      return sectionTexts;
    } catch (error) {
      logger.error('❌ 섹션 텍스트 조회 실패:', error);
      return {};
    }
  }

  /**
   * 날짜 포맷팅
   */
  formatDate(date, options = {}) {
    try {
      const defaultOptions = { year: 'numeric', month: 'long', day: 'numeric' };
      const formatOptions = { ...defaultOptions, ...options };

      return formatDateInAppTimeZone(date, getLocaleFromLanguage(this.currentLanguage), formatOptions);
    } catch (error) {
      logger.error('❌ 날짜 포맷팅 실패:', error);
      return date.toString();
    }
  }

  /**
   * 숫자 포맷팅
   */
  formatNumber(number, options = {}) {
    try {
      const locale = this.currentLanguage === 'zh' ? 'zh-CN' : this.currentLanguage;
      return number.toLocaleString(locale, options);
    } catch (error) {
      logger.error('❌ 숫자 포맷팅 실패:', error);
      return number.toString();
    }
  }

  /**
   * 통화 포맷팅
   */
  formatCurrency(amount, currency = 'KRW') {
    try {
      const locale = this.currentLanguage === 'zh' ? 'zh-CN' : this.currentLanguage;
      return amount.toLocaleString(locale, {
        style: 'currency',
        currency: currency,
      });
    } catch (error) {
      logger.error('❌ 통화 포맷팅 실패:', error);
      return `${amount} ${currency}`;
    }
  }

  /**
   * 🔥 완전 통합된 번역 데이터 (모든 언어, 모든 키 포함)
   */
  getDefaultTranslations() {
    return {
      ko: {
        // 앱 기본 정보
        'app.name': 'DSK',
        'app.subtitle': '{eventName}',
        'app.description': '{eventName} - 탄소중립 전시 플랫폼',
        
        // 사용자 타입 뱃지용 번역
        'userTypes.VISITOR': '일반 관람객',
        'userTypes.BUSINESS': '비즈니스',
        'userTypes.COMPANY': '참가 기업',
        'userTypes.BUYER': '바이어',
        'userTypes.ADMIN': '관리자',
        'userTypes.BUSINESS_PENDING': '승인 대기',
        'userTypes.INVESTOR': '투자자',
        'userTypes.BUYER_PENDING': '승인 대기',



        // 공통 텍스트
        'common.ok': '확인',
        'common.cancel': '취소',
        'common.save': '저장',
        'common.delete': '삭제',
        'common.edit': '수정',
        'common.search': '검색',
        'common.filter': '필터',
        'common.sort': '정렬',
        'common.loading': '로딩 중...',
        'common.error': '오류가 발생했습니다',
        'common.success': '성공',
        'common.retry': '다시 시도',
        'common.load_failed': '데이터를 불러오지 못했습니다. 네트워크 상태를 확인해주세요.',
        'survey.complete_first': '설문 완료 후 이용할 수 있습니다.',
        'auth.verification_code_required': '6자리 인증코드를 입력해주세요.',
        'auth.verification_code_invalid': '인증코드가 일치하지 않습니다.',
        'auth.sms_verify_first': 'SMS 인증을 먼저 완료해주세요.',
        'common.refresh': '새로고침',
        'common.back': '뒤로가기',
        'common.next': '다음',
        'common.previous': '이전',
        'common.close': '닫기',
        'common.share': '공유',
        'common.copy': '복사',
        'common.download': '다운로드',
        'common.upload': '업로드',
        'common.send': '전송',
        'common.submit': '제출',
        'common.reset': '초기화',
        'common.confirm': '확인',
        'common.logout': '로그아웃',
        'common.guest': '게스트',
        'common.saved': '저장되었습니다.',
        'common.deleted': '삭제되었습니다.',
        'common.confirm_delete': '정말 삭제하시겠습니까?',
        'common.uncategorized': '미분류',
        'common.count_unit': '건',
        'common.count_suffix': '회',
        'common.minutes': '분',
        'common.hours': '시간',
        'common.days': '일',
        'common.user': '사용자',
        'common.no_email': '이메일 없음',
        'common.no_name': '이름 없음',
        'common.sending': '전송 중...',
        'common.close': '닫기',
        'common.uploading': '업로드 중...',
        'common.saving': '저장 중...',
        // 인증 관련
        'auth.login': '로그인',
        'auth.login_required': '로그인이 필요합니다.',

        // 🔥 [추가] 기업 상세 페이지 및 카테고리 관련
        'company.intro_title': '회사 소개',
        
        // 🔥 [추가] 카테고리 번역 (CompaniesPage의 INTEREST_CATEGORIES와 매핑)
        'category.all': '전체',
        'category.drone_platforms': '드론 기체 및 하드웨어',
        'category.military_defense_uavs': '군·국방·보안 드론',
        'category.industrial_uavs': '산업·상업용 드론 활용',
        'category.autonomous_flight_utm': '자율비행·관제·UAM',
        'category.battery_power_charging': '배터리·전원·충전 솔루션',
        'category.motors_propellers_components': '모터·프로펠러·부품',
        'category.ai_software_data': 'AI·소프트웨어·데이터',
        'category.sensors_navigation_cameras': '카메라·센서·통신·항법 기술',
        'category.ugv_usv_robotics': '무인이동체 확장 분야',
        'category.space_aerospace': '우주·항공우주 기술',
        'category.training_education_services': '교육·훈련·서비스',
        'category.sustainable_aviation': '지속가능한 항공기술',
        'category.other_interest': '기타',
        'category.group_basic': '기본',
        'category.group_airframe': '기체/플랫폼',
        'category.group_technology': '기술/시스템',
        'category.group_power': '동력/부품',
        'category.group_unmanned': '무인이동체',
        'category.group_services': '교육/서비스',
        'category.group_other': '기타',

        'auth.logout': '로그아웃',
        'auth.register': '회원가입',
        'auth.spc_exhibitor_notice': '참가기업이신가요? 비즈니스 방문자로 가입한 뒤, 기업 인증을 거쳐 기업 회원으로 승급됩니다.', // 🔥 [2026-06-10 B-3 D1] SPC exhibitor 유입 안내
        'welcome.return_to_guide': '전시 가이드로 돌아가기', // 🔥 [2026-06-10 B-3 D4] SPC return_url CTA
        'auth.email': '이메일',
        'auth.login_id': '이메일 또는 아이디(회사명)',
        'year_consent.title': '{year} 행사 이용 동의 확인',
        'year_consent.message': '작년 계정으로 {year} 행사에도 그대로 이용하실 수 있습니다. 올해 이용을 위해 아래 동의를 다시 확인해 주세요.',
        'year_consent.all': '전체 동의',
        'year_consent.terms': '[필수] 이용약관 동의',
        'year_consent.privacy': '[필수] 개인정보 수집·이용 동의',
        'year_consent.third_party': '[필수] 개인정보 제3자 제공 동의 (비즈니스 매칭)',
        'year_consent.third_party_info': '비즈니스 매칭 시 상대 기업에 회사명·담당자명·연락처·관심분야가 제공됩니다.',
        'year_consent.sms': '[선택] SMS 알림 수신',
        'year_consent.email': '[선택] 이메일 광고성 정보 수신',
        'year_consent.night': '[선택] 야간(21~08시) 광고 수신',
        'year_consent.view': '보기',
        'year_consent.hint': '선택 항목은 프로필 > 알림 설정에서 언제든 바꿀 수 있습니다.',
        'year_consent.required_missing': '필수 항목에 동의해 주세요.',
        'year_consent.save_failed': '저장하지 못했습니다. 다시 시도해 주세요.',
        'year_consent.logout': '로그아웃',
        'year_consent.submit': '동의하고 계속하기',
        'year_consent.terms_title': '이용약관',
        'year_consent.privacy_title': '개인정보처리방침',
        'year_consent.confirm_agree': '확인 및 동의',
        'year_consent.close': '닫기',
        'auth.password': '비밀번호',
        'auth.show_password': '비밀번호 보기',
        'auth.hide_password': '비밀번호 숨기기',
        'auth.passwordConfirm': '비밀번호 확인',
        'auth.name': '이름',
        'auth.phone': '전화번호',
        'auth.company': '회사명',
        'auth.forgotPassword': '비밀번호 찾기',
        'auth.resetPassword': '비밀번호 재설정',
        'auth.socialLogin': '소셜 로그인',
        'auth.loginWithGoogle': 'Google로 로그인',
        'auth.loginWithKakao': 'Kakao로 로그인',
        'auth.loginWithNaver': 'Naver로 로그인',
        'auth.loginWithApple': 'Apple로 로그인',
        'auth.title_sub': '{eventName}',
        'auth.guest': '게스트',
        'auth.guest_login': '게스트',
        'auth.auto_login': '자동 로그인',
        'auth.forgot_pw': '비밀번호 찾기',
        'auth.no_account': '계정이 없으신가요?',
        'auth.error_email': '이메일을 입력해주세요',
        'auth.error_pw': '비밀번호를 입력해주세요',
        'auth.fail_login': '로그인에 실패했습니다.',
        
        // 네비게이션
        'nav.home': '홈',
        'nav.companies': '기업',
        'nav.meetings': '미팅',
        'nav.chats': '채팅',
        'nav.carbon': '탄소 포인트',
        'nav.profile': '프로필',
        'nav.settings': '설정',
        'nav.notifications': '알림',
        'nav.help': '도움말',
        'nav.events': '이벤트',
        'nav.activities': '활동',
        'nav.map': '지도',
        'nav.matching': '매칭',
        
        // 프로필 페이지
        'profile.language_setting': '언어 설정',
        'profile.language_desc': '앱 전체 언어를 변경합니다',
        'profile.change_language': '언어 변경',
        'profile.interests_saved': '관심분야가 저장되었습니다.',
        'profile.interests_saved_refreshing': '관심분야가 저장되었습니다. 매칭이 잠시 후 갱신됩니다.',
        'profile.interests_no_change': '변경된 항목이 없습니다.',
        'profile.interests_save_failed': '관심분야 저장에 실패했습니다.',
        'profile.my_info': '내 정보',
        'profile.profile_settings': '프로필 및 계정 설정',
        'profile.company_info': '소속 회사 정보 관리',
        'profile.company_info_desc': '기업 소개 및 제품 정보 관리',
        'profile.carbon_points': '탄소 포인트',
        'profile.carbon_desc': '이동수단별 탄소 감축량 계산',
        'profile.bookmarks': '관심 이벤트',
        'profile.bookmarks_desc': '관심 기업 및 이벤트 통합 관리',
        'profile.notifications': '알림',
        'profile.notifications_desc': '푸시 및 이메일 알림 관리',
        'profile.settings': '설정',
        'profile.settings_desc': '언어, 버전 정보',
        'profile.activity_details': '활동 상세',
        'profile.total_matches': '매칭 횟수',
        'profile.completed_meetings': '완료된 미팅',
        'profile.completed_events': '참석 이벤트',
        // 🔥 [추가] 프로필 동적 텍스트
        'profile.carbon_total_saved': '🌱 총 {amount}kg 감축',
        'profile.tree_effect': '🌲 나무 {count}그루 효과',
        'profile.activity_hint': '🌱 활동을 기록해보세요!',
        'profile.badges': '뱃지',
        'profile.no_badges': '부스를 방문하면 뱃지를 받을 수 있습니다.',
        'profile.badge.explorer': '탐험가',
        'profile.badge.curator': '큐레이터',
        'profile.badge.master': '마스터',
        'profile.badge_threshold': '{count}개',
        'profile.badge_tooltip': '{name} ({count}개 부스 방문)',
        
        // 앱 설정
        'settings.title': '앱 설정',
        'settings.language': '언어',
        'settings.language_label': '앱 언어',
        'settings.language_desc': '* 일부 콘텐츠는 원본 언어로 표시될 수 있습니다.',
        'settings.screen': '화면',
        'settings.theme': '테마',
        'settings.theme_light': '라이트 모드',
        'settings.theme_dark': '다크 모드',
        'settings.theme_system': '시스템 설정',
        'settings.font_size': '글꼴 크기',
        'settings.font_small': '작게',
        'settings.font_medium': '보통',
        'settings.font_large': '크게',
        'settings.media': '미디어',
        'settings.autoplay': '동영상 자동 재생',
        'settings.autoplay_desc': 'Wi-Fi 연결 시 동영상을 자동 재생합니다',
        'settings.data_saver': '데이터 절약 모드',
        'settings.data_saver_desc': '이미지 품질을 낮춰 데이터 사용량을 줄입니다',
        'settings.data_mgmt': '데이터 관리',
        'settings.clear_cache': '캐시 삭제',
        'settings.clear_cache_desc': '임시 저장된 데이터를 삭제합니다',
        'settings.clear_data': '모든 데이터 삭제',
        'settings.clear_data_desc': '로그아웃되며 모든 앱 데이터가 삭제됩니다',
        'settings.app_info': '앱 정보',
        'settings.version': '앱 버전',
        'settings.build': '빌드 번호',
        'settings.developer': '개발사',
        'settings.terms': '이용약관',
        'settings.privacy': '개인정보처리방침',
        'settings.licenses': '오픈소스 라이선스',
        'settings.alert_cache_cleared': '캐시가 삭제되었습니다.',
        'settings.modal_delete_title': '⚠️ 데이터 삭제',
        'settings.modal_delete_confirm': '정말 모든 데이터를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.',
        'settings.font_size_desc': '앱 전체 글꼴 크기를 변경합니다.',
        'settings.data_saver_on': '✓ 활성화됨 - 썸네일 이미지 사용 중',
        'settings.data_saver_off': '비활성화됨 - 고품질 이미지 사용 중',


        // 내 정보 수정
        'myinfo.title': '내 정보 수정',
        'myinfo.profile_image': '프로필 사진',
        'myinfo.profile_alt': '프로필 이미지',
        'myinfo.change_photo': '사진 변경',
        'myinfo.photo_change_coming_soon': '사진 변경 기능은 준비 중입니다.',
        // 🔥 [추가] 탄소 기여도 카드 관련 키
        'myinfo.carbon_impact': '나의 탄소 기여도',
        'myinfo.tree_equivalent': '나무 심기 환산',
        'myinfo.trees': '그루',
        'myinfo.view_carbon_details': '탄소 감축 상세 보기',
        'myinfo.basic_info': '기본 정보',
        'myinfo.name': '이름',
        'myinfo.name_placeholder': '이름을 입력하세요',
        'myinfo.email': '이메일',
        'myinfo.email_desc': '이메일은 변경할 수 없습니다',
        'myinfo.email_unchangeable': '이메일은 변경할 수 없습니다.',
        'myinfo.phone': '전화번호',
        'myinfo.phone_placeholder': '전화번호를 입력하세요',
        'myinfo.work_info': '직장 정보',
        'myinfo.department': '부서',
        'myinfo.department_placeholder': '부서를 입력하세요',
        'myinfo.position': '직책',
        'myinfo.position_select': '직책을 선택해주세요',
        'myinfo.position.staff': '사원',
        'myinfo.position.senior': '주임',
        'myinfo.position.assistant_manager': '대리',
        'myinfo.position.manager': '과장',
        'myinfo.position.deputy_manager': '차장',
        'myinfo.position.general_manager': '부장',
        'myinfo.position.director': '이사',
        'myinfo.position.executive': '임원',
        'myinfo.position.ceo': '대표',
        'myinfo.position.other': '기타',
        'myinfo.bio': '자기소개',
        'myinfo.bio_title': '한 줄 소개',
        'myinfo.bio_label': '한 줄 소개',
        'myinfo.bio_placeholder': '간단한 자기소개를 작성해주세요 (B2B 매칭 시 표시됩니다)',
        'myinfo.interests_title': '관심 분야',
        'myinfo.interests_desc': '관심 있는 카테고리를 선택하고, 구체적으로 찾는 제품이나 기술을 자유롭게 입력해주세요.',
        'myinfo.interest_text_label': '관심 제품/기술 (자유 기재)',
        'myinfo.interest_text_placeholder': '관심 있는 제품이나 기술을 자유롭게 입력해주세요.',
        'myinfo.save_interests': '관심 분야 저장',
        'myinfo.categories_count': '선택',
        'myinfo.error_max_categories': '관심 분야는 최대 5개까지 선택할 수 있습니다.',
        'myinfo.alert_name_required': '이름을 입력해주세요',
        'myinfo.error_name_required': '이름은 필수 입력 항목입니다.',
        'myinfo.alert_phone_invalid': '올바른 전화번호 형식이 아닙니다',
        'myinfo.error_phone_invalid': '유효한 전화번호 형식이 아닙니다.',
        'myinfo.photo_updated': '프로필 사진이 변경되었습니다.',
        'myinfo.business_card': '명함',
        'myinfo.business_card_desc': '명함 이미지를 등록하면 비즈니스 미팅 시 상대방에게 표시됩니다.',
        'myinfo.business_card_alt': '명함 이미지',
        'myinfo.business_card_uploaded': '명함 이미지가 업로드되었습니다. 저장 버튼을 눌러 반영하세요.',
        'myinfo.change_business_card': '명함 변경',
        'myinfo.remove_business_card': '명함 삭제',
        'myinfo.confirm_remove_business_card': '명함 이미지를 삭제하시겠습니까?',
        'myinfo.upload_business_card': '명함 이미지 업로드',
        'myinfo.upload_business_card_hint': '클릭하여 이미지를 선택하세요 (최대 5MB)',
        'myinfo.business_card_notice': '명함 이미지 변경 후 하단의 "저장" 버튼을 눌러야 반영됩니다.',
        // 🔥 [추가] 보안/비밀번호 관련 키
        'myinfo.security': '보안',
        'myinfo.password_desc': '계정 보안을 위해 정기적으로 비밀번호를 변경하는 것을 권장합니다.',
        'myinfo.change_password': '비밀번호 변경',
        'myinfo.current_password': '현재 비밀번호',
        'myinfo.new_password': '새 비밀번호',
        'myinfo.confirm_password': '새 비밀번호 확인',
        'myinfo.password_changed': '비밀번호가 성공적으로 변경되었습니다.',
        'myinfo.password_hint': '비밀번호는 8자 이상으로 설정해주세요.',
        'myinfo.change_password_btn': '변경하기',
        // 계정 초기 설정 (ChangePasswordPage)
        'setup.title': '계정 초기 설정',
        'setup.subtitle': '보안을 위해 임시 비밀번호를 변경해주세요.',
        'setup.subtitle_survey': '설정 완료 후 {survey}로 이동합니다.',
        'setup.survey': '설문조사',
        'setup.current_password': '현재 (임시) 비밀번호 *',
        'setup.change_info': '변경할 정보',
        'setup.new_email': '실제 사용할 이메일 (ID로 사용됨) *',
        'setup.contact_name': '담당자 성함 *',
        'setup.phone_label': '담당자 전화번호',
        'setup.phone_required': '* 인증 필수',
        'setup.phone_placeholder': '전화번호 입력',
        'setup.select_country': '국가 선택',
        'setup.no_results': '검색 결과 없음',
        'setup.send_code': '인증요청',
        'setup.resend_code': '재발송',
        'setup.verified': '인증완료',
        'setup.new_password': '새 비밀번호 (8자 이상) *',
        'setup.confirm_password': '새 비밀번호 확인 *',
        'setup.sms_consent': 'SMS 수신에 동의합니다 (선택)',
        'setup.submit': '설정 완료',
        'setup.complete_alert': '계정 초기 설정이 완료되었습니다.\n입력하신 이메일로 다시 로그인해주세요.',
        'setup.email_invalid': '올바른 이메일 형식이 아닙니다.',
        'setup.name_required': '이름을 입력해주세요.',
        'setup.phone_invalid_kr': '올바른 휴대폰 번호를 입력해주세요. (예: 010-1234-5678)',
        'setup.overseas_notice': '해외 번호는 국가번호 제외, 0을 빼고 입력해주세요.',
        'setup.sms_description': '미팅 알림, 이벤트 리마인더 등을 SMS로 받아보실 수 있습니다.',
        'setup.sms_optional_note': '동의하지 않아도 계정 설정 및 서비스 이용에 제한이 없습니다.',
        'setup.notice': '설정 완료 후 입력하신 이메일(ID)로 다시 로그인해야 합니다.',
        'setup.notice_label': '안내',
        'setup.processing': '처리 중...',
        'setup.phone_verified': '전화번호 인증이 완료되었습니다.',
        'setup.enter_code': '문자로 전송된 6자리 인증코드를 입력해주세요.',
        'setup.code_placeholder': '인증코드 6자리',
        'setup.confirm': '확인',
        'myinfo.error_current_password_required': '현재 비밀번호를 입력해주세요.',
        'myinfo.error_new_password_required': '새 비밀번호를 입력해주세요.',
        'myinfo.error_password_min_length': '비밀번호는 8자 이상이어야 합니다.',
        'myinfo.error_confirm_password_required': '비밀번호 확인을 입력해주세요.',
        'myinfo.error_password_mismatch': '비밀번호가 일치하지 않습니다.',
        'myinfo.error_current_password_wrong': '현재 비밀번호가 올바르지 않습니다.',
        // 🔥 [추가] 비밀번호 실시간 검증 메시지
        'myinfo.password_valid': '사용 가능한 비밀번호입니다.',
        'myinfo.password_match': '비밀번호가 일치합니다.',
        'myinfo.current_password_valid': '비밀번호가 확인되었습니다.',

        // 🔥 [추가] 비밀번호 변경 관련 추가 키
        'error.password_change_failed': '비밀번호 변경에 실패했습니다.',
        'myinfo.current_password_placeholder': '현재 비밀번호 입력',
        'myinfo.new_password_placeholder': '8자 이상 입력',
        'myinfo.confirm_password_placeholder': '새 비밀번호 다시 입력',
        'myinfo.intl_phone_hint': '국가 코드를 선택한 후 전화번호를 입력해주세요.',
        'myinfo.phone_placeholder_intl': '전화번호 (국가코드 제외)',

        'common.processing': '처리 중...',


        
        // 회사 정보
        'company_info.title': '회사 정보',
        'company_info.no_company': '등록된 회사가 없습니다',
        'company_info.no_company_desc': '회사를 등록하면 B2B 매칭 서비스를 이용할 수 있습니다.',
        'company_info.register_btn': '회사 등록하기',
        'company_info.basic': '기본 정보',
        'company_info.name': '회사명',
        'company_info.industry': '산업 분류',
        'company_info.desc_label': '회사 소개',
        'company_info.desc_placeholder': '회사에 대한 간단한 소개를 작성해주세요',
        'company_info.details': '상세 정보',
        'company_info.founded': '설립연도',
        'company_info.employees': '직원 수',
        'company_info.website': '웹사이트',
        'company_info.address': '주소',
        'company_info.contact': '연락처',
        'company_info.email': '대표 이메일',
        'company_info.phone': '대표 전화번호',
        'company_info.visit': '방문하기',
        'company_info.main_products_label': '주요 제품/서비스',
        'company_info.main_products_placeholder': '제품명을 쉼표(,)로 구분하여 입력하세요',
        'company_info.main_products_hint': '예: 산업용 드론, 촬영 장비, 소프트웨어 솔루션',
        'company_info.media_section': '배너 · 홍보 영상',
        'company_info.banner_label': '배너 이미지',
        'company_info.banner_hint': '기업 상세 페이지 상단에 표시됩니다. 가로로 긴 이미지(권장 1200×400 이상)를 사용하세요.',
        'company_info.banner_upload': '배너 업로드',
        'company_info.banner_uploading': '업로드 중…',
        'company_info.banner_remove': '배너 제거',
        'company_info.banner_upload_failed': '배너 업로드에 실패했습니다',
        'company_info.video_label': '홍보 영상 URL',
        'company_info.video_hint': 'YouTube 등 영상 링크를 입력하세요. 기업 상세 페이지에 표시됩니다.',
        'company_info.video_view': '영상 보기',
        'company_info.edit_submitted': '수정 요청이 관리자 검토를 위해 제출되었습니다.',
        'company_info.pending_edit': '대기 중인 수정 요청이 있습니다. 관리자 승인 후 반영됩니다.',
        'company_info.no_changes': '변경된 내용이 없습니다.',
        'company_info.edit_restricted_notice': '기업 소개, 주요 제품, 배너, 홍보 영상을 수정할 수 있습니다. 다른 정보는 관리자에게 문의하세요.',
        'company_info.editable_section': '수정 가능 항목',
        'company_info.submit_edit': '수정 요청',
        'company_info.description': '기업 소개',
        'company_info.description_placeholder': '기업에 대한 상세 소개를 작성해주세요',
        'company_info.established_year': '설립연도',
        'company_info.employee_count': '직원 수',
        'company_info.year_suffix': '년',
        'company_info.industry_select': '산업 분류 선택',
        'company_info.industry.drone_manufacturing': '드론 제조',
        'company_info.industry.drone_service': '드론 서비스',
        'company_info.industry.software': '소프트웨어',
        'company_info.industry.hardware': '하드웨어',
        'company_info.industry.logistics': '물류',
        'company_info.industry.agriculture': '농업',
        'company_info.industry.construction': '건설',
        'company_info.industry.media': '미디어',
        'company_info.industry.security': '보안',
        'company_info.industry.education': '교육',
        'company_info.industry.research': '연구',
        'company_info.industry.government': '공공기관',
        'company_info.industry.other': '기타',
        'company_info.industry.uncategorized': '미분류',
        'common.submitting': '제출 중...',

        // 탄소 활동 (🔥 누락된 키 추가됨)
        'carbon.title': '탄소 활동 계산기',
        'carbon.activity': '탄소 감축 활동',
        'carbon.activities': '탄소 감축 활동',
        'carbon.activities_desc': '이동 경로 및 감축량 확인',
        'carbon.history': '활동 히스토리',
        'carbon.history_desc': '지난 감축 기록 모아보기',
        'carbon.status_title': '🌱 나의 탄소 절감 현황',
        'carbon.saved': '절감량',
        'carbon.trees': '나무 환산',
        'carbon.trees_empty': '나무 심기',
        'carbon.environmental_impact': '환경 기여',
        'carbon.loading': '계산 중...',
        'carbon.offset_needed': '탄소 상쇄가 필요해요! ✈️',
        'carbon.great_job': '훌륭해요! 🌿',
        'carbon.reduction_label': '총 CO₂ 감축량',
        'carbon.input_title': '📍 이동 정보 입력',
        'carbon.origin_label': '어디서 오셨나요?',
        'carbon.origin_custom': '거리 직접 입력 (km)',
        'carbon.transport_label': '무엇을 타고 오셨나요?',
        'carbon.passengers_label': '탑승 인원 (카풀 시 감축량 증가)',
        'carbon.calc_btn': '계산하기',
        'carbon.result_title': '🌱 탄소 절감 결과',
        'carbon.distance': '이동 거리',
        'carbon.transport': '교통수단',
        'carbon.baseline': '승용차 기준 배출량',
        'carbon.actual': '실제 배출량',
        'carbon.reduction': '승용차 대비 감축량',
        'carbon.tree_equiv': '🌳 나무 {count}그루가 1년간 흡수하는 양',
        'carbon.reward_text': '지금 기록하면',
        'carbon.reward_suffix': '를 받습니다!',
        'carbon.submit_btn': '🎉 포인트 적립하기',
        'carbon.submit_loading': '적립 중...',
        'carbon.history_title': '활동 이력',
        'carbon.history_empty': '아직 기록된 활동이 없습니다',
        'carbon.fold': '접기',
        'carbon.unfold': '펼치기',
        'carbon.guide_title': '탄소 감축 팁',
        'carbon.guide_1': '대중교통 이용 시 승용차 대비 최대 80% 탄소 절감',
        'carbon.guide_2': '전기차/하이브리드는 일반 차량 대비 50% 이상 절감',
        'carbon.guide_3': '도보/자전거는 탄소 배출 제로!',
        'carbon.guide_4': '항공 이동 시 다른 친환경 활동으로 상쇄해보세요',
        'carbon.transport.car': '승용차',
        'carbon.transport.bus': '고속버스',
        'carbon.transport.train': 'KTX/열차',
        'carbon.transport.plane': '항공',
        'carbon.transport.bicycle': '자전거',
        'carbon.transport.walk': '도보',
        'carbon.points': '포인트',
        'carbon.earned': '획득',
        'carbon.total': '총계',
        'carbon.redeem': '사용',
        'carbon.leaderboard': '순위',
        'carbon.reduce': '감축량',
        'carbon.trees_unit': '그루',
        'carbon.hours_unit': '시간',
        'carbon.cups_unit': '컵',
        'carbon.offset_guide': '항공 이동은 탄소 배출이 많아요. 다른 활동으로 상쇄해보세요!',
        'carbon.no_survey': '아래에서 출발 지역과 교통수단을 선택하고 계산하기 버튼을 눌러주세요.',
        // 🔥 [추가] 사이드바 탄소 감축량 표시
        'carbon.my_reduction': '나의 탄소 감축량',
        'carbon.my_journey': '나의 {venueName} 여정',
        'carbon.survey_loaded': '설문 응답이 자동으로 불러와졌습니다.',
        // 🔥 [추가] 지역 선택 관련 키
        'carbon.province_city': '도/시',
        'carbon.select_region': '지역을 선택하세요',
        'carbon.district': '시/군/구',
        'carbon.optional': '선택사항',
        'carbon.select_all': '전체 / 선택 안 함',
        'carbon.departure_region': '출발 지역',
        'carbon.transport_mode': '이동 수단',
        'carbon.recalculate': '감축량 계산하기',
        'carbon.saving': '저장 중...',
        'carbon.save_result': '내 감축량 저장하기',
        'carbon.save_success': '저장되었습니다!',
        'carbon.save_failed': '저장에 실패했습니다.',
        'carbon.total_stats': '나의 누적 현황',
        'carbon.total_saved': '총 감축량',
        'carbon.trees_equivalent': '나무 심기 환산',
        'carbon.calc_note_1': '이 계산은 휘발유 승용차(150g CO₂/km)를 기준으로 비교합니다.',
        'carbon.calc_note_2': '목적지',
        'carbon.exhibition_center': '제1전시장',
        'carbon.calc_note_3': '거리는 Haversine 공식으로 계산되며, 도로 굴곡 보정(×1.35)이 적용됩니다.',
        'carbon.excess': '초과 배출량',
        'carbon.my_emission_excess': '탄소 초과 배출',


        // ==================== 🔥🔥🔥 [추가] 미팅 상세 페이지 ====================
        'meeting.detail_title': '미팅 상세',
        'meeting.phone_required_title': '전화번호 등록 필요',
        'meeting.phone_required': '미팅 요청은 상대방의 수락/변경 알림을 SMS로 받기 위해 전화번호 등록이 필요합니다. 프로필에서 등록 후 다시 시도해 주세요.',
        'meeting.btn_go_register_phone': '프로필에서 등록',
        'meeting.loading': '미팅 정보를 불러오는 중...',
        'meeting.load_error': '미팅을 불러올 수 없습니다.',
        'meeting.calendar_notice': '이 미팅은 인앱 캘린더(일정)에 저장됩니다',
        'meeting.time_location': '시간 및 장소',
        'meeting.location_tbd': '장소 미정',
        'meeting.description': '설명',
        'meeting.agenda': '안건',
        'meeting.agenda_item': '안건 항목',
        'meeting.participants': '참석자',
        'meeting.organizer': '주최자',
        'meeting.notes': '메모',
        
        // 미팅 상태
        'meeting.status_confirmed': '확정됨',
        'meeting.status_scheduled': '예정됨',
        'meeting.status_in_progress': '진행 중',
        'meeting.status_pending': '대기중',
        'meeting.status_proposed': '제안됨',
        'meeting.status_requested': '요청됨',
        'meeting.status_cancelled': '취소됨',
        'meeting.status_declined': '거절됨',
        'meeting.status_completed': '완료됨',
        'meeting.status_accepted': '수락됨',
        
        // 우선순위
        'meeting.priority_high': '높음',
        'meeting.priority_urgent': '긴급',
        'meeting.priority_medium': '보통',
        'meeting.priority_low': '낮음',
        
        // 액션 버튼
        'meeting.btn_accept_with_notification': '수락 (알림 설정)',
        'meeting.cancel_meeting': '미팅 취소',
        'meeting.join_meeting': '화상 미팅 입장',
        'meeting.join_in_progress': '화상 미팅 입장 (진행 중)',
        'meeting.already_accepted': '이 미팅을 수락했습니다',
        
        // 확인/에러 메시지
        'meeting.confirm_accept': '이 미팅을 수락하시겠습니까?',
        'meeting.accept_error': '미팅 수락에 실패했습니다.',
        'meeting.decline_reason_prompt': '거절 사유를 입력해주세요 (선택):',
        'meeting.decline_error': '미팅 거절에 실패했습니다.',
        'meeting.cancel_reason_prompt': '취소 사유를 입력해주세요 (선택):',
        'meeting.cancelled': '미팅을 취소했습니다.',
        'meeting.cancel_error': '미팅 취소에 실패했습니다.',
        'meeting.cancel_reason_required': '취소 사유를 5자 이상 입력해 주세요.',
        'meeting.cancel_reason_required_label': '취소 사유 (필수)',
        'meeting.cancel_reason_placeholder_required': '취소 사유를 입력해 주세요.',
        'meeting.cancel_reason_min_hint': '최소 5자 이상 입력해야 합니다.',
        
        // 시간 경고
        'meeting.warning_time_passed': '⚠️ 이미 미팅 시간이 지났습니다.\n시간을 수정하여 다시 제안하시겠습니까?',
        'meeting.warning_time_soon': '⚠️ 미팅 시작까지 {minutes}분 남았습니다.\n준비 시간이 부족할 수 있습니다. 시간을 수정하여 제안하시겠습니까?',
        
        // 일정 변경
        'meeting.reschedule_title': '수정 제안 (시간 변경)',
        'meeting.new_proposed_time': '새로운 제안 시간',
        'meeting.select_new_time': '새로운 시간을 선택해주세요.',
        'meeting.change_reason': '변경 사유 (선택)',
        'meeting.change_reason_placeholder': '시간 변경이 필요한 이유를 입력해주세요.',
        'meeting.time_change_reason': '시간 변경 요청',
        'meeting.send_reschedule': '수정 제안 보내기',
        'meeting.reschedule_sent': '일정 변경 제안을 보냈습니다.',
        'meeting.reschedule_error': '수정 제안에 실패했습니다.',
        'meeting.reschedule_target_missing': '미팅 상대방 정보를 찾을 수 없습니다.',
        'meeting.counterpart': '상대방',
        'meeting.reschedule_initial_title': '{title} - 일정 변경 요청',
        'meeting.original_meeting_time': '기존 미팅 시간',
        'meeting.reschedule_default_note': '기존 일정 조정이 필요해 새로운 미팅 시간을 제안드립니다. 제안된 시간 중 편하신 시간을 선택해 주세요.',
        'meeting.reschedule_reason_hint': '일정 변경 사유나 참고 사항을 입력해 주세요.',
        'meeting.field_reschedule_message': '상대방에게 보낼 메시지',
        'meeting.placeholder_reschedule_notes': '일정 변경 사유나 참고 사항을 선택적으로 입력해 주세요.',
        'meeting.reschedule_modal_title': '새 미팅 시간 제안',
        'meeting.btn_send_reschedule_proposal': '일정 변경 제안 보내기',
        'meeting.toast_reschedule_proposal_sent': '일정 변경 제안을 보냈습니다.',
        'meeting.reschedule_proposal_message': '미팅 일정 변경 요청\n\n제안된 시간 중 하나를 선택해 주세요.',
        // 기존 meeting 키들 뒤에 추가
        'meeting.cancel_meeting': '미팅 취소',
        'meeting.cancel_confirm_message': '정말로 이 미팅을 취소하시겠습니까? 참석자들에게 취소 알림이 전송됩니다.',
        'meeting.cancel_reason': '취소 사유 (선택)',
        'meeting.cancel_reason_placeholder': '취소 사유를 입력해주세요...',
        'meeting.btn_confirm_cancel': '취소하기',
        'meeting.decline_meeting': '미팅 거절',
        'meeting.decline_confirm_message': '이 미팅 초대를 거절하시겠습니까?',
        'meeting.decline_reason': '거절 사유 (선택)',
        'meeting.decline_reason_placeholder': '거절 사유를 입력해주세요...',
        'meeting.reschedule_meeting': '일정 변경',
        'meeting.reschedule_confirm_message': '미팅 일정을 변경하시겠습니까?',
        'meeting.reschedule_reason': '변경 사유 (선택)',
        'meeting.reschedule_reason_placeholder': '일정 변경 사유를 입력해주세요...',
        'meeting.new_datetime': '새로운 일시',
        'meeting.request': '미팅 요청',
        'meeting.waiting_for_response': '상대방의 응답을 기다리고 있습니다',
        'meeting.propose_reschedule': '일정 변경 제안',
        'meeting.next_actions': '다음 작업',
        'meeting.manage_meeting': '미팅 관리',
        'meeting.action_hint_final': '이미 종료된 미팅입니다. 세부 내용만 확인할 수 있습니다.',
        'meeting.action_hint_pending': '초대에 응답하거나, 필요하면 채팅에서 새 시간을 협의해 주세요.',
        'meeting.action_hint_waiting': '상대방 응답을 기다리는 중이며, 필요하면 일정 변경 제안을 다시 보낼 수 있습니다.',
        'meeting.action_hint_accepted': '확정된 미팅입니다. 일정 변경 제안이나 취소가 필요한 경우 아래 버튼을 사용하세요.',
        'meeting.action_hint_available': '미팅 상태에 따라 가능한 작업을 선택할 수 있습니다.',
        'meeting.location_hint': '상대방 부스 번호가 기본으로 설정됩니다',
        'meeting.error_past_datetime': '미팅 시간은 현재보다 최소 5분 이후여야 합니다.',
        'meeting.error_self_meeting': '자기 자신에게는 미팅을 요청할 수 없습니다.',
        'meeting.booth_hint': '기본값: {location}',



        // 🔥 설문 데이터 (누락된 키 추가됨)
        'survey.busan': '부산',
        'survey.seoul': '서울/경기/인천',
        'survey.gyeongsang': '경상권 (부산 외)',
        'survey.chungcheong': '충청권',
        'survey.jeolla': '전라권',
        'survey.gangwon': '강원권',
        'survey.jeju': '제주권',
        'survey.overseas': '해외',
        // 🔥 [추가] 개별 교통수단 키
        'survey.walk': '도보',
        'survey.bicycle': '자전거',
        'survey.subway': '지하철',
        'survey.bus': '버스',
        'survey.car': '자가용',
        'survey.train': '기차/KTX',
        'survey.walk_bicycle': '도보/자전거',
        'survey.public': '대중교통',
        'survey.car_internal': '자가용 (내연기관)',
        'survey.car_electric': '전기차/하이브리드',
        'survey.airplane': '항공기',
        
        'survey.transport_q': '행사장({venueName})까지 주로 어떤 교통수단을 이용하시나요?',
        'survey.transport_desc': '선택하신 교통수단을 기반으로 탄소 절감량이 계산됩니다.',
        'survey.transport_public': '대중교통 (지하철/버스)',
        'survey.transport_train': '기차/KTX',
        'survey.transport_airplane': '항공기',
        'survey.transport_car_internal': '자가용 (휘발유/경유)',
        'survey.transport_car_electric': '자가용 (전기/수소/하이브리드)',
        'survey.transport_walk_bicycle': '도보/자전거',


        // 알림 설정
        'noti.title': '알림 설정',
        'noti.saving': '저장 중...',
        'noti.push_title': '푸시 알림',
        'noti.push_desc_on': '앱에서 발송하는 알림을 받습니다',
        'noti.push_desc_off': '브라우저에서 알림이 차단되어 있습니다',
        'noti.chat': '채팅 메시지',
        'noti.chat_desc': '새 메시지가 도착하면 알림',
        'noti.meeting': '미팅 알림',
        'noti.meeting_desc': '미팅 요청, 수락, 일정 알림',
        'noti.matching': '매칭 알림',
        'noti.matching_desc': 'AI 추천 기업, 매칭 요청',
        'noti.event': '이벤트 알림',
        'noti.event_desc': '관심 이벤트 일정, 새 이벤트',
        'noti.carbon': '탄소 활동 알림',
        'noti.carbon_desc': '포인트 적립, 목표 달성',
        'noti.email_title': '이메일 알림',
        'noti.email_desc': '이메일로 알림을 받습니다',
        'noti.newsletter': '뉴스레터',
        'noti.newsletter_desc': '드론 산업 뉴스, 행사 소식',
        'noti.email_meeting': '미팅 요약',
        'noti.email_meeting_desc': '미팅 확정, 일정 리마인더',
        'noti.email_matching': '매칭 리포트',
        'noti.email_matching_desc': '주간 AI 추천 기업 리포트',
        'noti.email_event': '이벤트 소식',
        'noti.email_event_desc': '관심 이벤트 업데이트',
        'noti.sms_title': 'SMS 알림',
        'noti.sms_desc': '문자 메시지로 알림을 받습니다',
        'noti.sms_meeting': '미팅 리마인더',
        'noti.sms_meeting_desc': '미팅 30분 전 SMS 알림',
        'noti.dnd_title': '🌙 방해금지 모드',
        'noti.dnd_desc': '설정된 시간에는 알림을 받지 않습니다',
        'noti.start_time': '시작 시간',
        'noti.end_time': '종료 시간',
        'noti.current_setting': '현재 설정',
        'noti.help_title': '알림 관련 도움말',
        'noti.help_1': '푸시 알림은 브라우저 권한이 필요합니다.',
        'noti.help_2': '이메일 알림은 가입 시 등록한 이메일로 발송됩니다.',
        'noti.help_3': '방해금지 모드 중에도 중요 알림은 수신됩니다.',
        // 🔥 [추가] 알림 설정 페이지 누락 키
        'noti.push_section': '푸시 알림',
        'noti.push_desc_denied': '브라우저에서 알림이 차단되어 있습니다',
        'noti.push_chat': '채팅 메시지',
        'noti.push_chat_desc': '새 메시지가 도착하면 알림',
        'noti.push_meeting': '미팅 알림',
        'noti.push_meeting_desc': '미팅 요청, 수락, 일정 알림',
        'noti.push_matching': '매칭 알림',
        'noti.push_matching_desc': 'AI 추천 기업, 매칭 요청',
        'noti.push_event': '이벤트 알림',
        'noti.push_event_desc': '관심 이벤트 일정, 새 이벤트',
        'noti.push_carbon': '탄소 활동 알림',
        'noti.push_carbon_desc': '포인트 적립, 목표 달성',
        'noti.email_section': '이메일 알림',
        'noti.email_newsletter': '뉴스레터',
        'noti.email_newsletter_desc': '드론 산업 뉴스, 행사 소식',
        'noti.sms_section': 'SMS 알림',
        'noti.email_section': '이메일',
        'noti.email_marketing_title': '이메일 광고성 정보 수신 동의',
        'noti.email_marketing_desc': '행사 안내·소식 등 광고성 이메일을 받습니다',
        'noti.email_marketing_note': '미팅·매칭 등 서비스 필수 알림 이메일은 본 동의와 무관하게 발송됩니다. 동의는 언제든 철회할 수 있습니다.',
        'noti.dnd_section': '방해금지 모드',
        'noti.dnd_start': '시작 시간',
        'noti.dnd_end': '종료 시간',
        'noti.dnd_current': '현재 설정: {start}부터 {end}까지 알림을 받지 않습니다.',
        'noti.help_4': '설정은 자동으로 저장됩니다.',
        'noti.alert_not_supported': '이 브라우저는 알림을 지원하지 않습니다.',
        'noti.alert_granted': '알림 권한이 허용되었습니다.',
        'noti.alert_denied': '알림 권한이 거부되었습니다. 브라우저 설정에서 변경해주세요.',
        'noti.sms_1hour_before': '1시간 전 알림',
        'noti.sms_10min_before': '10분 전 알림',
        'noti.sms_event': '이벤트 리마인더',
        'noti.sms_event_desc': '전시회 주요 일정 및 이벤트 알림',
        'noti.help_sms_1': 'SMS 알림은 등록된 휴대폰 번호로 발송됩니다.',
        'noti.help_sms_2': '미팅 알림은 예약된 상담 시작 전에 발송됩니다.',
        'noti.help_sms_3': '이벤트 리마인더는 전시회 주요 일정을 안내합니다.',
        'sms_consent_popup.title': 'SMS 알림 수신 동의',
        'sms_consent_popup.message': '미팅 알림, 이벤트 리마인더 등 중요한 정보를 SMS로 받아보시겠습니까?',
        'sms_consent_popup.benefit_meeting': '미팅 시작 전 알림',
        'sms_consent_popup.benefit_event': '이벤트 리마인더',
        'sms_consent_popup.profile_hint': '프로필 페이지에서 언제든 수신알람을 켜고 끌 수 있습니다.',
        'sms_consent_popup.decline': '다음에 하기',
        'sms_consent_popup.agree': '동의하기',


        // 이벤트 관련
        'events.bookmarks': '관심 기업/이벤트',
        'events.no_bookmarks': '아직 찜한 이벤트가 없습니다.',
        'events.no_official': '현재 등록된 공식 행사가 없습니다',
        'events.coming_soon': '새로운 행사가 곧 공개될 예정입니다',
        'events.date_tbd': '미정',
        
        // 🔥 [추가] EventsPage 관련 키
        'today': '오늘',
        'official_event': '공식',
        'search_events': '이벤트 검색...',
        'filters': '필터',
        'grid_view': '그리드',
        'category': '카테고리',
        'category_exhibition': '전시회',
        'category_conference': '컨퍼런스',
        'category_trade_show': '무역박람회',
        'category_workshop': '워크숍',
        'category_seminar': '세미나',
        'category_networking': '네트워킹',
        'category_community': '커뮤니티',
        'clear_filters': '필터 초기화',
        'retry': '다시 시도',
        'no_events_found': '이벤트가 없습니다.',
        
        // 🔥 [추가] EventDetailPage 관련 키
        'event_not_found': '이벤트를 찾을 수 없습니다.',
        'go_back': '돌아가기',
        'bookmark_removed': '북마크가 해제되었습니다.',
        'reminder_1h': '시작 1시간 전입니다.',
        'reminder_10m': '시작 10분 전입니다!',
        'bookmark_added_with_reminder': '이벤트가 북마크 되었습니다. 시작 전 알림을 보내드릴게요!',
        'bookmark_failed': '작업을 완료할 수 없습니다.',
        'registration_success': '등록이 완료되었습니다.',
        'registration_failed': '등록에 실패했습니다.',
        'link_copied': '링크가 복사되었습니다.',
        'cancelled': '취소됨',
        'date': '일시',
        'location': '장소',
        'participants': '참가자',
        'waiting_list': '대기자',
        'carbon_impact': '탄소 영향',
        'eco_friendly_event': '친환경 이벤트',
        'description': '상세 설명',
        'no_description': '상세 설명이 없습니다.',
        'show_less': '접기',
        'show_more': '더보기',
        'opening_hours': '개장 시간',
        'pricing': '요금 정보',
        'regular_fee': '일반 요금',
        'early_bird': '얼리버드',
        'organizer_info': '주최/주관',
        'host': '주최',
        'organizer': '주관',
        'contact_person': '담당자',
        'contact': '연락처',
        'official_website': '공식 웹사이트',
        'tags': '태그',
        'gallery': '갤러리',
        'history': '연혁',
        'visitors': '명',
        'registering': '등록 중...',
        'event_cancelled': '이벤트 취소됨',
        'register': '참가 신청하기',
        'registered': '등록 완료',
        'bookmarked': '북마크됨',
        'add_bookmark': '북마크 추가',
        'edition_number': '제',
        'edition_suffix': '회',


        
        // 캘린더 관련
        'calendar.year': '년',
        'calendar.month': '월',
        'calendar.sun': '일',
        'calendar.mon': '월',
        'calendar.tue': '화',
        'calendar.wed': '수',
        'calendar.thu': '목',
        'calendar.fri': '금',
        'calendar.sat': '토',
        
        // 에러 메시지
        'error.networkError': '네트워크 연결을 확인해주세요',
        'error.serverError': '서버 오류가 발생했습니다',
        'error.unknownError': '알 수 없는 오류가 발생했습니다',
        'error.authError': '인증에 실패했습니다',
        'error.notFound': '요청한 페이지를 찾을 수 없습니다',
        'error.forbidden': '접근 권한이 없습니다',
        'error.timeout': '요청 시간이 초과되었습니다',
        'error.badRequest': '잘못된 요청: {message}',
        'error.unauthorized': '인증이 필요합니다. 다시 로그인해주세요.',
        'error.notFoundResource': '요청한 정보를 찾을 수 없습니다.',
        'error.unprocessable': '입력 정보를 확인해주세요: {message}',
        'error.tooManyRequests': '너무 많은 요청입니다. 잠시 후 다시 시도해주세요.',
        'error.serverErrorRetry': '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        'error.unknownStatus': '서버 오류 ({status}): {message}',
        'error.save_failed': '저장에 실패했습니다.',
        'error.invalid_image_type': '이미지 파일만 업로드 가능합니다.',
        'error.file_too_large': '파일 크기는 5MB 이하여야 합니다.',
        'error.upload_failed': '업로드에 실패했습니다.',
        'error.phone_format': '올바른 휴대폰 번호를 입력해주세요.',
        'error.sms_send_failed': 'SMS 발송에 실패했습니다.',
        'error.code_length': '인증번호 6자리를 입력해주세요.',
        'error.invalid_code': '인증번호가 올바르지 않습니다.',
        'error.phone_verification_required': '변경된 전화번호 인증이 필요합니다.',
        'sms.code_sent': '인증코드가 발송되었습니다.',
        'sms.verified': '전화번호 인증이 완료되었습니다.',
        'sms.resend': '재발송',
        'sms.request': '인증요청',
        'sms.code_placeholder': '인증번호 6자리',
        'sms.phone_verified': '전화번호 인증이 완료되었습니다.',
        'common.verifying': '확인중...',
        'myinfo.phone_change_notice': '전화번호 변경 시 SMS 인증이 필요합니다.',
        'myinfo.remove_business_card_title': '명함 삭제',


        
        // 환영 메시지
        'welcome.message': '환영합니다, {name}님!',
        'welcome.greeting': '안녕하세요',
        'welcome.description': 'DSK에 오신 것을 환영합니다',
        
        // 홈 화면 (🔥 누락된 키 추가됨)
        'home.greeting.morning': '좋은 아침입니다, {name}님',
        'home.greeting.afternoon': '반가워요, {name}님',
        'home.greeting.evening': '좋은 저녁입니다, {name}님',
        'home.activity_summary': '내 활동 요약',
        'home.recent_activity': '최근 활동',
        'home.featured_companies': '추천 기업',
        'home.featured_events': '추천 이벤트',
        'home.upcoming_schedule': '예정된 일정',
        'home.view_all': '전체보기',
        'home.more': '더보기',
        'home.loading': '데이터 로딩 중...',
        'home.no_activity': '아직 기록된 활동이 없습니다.',
        'home.no_activity_hint': '설문을 완료하거나 활동을 기록하면 여기에 나타납니다.',
        'home.record_activity': '활동 기록하기',
        // 🔥🔥🔥 [2026-01-09 추가] 홈페이지 관심분야 기반 추천 관련 키
        'home.recommended_for_you': '맞춤 추천 기업',
        'home.recommended_companies': '추천 기업',

        
        // 기업 관련
        'company.search': '기업 검색',
        'company.filter': '기업 필터',
        'company.details': '기업 상세',
        'company.contact': '연락처',
        'company.interests': '관심 분야',
        'company.products': '제품',
        'company.services': '서비스',
        'company.matchScore': '매칭 점수',
        'company.requestMeeting': '미팅 요청',
        'company.website': '웹사이트',
        'company.email': '이메일',
        'company.phone': '전화',
        'company.chat_btn': '채팅 문의하기',
        'company.map_btn': '부스 지도 보기',
        'company.booth': '부스 위치',
        'company.booth_tbd': '미정',
        'company.tab_overview': '개요',
        'company.tab_products': '제품',
        'company.video_title': '홍보 영상',
        'company.video_view': '영상 보기',
        'company.tab_news': '성과/소식',
        'company.info_employees': '직원 수',
        'company.info_founded': '설립년도',
        'company.info_location': '위치',
        'company.info_industry': '업종',
        'company.service_title': '주요 서비스',
        'company.empty_products': '등록된 제품이 없습니다.',
        'company.empty_news': '등록된 소식이 없습니다.',
        'company.loading': '기업 정보를 불러오는 중...',
        'company.error_not_found': '기업 정보를 찾을 수 없습니다',
        'company.online_status': '온라인',
        'company.no_description': '등록된 기업 소개가 없습니다.',
        'company.alert_login_required': '로그인이 필요합니다.',
        'company.alert_chat_error': '채팅방 생성에 실패했습니다.',

        
        // 기업 목록
        'companies.title': '참가 기업 목록',
        'companies.search_placeholder': '기업명, 업종, 태그로 검색...',
        'companies.filter_title': '카테고리 선택',
        'companies.filter_all': '전체 카테고리',
        'companies.search_label': '검색',
        'companies.sort_label': '정렬',
        'companies.filter_active_count': '카테고리 {count}개',
        'companies.filters_active': '필터 {count}개 적용 중',
        'companies.clear_filters': '필터 초기화',
        'companies.sort_name_asc': '이름순 (ㄱ-ㅎ)',
        'companies.sort_name_desc': '이름순 (ㅎ-ㄱ)',
        'companies.sort_booth_asc': '부스번호순 (낮은순)',
        'companies.sort_booth_desc': '부스번호순 (높은순)',
        'companies.result_count': '총 {count}개 기업',
        'companies.empty_title': '등록된 기업이 없습니다',
        'companies.empty_search': '검색 결과가 없습니다',
        'companies.semantic_search': 'AI 의미 검색', // 🔥 [2026-06-10 B-2] 자연어(시맨틱) 검색 라벨
        'companies.reset': '초기화',
        'companies.apply': '적용',
        'companies.end_of_list': '더 이상 기업이 없습니다',

        // 매칭
        'matching.title': 'AI 기업 매칭',
        'matching.subtitle': '최적의 비즈니스 파트너를 찾아보세요.',
        'matching.mode_ai': 'AI 추천',
        'matching.mode_search': '직접 검색',
        'matching.search_placeholder': '기술, 제품, 기업명 검색...',
        'matching.btn_search': '검색',
        'matching.result_count': '{count}개의 추천 기업',
        'matching.analyzed': '분석 완료',
        'matching.empty_ai': '아직 관심 등록된 기업이 없습니다.',
        'matching.empty_ai_desc': '프로필을 보완하면 더 정확한 추천을 받을 수 있습니다.',
        'matching.empty_search': '검색 결과가 없습니다.',
        'matching.empty_search_desc': '다른 키워드로 검색해보세요.',
        'matching.section_ai': 'AI 통합 분석 추천',
        'matching.section_desc': '사업 내용이 비슷한 기업',
        'matching.section_prod': '주요 제품이 유사한 기업',
        'matching.action_chat': '메시지 보내기',
        'matching.booth_prefix': '부스',
        'matching.logo_alt': '로고',
        'matching.no_description': '기업 소개가 없습니다.',
        'matching.action_location': '위치 보기',
        'matching.top_rank': 'TOP {rank}',
        'matching.loading': 'AI가 분석 중입니다...',
        'matching.info_message': 'DSK AI 이월이가 귀하의 프로필과 관심사를 분석하여 최적의 파트너를 추천합니다.',
        'matching.alert_login_required': '로그인이 필요한 서비스입니다.',
        'matching.alert_load_error': '데이터를 불러오는데 실패했습니다.',
        'matching.alert_min_chars': '검색어를 2글자 이상 입력해주세요.',
        'matching.alert_search_error': '검색 중 오류가 발생했습니다.',
        'matching.alert_chat_error': '채팅방 생성에 실패했습니다.',
        'matching.alert_booth_location': '부스 위치: {booth}',
        'matching.buyer_title': 'AI 관심 기업 탐색',
        'matching.buyer_subtitle': '회원님의 관심사를 분석하여 딱 맞는 기업을 찾아냈어요.',
        'matching.buyer_mode_ai': '관심사 매칭',
        'matching.buyer_empty_title': '매칭된 기업이 없습니다',
        'matching.buyer_empty_desc': '관심사를 더 구체적으로 작성하시면 정확도가 올라갑니다.',
        'matching.buyer_analyzed': '관심사 분석 완료',
        'matching.buyer_info_message': "DSK AI 이월이가 귀하의 프로필과 관심사를 분석하여 최적의 파트너를 추천합니다.",
        'matching.buyer_reason': '회원님의 관심사와 높은 유사도를 보입니다.',
        'matching.section_interest': '회원님의 관심사와 일치하는 기업',
        'matching.section_recommended': '추천 기업',
        'matching.company_reason': 'AI 추천 매칭',
        'matching.access_denied_title': '접근 제한',
        'matching.access_denied_message': '해당 기능은 비즈니스 회원 전용 기능입니다.',
        'matching.btn_business_upgrade': '비즈니스 비지터 신청',
        'matching.alert_no_booth': '부스 위치가 지정되지 않았습니다.',
        'matching.pending_approval_title': '승인 대기 중',
        'matching.pending_approval_message': '현재 사무국 승인 대기 중입니다.\n승인이 완료되면 매칭 기능을 이용하실 수 있습니다.',

        // ─── businessUpgrade (BusinessUpgradeModal.jsx) ───
        'businessUpgrade.title': '비즈니스 회원 신청',
        'businessUpgrade.description': '채팅, 매칭, 미팅 요청 기능을 이용하기 위해\n아래 정보를 모두 입력해주세요.',
        'businessUpgrade.profile_label': '프로필 사진',
        'businessUpgrade.profile_required': '*필수 (본인 사진)',
        'businessUpgrade.profile_placeholder': '클릭하여 등록',
        'businessUpgrade.profile_error': '본인 사진을 등록해주세요',
        'businessUpgrade.company_placeholder': '회사/소속명 *',
        'businessUpgrade.company_error': '회사/소속명을 입력해주세요',
        'businessUpgrade.department_placeholder': '부서 (선택)',
        'businessUpgrade.job_title_placeholder': '직함 *',
        'businessUpgrade.job_title_error': '직함을 입력해주세요',
        'businessUpgrade.card_label': '명함 이미지',
        'businessUpgrade.card_required': '*필수',
        'businessUpgrade.card_error': '명함 이미지를 업로드해주세요',
        'businessUpgrade.notice': '신청 후 사무국 승인이 필요합니다. 승인 완료 시 채팅, 매칭, 미팅 요청 기능을 이용하실 수 있습니다.',
        'businessUpgrade.btn_cancel': '취소',
        'businessUpgrade.btn_submit': '승인 신청하기',
        'businessUpgrade.btn_uploading': '업로드 중...',
        'businessUpgrade.btn_processing': '처리 중...',
        'businessUpgrade.success_title': '신청 완료',
        'businessUpgrade.success_message': '비즈니스 회원 신청이 완료되었습니다.\n사무국 승인 후 채팅, 매칭, 미팅 요청 기능을 이용하실 수 있습니다.',
        'businessUpgrade.success_confirm': '확인',
        'businessUpgrade.error_file_size': '파일 크기는 5MB 이하여야 합니다.',
        'businessUpgrade.error_file_type': '이미지 파일만 업로드 가능합니다.',
        'businessUpgrade.error_profile_required': '프로필 사진은 필수입니다. 본인 사진을 등록해주세요.',
        'businessUpgrade.error_card_required': '명함 이미지는 필수입니다.',
        'businessUpgrade.error_submit': '신청 처리 중 오류가 발생했습니다. 다시 시도해주세요.',
        'businessUpgrade.success_toast': '신청이 완료되었습니다!',

        'matching.hero_title': 'AI 매칭',
        'matching.hero_subtitle': '회원님의 프로필을 기반으로 최적의 비즈니스 파트너를 추천합니다.',
        'matching.btn_ai_analysis': '관심분야 기반 분석',
        'matching.btn_direct_search': '직접 검색',
        'matching.result_count_suffix': '개의 추천 기업',
        'matching.before_title': '매칭 전',
        'matching.before_desc': '아직 올해 매칭 전입니다. 매칭이 시작되면 추천 기업을 보여 드립니다.',
        'matching.calculating_title': 'AI 분석 중...',
        'matching.calculating_desc': '최적의 비즈니스 파트너를 찾고 있습니다.',
        'matching.calculating_progress': '분석 진행 중...',
        'matching.calculating_almost': '거의 완료되었습니다...',
        'matching.search_empty_title': '검색 결과가 없습니다',
        'matching.search_empty_desc': '다른 키워드로 검색해보세요.',
        'matching.info_modal_title': 'AI 기업 매칭 안내',
        'matching.reason_buyer': 'AI 분석: 귀하의 관심사와 관련된 기업입니다.',
        'matching.reason_default': 'AI 분석: 귀하의 관심사와 관련된 기업입니다.',
        'matching.reason_high': '회원님의 관심사와 일치도가 매우 높은 기업이에요',
        'matching.reason_high_cat': "'{category}' 분야에서 회원님의 관심사와 일치도가 매우 높아요",
        'matching.reason_mid': '회원님의 관심사와 관련이 있는 기업이에요',
        'matching.reason_mid_cat': "'{category}' 분야가 회원님의 관심사와 관련이 있어요",
        'matching.reason_low': '새로운 비즈니스 기회가 될 수 있는 기업이에요',
        'matching.reason_low_buyer': '잠재적인 거래처가 될 수 있는 기업이에요',
        'matching.show_more': '더 보기',
        'matching.score_explain': '매칭 점수(%)는 카테고리 적합도, 관심 분야 일치, 기업 정보 충실도를 종합한 값으로, 높을수록 더 잘 맞는 파트너입니다.',
        'matching.no_company_name': '회사명 없음',
        'matching.no_category': '카테고리 미지정',
        'matching.booth_tbd': '미정',
        'matching.btn_location': '위치 보기',
        'matching.btn_meeting': '미팅 요청',
        'matching.btn_message': '메시지',
        'matching.booth': '부스',
        'matching.section_title': '통합 추천',
        'matching.no_interest_title': '관심 분야를 입력해주세요',
        'matching.no_interest_desc': 'AI 매칭을 위해 관심 기술/제품을 설문에서 입력해주세요.',
        'matching.no_interest_btn': '설문 작성하기',
        'matching.buyer_no_interest_title': '관심 분야를 입력해주세요',
        'matching.buyer_no_interest_desc': 'AI 매칭을 위해 관심 기술/제품을 설문에서 입력해주세요.',
        'matching.buyer_no_interest_btn': '바이어 설문 작성하기',
        'matching.alert_min_chars': '검색어를 2자 이상 입력해주세요.',
        'matching.alert_search_error': '검색 중 오류가 발생했습니다.',
        'matching.search_result_count_suffix': '개의 검색 결과',




        // 미팅 관련
        'meeting.schedule': '일정 잡기',
        'meeting.upcoming': '예정된 미팅',
        'meeting.past': '지난 미팅',
        'meeting.cancel': '취소',
        'meeting.reschedule': '일정 변경',
        'meeting.confirm': '확인',
        'meeting.location': '장소',
        'meeting.time': '시간',
        'meeting.type': '유형',
        'meeting.participants': '참가자',
        'meeting.notes': '메모',
        'meetings.title': '내 일정',
        'meetings.view_list': '목록',
        'meetings.view_calendar': '달력',
        'meetings.empty': '예정된 미팅이 없습니다.',
        'meetings.go_book': '미팅 잡으러 가기',
        'meetings.location_tbd': '장소 미정',
        'meetings.upcoming': '예정된 미팅',
        'meetings.no_schedule': '이 날은 예정된 일정이 없습니다',
        'meetings.no_upcoming': '예정된 미팅이 없습니다',
        'meetings.schedule_new': '새로운 미팅을 예약해보세요',
        'meeting.modal_title': '미팅 제안하기',
        'meeting.target_company': '대상 기업',
        'meeting.field_title': '미팅 주제',
        'meeting.field_date': '날짜',
        'meeting.field_time': '시간',
        'meeting.field_location': '장소 (선택)',
        'meeting.default_title': '비즈니스 미팅',
        'meeting.booth_location': '귀사의 부스에서 미팅하길 희망합니다.',
        'meeting.placeholder_title': '예: 제품 소개 및 협력 논의',
        'meeting.placeholder_location': '예: A홀 102호 부스',
        'meeting.notice_proposal': '미팅 제안이 상대방에게 전송되며, 상대방이 수락하면 캘린더에 자동으로 등록됩니다.',
        'meeting.sending_proposal': '미팅 제안 전송 중...',
        'meeting.btn_send_proposal': '미팅 제안 보내기',
        'meeting.btn_go_to_chat': '채팅방으로 이동',
        'meeting.proposal_sent_title': '미팅 제안 완료',
        'meeting.confirm_navigate_to_chat': '미팅 제안이 성공적으로 전송되었습니다. 채팅방에서 상대방의 응답을 확인하시겠습니까?',
        'meeting.toast_proposal_sent': '미팅 제안을 보냈습니다.',
        'meeting.error_title_required': '미팅 주제를 입력해주세요.',
        'meeting.error_date_required': '날짜를 선택해주세요.',
        'meeting.error_time_required': '시간을 선택해주세요.',
        'meeting.error_no_company': '기업 정보가 없습니다.',
        'meeting.error_chat_creation': '채팅방 생성에 실패했습니다.',
        'meeting.error_invalid_datetime': '유효하지 않은 날짜/시간입니다.',
        'meeting.error_proposal_failed': '미팅 제안에 실패했습니다.',
        'meeting.field_notes': '미팅 안건 및 메시지',
        'meeting.placeholder_notes': '상대방에게 전달할 상세 안건이나 메시지를 입력해주세요.',
        // 🔥 [2026-04-22] MeetingProposalModal 누락 키 보강
        'meeting.matrix_label': '후보 시간 선택',
        'meeting.loading_matrix': '가능 시간을 불러오는 중입니다.',
        'meeting.no_active_event_notice': '활성 행사 기간이 있어야 제안할 수 있습니다.',
        'meeting.reload_matrix_button': '시간표 다시 불러오기',
        'meeting.max_candidates_notice': '최대 {count}개까지 선택할 수 있습니다.',
        'meeting.select_slots_required': '후보 시간을 선택해 주세요.',
        // 🔥 [2026-04-23] ChatRoom 미팅 제안 카드 누락 키 보강
        'meeting.expires_at_label': '만료',
        'meeting.accept_slot_button': '이 시간 수락',
        // 🔥 [2026-04-22] MeetingAvailabilityGrid 누락 키 보강
        'meeting.matrix_status_available': '가능',
        'meeting.matrix_status_me_busy': '내 일정 충돌',
        'meeting.matrix_status_other_busy': '상대 일정 충돌',
        'meeting.matrix_status_room_full': '룸 없음',
        'meeting.matrix_status_past': '지남',
        'meeting.matrix_status_seminar': '세미나 (선택 불가)',
        'meeting.selected_slots_count': '선택된 후보',
        'meeting.recommended_slots_label': '추천 후보',
        'meeting.matrix_time': '시간',
        'meeting.matrix_selected': '선택됨',
        'meeting.available_rooms_count': '{count}개 룸',
        'meeting.room_selection_title': '후보별 미팅룸',
        'meeting.no_available_room_option': '선택 가능한 룸 없음',
        'meeting.room_capacity_persons': '{count}명',


        // 채팅
        'chat.messages': '메시지',
        'chat.send': '전송',
        'chat.typing': '입력 중...',
        'chat.online': '온라인',
        'chat.offline': '오프라인',
        'chat.newMessage': '새 메시지',
        'chat.readReceipt': '읽음',
        'chat.unread': '읽지 않음',
        'chat.read_by_other': '읽음',
        'chat.unread_by_other': '안읽음',
        'chat.title_default': '채팅방',
        'chat.input_placeholder': '메시지 입력... (미팅 키워드로 제안)',
        'chat.meeting_detected_title': '미팅 키워드 감지',
        'chat.confirm_meeting_proposal': '미팅 제안 카드를 보내시겠습니까?',
        'chat.btn_send_card': '미팅 카드 보내기',
        'chat.btn_send_text': '그냥 메시지로 전송',
        'chat.meeting_proposal': '미팅 제안',
        'chat.meeting_reschedule': '미팅 수정 제안',
        'chat.btn_accept': '수락',
        'chat.btn_reschedule': '수정 제안',
        'chat.btn_decline': '거절',
        'chat.status_accepted': '수락됨',
        'chat.status_declined': '거절됨',
        'chat.status_pending': '응답 대기중',
        'chat.status_countered': '수정 제안 전송됨',
        'chat.leave_room': '채팅방 나가기',
        'chat.confirm_leave': '채팅방을 나가시겠습니까? 대화 내용이 삭제됩니다.',
        'chat.alert_leave_error': '채팅방 나가기에 실패했습니다.',
        'chat.empty_messages': '대화 내역이 없습니다',
        'chat.empty_messages_desc': '상대방이 수락하지 않거나 상황에 따라 답변이 늦을 수 있습니다. 현장 방문을 권장하며, 이 기능은 보조 수단입니다.',
        'chat.meeting_proposal_title': '📅 미팅 제안',
        'chat.meeting_reschedule_title': '🕒 일정 변경 제안',
        'chat.meeting_proposal_format': '{title}\n일시: {datetime}\n장소: {location}',
        'chat.meeting_reschedule_format': '일정 변경 제안: {title}\n변경 일시: {datetime}\n장소: {location}',
        'chat.meeting_keywords': '미팅,meeting,회의,meet,schedule,일정,약속,see you',
        // 🔥 [추가] 채팅 로딩 및 미팅 키워드 감지 모달
        'chat.loading': '채팅을 불러오는 중...',
        'chat.ask_send_meeting_card': '미팅 관련 키워드가 감지되었습니다. 미팅 카드를 보내시겠습니까?',
        'chat.btn_no_thanks': '괜찮습니다',
        'chat.toast_meeting_sent': '미팅 제안을 보냈습니다.',
        'chat.toast_meeting_accepted': '미팅이 수락되었습니다.',
        'chat.toast_meeting_declined': '미팅이 거절되었습니다.',
        'chat.meeting_added_to_calendar': '미팅이 일정에 추가되었습니다.',
        'chat.modal_meeting_title': '미팅 제안하기',
        'chat.modal_reschedule_title': '일정 변경하기',
        'chat.modal_meeting_subject': '미팅 주제',
        'chat.modal_meeting_subject_placeholder': '예: 비즈니스 파트너십 논의',
        'chat.modal_date': '날짜',
        'chat.modal_time': '시간',
        'chat.modal_location': '장소',
        'chat.modal_location_placeholder': '예: A홀 102호 부스',
        'chat.modal_original_title': '기존 일정',
        'chat.modal_new_date': '변경 날짜',
        'chat.modal_new_time': '변경 시간',
        'chat.modal_new_location': '변경 장소',
        'chat.btn_send_proposal': '제안 보내기',
        'chat.btn_send_reschedule': '변경 제안 보내기',
        'chat.alert_meeting_required': '주제, 날짜, 시간은 필수 입력 항목입니다.',
        'chat.alert_no_receiver': '대화 상대방을 찾을 수 없습니다.',
        'chat.error_invalid_datetime': '유효하지 않은 날짜/시간입니다.',
        'chat.location_tbd': '장소 미정',
        'chat.alert_meeting_sent': '미팅 제안을 보냈습니다.',
        'chat.alert_meeting_send_error': '미팅 제안 전송에 실패했습니다.',
        'chat.alert_reschedule_sent': '일정 변경 제안을 보냈습니다.',
        'chat.other_party': '상대방',
        'chat.meeting_accepted_message': '미팅 제안을 수락했습니다.',
        'chat.meeting_declined_message': '미팅 제안을 거절했습니다.',
        'chat.alert_no_meeting_id': '미팅 정보를 찾을 수 없습니다.',
        'chat.alert_meeting_accepted': '미팅이 수락되었습니다.',
        'chat.alert_meeting_declined': '미팅이 거절되었습니다.',
        'chat.alert_meeting_accept_error': '미팅 수락 중 오류가 발생했습니다: {message}',
        'chat.alert_meeting_decline_error': '미팅 거절 중 오류가 발생했습니다: {message}',
        'chat.alert_no_reschedule_info': '변경할 미팅 정보를 찾을 수 없습니다.',
        'chat.alert_reschedule_error': '변경할 미팅 정보가 없습니다.',
        'chat.preview_image': '📷 사진',
        'chat.preview_file': '📎 파일',
        'chat.preview_system': '📢 시스템 메시지',
        'chat.preview_meeting_reschedule_sent': '🕒 일정 변경 제안을 보냈습니다.',
        'chat.preview_meeting_reschedule_received': '🕒 일정 변경 제안이 도착했습니다.',
        'chat.preview_meeting_rescheduled_sent': '🕒 일정 변경을 수락했습니다.',
        'chat.preview_meeting_rescheduled_received': '🕒 미팅 일정이 변경되었습니다.',
        'chat.preview_meeting_cancelled_sent': '🚫 미팅을 취소했습니다.',
        'chat.preview_meeting_cancelled_received': '🚫 미팅이 취소되었습니다.',
        'chat.status_message_accepted': '{name}님이 미팅을 수락했습니다.',
        'chat.status_message_declined': '{name}님이 미팅을 거절했습니다.',
        'chat.status_message_rescheduled': '{name}님이 일정 변경을 수락했습니다.',
        'chat.status_message_cancelled': '{name}님이 미팅을 취소했습니다.',
        'chat.status_message_time': '시간',
        'chat.status_message_location': '장소',
        'chat.status_message_reason': '사유',

        'notifications.title': '알림',
        'notifications.mark_all_read': '모두 읽음',
        'notifications.mark_all_read_success': '모든 알림을 읽음 처리했습니다.',
        'notifications.empty': '새로운 알림이 없습니다.',
        'notifications.swipe_delete_hint': '왼쪽으로 밀어서 삭제',
        'notifications.time_just_now': '방금 전',
        'notifications.time_minutes_ago': '{count}분 전',
        'notifications.time_hours_ago': '{count}시간 전',
        'notifications.new_arrived': '🔔 새로운 알림이 도착했습니다!',
        'notifications.delete_failed': '알림 삭제에 실패했습니다.',
        'notifications.load_failed': '알림을 불러오는데 실패했습니다.',
        'notifications.default_title': '알림',
        'notifications.meeting_default_title': '미팅',
        'notifications.meeting_proposed.title': '미팅 제안',
        'notifications.meeting_proposed.message': '{name}님이 {title} 미팅 후보를 제안했습니다.',
        'notifications.meeting_accepted.title': '미팅 수락',
        'notifications.meeting_accepted.message': '{name}님이 {title} 미팅을 수락했습니다. {time}',
        'notifications.meeting_rejected.title': '미팅 거절',
        'notifications.meeting_rejected.message': '{name}님이 {title} 미팅을 거절했습니다. {reason}',
        'notifications.meeting_rescheduled.title': '일정 변경 수락',
        'notifications.meeting_rescheduled.message': '{name}님이 {title} 미팅의 일정 변경을 수락했습니다. {time}',
        'notifications.meeting_cancelled.title': '미팅 취소',
        'notifications.meeting_cancelled.message': '{title} 미팅이 취소되었습니다. {reason}',
        'notifications.meeting_reminder_1day.title': '미팅 하루 전 알림',
        'notifications.meeting_reminder_1day.message': '{title} 미팅이 내일 예정되어 있습니다. {time}',
        'notifications.meeting_reminder_1hour.title': '미팅 1시간 전 알림',
        'notifications.meeting_reminder_1hour.message': '{title} 미팅이 1시간 후 시작됩니다. {time}',
        'notifications.meeting_reminder_10min.title': '미팅 10분 전 알림',
        'notifications.meeting_reminder_10min.message': '{title} 미팅이 10분 후 시작됩니다. {time}',
        'notifications.meeting_pending_reminder.title': '미팅 응답 대기',
        'notifications.meeting_pending_reminder.message': '{title} 미팅 제안에 아직 응답하지 않았습니다.',
        'notifications.meeting_expired_reminder.title': '미팅 제안 만료',
        'notifications.meeting_expired_reminder.message': '{title} 미팅 제안이 만료되었습니다.',
        'notifications.survey_reminder.title': '설문 요청',
        'notifications.survey_reminder.message': '{title} 관련 설문을 작성해주세요.',
        'notifications.event_reminder.title': '이벤트 알림',
        'notifications.event_reminder.message': '{title} 일정이 곧 시작됩니다. {time}',

        // 🔥🔥🔥 미팅 미리보기 - 보낸사람용
        'chat.preview_meeting_sent': '📅 미팅 제안을 보냈습니다.',
        'chat.preview_meeting_accepted_sent': '✅ 미팅을 수락했습니다.',
        'chat.preview_meeting_rejected_sent': '❌ 미팅을 거절했습니다.',

        // 🔥🔥🔥 미팅 미리보기 - 받는사람용
        'chat.preview_meeting_received': '📅 미팅 제안이 도착했습니다.',
        'chat.preview_meeting_accepted_received': '✅ 미팅이 수락되었습니다.',
        'chat.preview_meeting_rejected_received': '❌ 미팅이 거절되었습니다.',

        // 기존 키 (하위 호환성)
        'chat.preview_meeting': '📅 미팅 제안이 도착했습니다.',
        'chat.preview_meeting_accepted': '✅ 미팅이 수락되었습니다.',
        'chat.preview_meeting_rejected': '❌ 미팅이 거절되었습니다.',
        'chat.preview_new_message': '📅 새 메시지가 있습니다.',
        'chat.pending_title': '승인 대기 중',
        'chat.pending_message': '현재 사무국 승인 대기 중입니다.\n승인이 완료되면 채팅 기능을 이용하실 수 있습니다.',
        'chat.access_denied_title': '회원 전용 기능',
        'chat.guest_message': '채팅 기능은 회원만 이용할 수 있습니다.',
        'chat.visitor_message': '채팅, 매칭, 미팅 요청 기능은 비즈니스 회원 전용입니다.\n비즈니스 회원으로 가입신청 하시겠습니까?',
        'chat.alert_load_error': '채팅방을 불러오는데 실패했습니다.',
        'chat.alert_send_error': '메시지 전송에 실패했습니다.',
        'chat.alert_reschedule_send_error': '수정 제안 전송에 실패했습니다: {message}',
        'chat.view_profile': '프로필 보기',
        'chat.unknown_user': '알 수 없는 사용자',
        'chat.profile_title': '프로필',
        'chat.business_card': '명함',
        'chat.click_to_enlarge': '클릭하면 크게 볼 수 있습니다',
        // 🔥🔥🔥 [2026-01-09 추가] 파일 첨부 관련 키
        'chat.max_files_exceeded': '최대 {count}개의 파일만 선택할 수 있습니다.',
        'chat.file_too_large': '파일 크기가 10MB를 초과합니다.',
        'chat.file_type_not_allowed': '지원하지 않는 파일 형식입니다.',
        'chat.upload_cancelled': '업로드가 취소되었습니다.',
        'chat.file_sent': '파일을 전송했습니다.',
        'chat.files_sent': '{count}개 파일을 전송했습니다.',
        'chat.file_send_error': '파일 전송에 실패했습니다.',
        'chat.attach_file': '파일 첨부',
        'chat.add_caption': '설명 추가 (선택사항)',
        'chat.drop_files_here': '파일을 여기에 놓으세요',
        'chat.max_files_info': '최대 5개 파일, 각 10MB 이하',
        'chat.download': '다운로드',
        'chat.preview_not_available': '미리보기를 사용할 수 없습니다.',

        // 🔥🔥🔥 [2026-01-09 추가] 음성 메시지 관련 키
        'chat.voice_permission_denied': '마이크 권한이 필요합니다.',
        'chat.voice_message': '음성 메시지',
        'chat.voice_sent': '음성 메시지를 전송했습니다.',
        'chat.voice_send_error': '음성 메시지 전송에 실패했습니다.',

        // 🔥🔥🔥 [2026-01-09 추가] 비디오 메시지 관련 키
        'chat.video_permission_denied': '카메라 권한이 필요합니다.',
        'chat.video_message': '비디오 메시지',
        'chat.video_sent': '비디오 메시지를 전송했습니다.',
        'chat.video_send_error': '비디오 메시지 전송에 실패했습니다.',
        'chat.record_video': '비디오 녹화',
        'chat.retake': '다시 녹화',
        'chat.start_recording': '녹화 시작',


        'activities.start_conversation': '대화를 시작해보세요',
        // 활동
        'activity.tab_chat': '채팅',
        'activity.tab_match': '매칭',
        'activity.empty_chat': '채팅 내역이 없습니다',
        'activity.empty_chat_sub': '기업을 탐색하고 대화를 시작해보세요',
        'activity.explore_btn': '기업 탐색하기',
        'events.explore_btn': '이벤트 탐색하기',
        'activities.tab_chats': '채팅',
        'activities.tab_matching': '매칭',
        'activities.loading_chats': '채팅 목록을 불러오는 중...',
        'activities.loading_matches': '매칭 정보를 분석 중...',
        'activities.empty_chats': '대화 내역이 없습니다',
        'activities.empty_chats_desc': '관심 있는 기업을 찾아 대화를 시작해보세요.',
        'activities.empty_matches': '아직 추천된 기업이 없습니다',
        'activities.empty_matches_desc': '프로필 정보를 업데이트하면 더 정확한 추천을 받을 수 있습니다.',
        'activities.btn_explore': '기업 탐색하기',
        'activities.btn_retry': '다시 시도',
        'activities.btn_preferences': '매칭 설정 변경',
        'activities.btn_send_message': '메시지 보내기',
        'activities.no_name': '이름 없음',
        'activities.no_messages': '메시지가 없습니다.',
        'activities.no_company_name': '기업명 없음',
        'activities.no_industry': '업종 정보 없음',
        'activities.booth_prefix': '부스',
        'activities.time_just_now': '방금 전',
        'activities.time_minutes_ago': '{minutes}분 전',
        'activities.time_hours_ago': '{hours}시간 전',
        'activities.time_days_ago': '{days}일 전',
        'activities.error_load_matches': '매칭 정보를 불러오는데 실패했습니다.',
        'activities.delete_chat': '채팅방 삭제',
        'activities.delete_chat_title': '채팅방 나가기',
        'activities.delete_chat_message': '"{name}" 채팅방을 나가시겠습니까?\n채팅 내용은 삭제되며 복구할 수 없습니다.',
        'activities.confirm_delete': '이 채팅방을 나가시겠습니까?\n채팅 내용은 복구할 수 없습니다.',
        'activities.delete_confirm': '나가기',
        'activities.delete_failed': '채팅방 삭제에 실패했습니다.',

        
        // 챗봇
        'chatbot.title': '이월이',
        'chatbot.subtitle': 'DSK',
        'chatbot.welcome': '안녕하세요! 👋\n\nDSK 이월이입니다.\n궁금한 기업이나 부스 위치를 물어보세요!',
        'chatbot.placeholder': '메시지를 입력하세요...',
        'chatbot.suggestions_label': '추천 질문',
        'chatbot.q1': '화장실이 어디인가요?',
        'chatbot.q2': '드론 기업 추천해줘',
        'chatbot.q3': '내 관심분야 기업 추천해줘',
        'chatbot.clear': '대화 지우기',
        'chatbot.close': '닫기',
        'chatbot.relatedCompanies': '관련 기업 추천',
        'chatbot.fold': '접기',
        'chatbot.unfold': '펼치기',
        'chatbot.detail': '상세보기',
        'chatbot.booth': '부스',
        'chatbot.thinking': '답변 생성 중...',
        'chatbot.error': '오류가 발생했습니다.',
        'chatbot.confirmClear': '대화를 삭제하시겠습니까?',
        'chatbot.resetMessage': '대화가 초기화되었습니다.',
        'chatbot.responding': '응답 중...',
        'chatbot.nav_error': '죄송합니다. 경로 안내 중 오류가 발생했습니다.',
        'chatbot.lang_change_ko': '한국어로 변경',
        'chatbot.lang_change_en': 'Change to English',
        'chatbot.lang_change_ja': '日本語に変更',
        'chatbot.lang_change_zh': '切换到中文',
        'chatbot.lang_accept_ko': '네, 한국어로 변경할게요',
        'chatbot.lang_accept_en': 'Yes, change to English',
        'chatbot.lang_accept_ja': 'はい、日本語に変更します',
        'chatbot.lang_accept_zh': '是的，切换到中文',
        'chatbot.lang_decline': '아니요, 그대로 쓸게요',
        'chatbot.meeting_header': '비즈니스 상담 예약',
        'chatbot.meeting_reserve': '상담 예약',
        'chatbot.more_companies': '외 {count}개 기업',
        'chatbot.survey_shortcut': '설문조사 바로가기',
        'chatbot.send_failed': '전송 실패',

        // 🔥 [추가] 3D 부스맵 관련 키
        'map.exhibition_hall_title': '{venueFullName}',
        'map.exhibition_subtitle': '{eventName}',
        'map.passable': '통행로',
        'map.search_result': '검색결과',
        'map.selected': '선택됨',
        'map.no_info': '정보 없음',
        'map.more_companies': '+{count}개 기업 더 있음',
        'map.rotate_left': '왼쪽으로 회전',
        'map.rotate_right': '오른쪽으로 회전',
        'map.reset_view': '초기 화면으로',
        'map.entrance': '입구',
        'map.exit': '출구',
        'map.toilet': '화장실',
        'map.booths_showing': '{count}개 부스 표시 중',
        'map.reset': '초기화',
        'map.facility_desc': '관람객을 위한 편의 시설입니다.',
        'map.tap_to_highlight_all': '탭하면 전체 위치를 표시합니다',
        'map.places': '곳',
        'map.no_company_info': '등록된 기업 정보가 없습니다.',
        // 🔥 [추가] 부스맵 페이지 하드코딩 다국어 처리
        'map.search_placeholder': '부스번호 또는 회사명 검색...',
        'map.loading_map': '맵 데이터 로딩 중...',
        'map.loading_3d': '3D 뷰 로딩 중...',
        'map.load_failed': '맵 데이터 로드 실패',
        'map.preparing': '부스맵 준비 중...',
        'map.coming_soon_title': '🗺️ 부스맵 오픈 예정',
        'map.coming_soon_message': '이 기능은 2월 2일 오픈 예정입니다.',
        'map.alert_select_both': '출발지와 도착지를 모두 입력해주세요.',
        'map.alert_no_route': '현재 유효한 경로를 찾을 수 없습니다.',
        'map.alert_route_error': '경로 탐색 중 오류가 발생했습니다.',
        'map.alert_invalid_booth': '존재하지 않는 부스 번호입니다. 다시 확인해 주세요.',
        'map.nav_title': '🧭 길찾기',
        'map.nav_reset': '초기화',
        'map.nav_select_start': '출발지 선택',
        'map.nav_select_end': '도착지 선택',
        'map.nav_routing': '경로 안내 중',
        'map.nav_hide': '숨기기',
        'map.nav_calculating': '경로 계산 중...',
        'map.nav_start': '경로 안내 시작',
        'map.nav_set_start': '출발지',
        'map.nav_set_end': '도착지',
        'map.company_detail': '기업 상세정보',
        'map.company_count': '{count}개 기업 입점',
        'map.company_detail_short': '상세',
        'map.booth_count': '{count}개 부스',
        'map.company_extra': '외 {count}개',
        'map.optimal_route_active': '최적 경로 안내 중',
        'map.booth_count_label': '{count}개 부스',
        'map.approx_distance': '약 {distance}m',
        'map.location_prompt_title': '📍 현재 어디에 계신가요?',
        'map.location_prompt_desc': '가장 가까운 동선을 계산하기 위해\n현재 위치한 부스 번호를 입력해 주세요.',
        'map.location_prompt_placeholder': '예: A01, D18',
        'map.congestion_title': '실시간 혼잡도',
        'map.congestion_low': '여유',
        'map.congestion_medium': '보통',
        'map.congestion_high': '혼잡',


        // 🔥 [추가] 부스맵 관심기업 순회
        'boothMap.favoriteRoute': '관심기업 동선 안내',
        'boothMap.favoriteTraverse': '관심기업 순회',
        'boothMap.noFavoriteCompanies': '관심기업이 없습니다.',
        'boothMap.routeCalculationFailed': '경로를 계산할 수 없습니다.',
        'boothMap.routeCalculationError': '동선 계산 중 오류가 발생했습니다.',
      },
      
      en: {
        'app.name': 'DSK',
        'app.subtitle': '{eventName}',
        'app.description': '{eventName} - Carbon Neutral Exhibition Platform',
        'userTypes.VISITOR': 'Visitor',
        'userTypes.BUSINESS': 'Business',
        'userTypes.COMPANY': 'Exhibitor',
        'userTypes.BUYER': 'Buyer',
        'userTypes.ADMIN': 'Admin',
        'userTypes.BUSINESS_PENDING': 'Pending Approval',
        'userTypes.INVESTOR': 'Investor',
        'userTypes.BUYER_PENDING': 'Pending Approval',


        'common.ok': 'OK',
        'common.cancel': 'Cancel',
        'common.save': 'Save',
        'common.delete': 'Delete',
        'common.edit': 'Edit',
        'common.search': 'Search',
        'common.filter': 'Filter',
        'common.sort': 'Sort',
        'common.loading': 'Loading...',
        'common.error': 'An error occurred',
        'common.success': 'Success',
        'common.retry': 'Retry',
        'common.load_failed': 'Failed to load data. Please check your connection.',
        'survey.complete_first': 'Available after completing the survey.',
        'auth.verification_code_required': 'Please enter the 6-digit verification code.',
        'auth.verification_code_invalid': 'Verification code does not match.',
        'auth.sms_verify_first': 'Please complete SMS verification first.',
        'common.refresh': 'Refresh',
        'common.back': 'Back',
        'common.next': 'Next',
        'common.previous': 'Previous',
        'common.close': 'Close',
        'common.share': 'Share',
        'common.copy': 'Copy',
        'common.download': 'Download',
        'common.upload': 'Upload',
        'common.send': 'Send',
        'common.submit': 'Submit',
        'common.reset': 'Reset',
        'common.confirm': 'Confirm',
        'common.logout': 'Logout',
        'common.guest': 'Guest',
        'common.saved': 'Saved.',
        'common.deleted': 'Deleted.',
        'common.confirm_delete': 'Are you sure?',
        'common.uncategorized': 'Uncategorized',
        'common.count_unit': 'items',
        'common.count_suffix': 'times',
        'common.minutes': 'min',
        'common.hours': 'hours',
        'common.days': 'days',
        'common.user': 'User',
        'common.no_email': 'No Email',
        'common.no_name': 'No Name',
        'common.sending': 'Sending...',
        'common.close': 'Close',
        'common.uploading': 'Uploading...',
        'common.saving': 'Saving...',

        'auth.login': 'Login',
        'auth.login_required': 'Please log in.',

        // 🔥 [Added] Company & Categories
        'company.intro_title': 'Company Introduction',
        'category.all': 'All',
        'category.drone_platforms': 'Drone Platforms & Hardware',
        'category.military_defense_uavs': 'Military, Defense & Security Drones',
        'category.industrial_uavs': 'Industrial & Commercial Drone Applications',
        'category.autonomous_flight_utm': 'Autonomous Flight, UTM & UAM',
        'category.battery_power_charging': 'Battery, Power & Charging Solutions',
        'category.motors_propellers_components': 'Motors, Propellers & Components',
        'category.ai_software_data': 'AI, Software & Data',
        'category.sensors_navigation_cameras': 'Cameras, Sensors, Communications & Navigation',
        'category.ugv_usv_robotics': 'Unmanned Vehicle Expansion (UGV/USV/Robotics)',
        'category.space_aerospace': 'Space & Aerospace Technology',
        'category.training_education_services': 'Training, Education & Services',
        'category.sustainable_aviation': 'Sustainable Aviation',
        'category.other_interest': 'Other',
        'category.group_basic': 'Basic',
        'category.group_airframe': 'Airframes / Platforms',
        'category.group_technology': 'Technology / Systems',
        'category.group_power': 'Power / Components',
        'category.group_unmanned': 'Unmanned Mobility',
        'category.group_services': 'Training / Services',
        'category.group_other': 'Other',
        'auth.logout': 'Logout',
        'auth.register': 'Sign Up',
        'auth.spc_exhibitor_notice': 'Exhibiting company? Start as a Business Visitor — after company verification, your account is upgraded to an exhibitor account.', // 🔥 [2026-06-10 B-3 D1] SPC exhibitor 유입 안내
        'welcome.return_to_guide': 'Back to the expo guide', // 🔥 [2026-06-10 B-3 D4] SPC return_url CTA
        'auth.email': 'Email',
        'auth.login_id': 'Email or ID (company name)',
        'year_consent.title': 'Confirm consent for {year}',
        'year_consent.message': 'You can keep using your existing account for the {year} event. Please confirm the consents below for this year.',
        'year_consent.all': 'Agree to all',
        'year_consent.terms': '[Required] Terms of Service',
        'year_consent.privacy': '[Required] Collection and use of personal information',
        'year_consent.third_party': '[Required] Third-party provision (business matching)',
        'year_consent.third_party_info': 'For business matching, your company name, contact person, contact info, and areas of interest are provided to the matched company.',
        'year_consent.sms': '[Optional] SMS notifications',
        'year_consent.email': '[Optional] Marketing emails',
        'year_consent.night': '[Optional] Night-time ads (21:00–08:00)',
        'year_consent.view': 'View',
        'year_consent.hint': 'You can change optional items anytime in Profile > Notification Settings.',
        'year_consent.required_missing': 'Please agree to the required items.',
        'year_consent.save_failed': 'Could not save. Please try again.',
        'year_consent.logout': 'Log out',
        'year_consent.submit': 'Agree and continue',
        'year_consent.terms_title': 'Terms of Service',
        'year_consent.privacy_title': 'Privacy Policy',
        'year_consent.confirm_agree': 'Confirm and agree',
        'year_consent.close': 'Close',
        'auth.password': 'Password',
        'auth.show_password': 'Show password',
        'auth.hide_password': 'Hide password',
        'auth.passwordConfirm': 'Confirm Password',
        'auth.name': 'Name',
        'auth.phone': 'Phone',
        'auth.company': 'Company',
        'auth.forgotPassword': 'Forgot Password',
        'auth.resetPassword': 'Reset Password',
        'auth.socialLogin': 'Social Login',
        'auth.loginWithGoogle': 'Login with Google',
        'auth.loginWithKakao': 'Login with Kakao',
        'auth.loginWithNaver': 'Login with Naver',
        'auth.loginWithApple': 'Login with Apple',
        'auth.title_sub': '{eventName}',
        'auth.guest': 'Guest',
        'auth.guest_login': 'Guest',
        'auth.auto_login': 'Remember Me',
        'auth.forgot_pw': 'Forgot Password?',
        'auth.no_account': 'No account?',
        'auth.error_email': 'Please enter email',
        'auth.error_pw': 'Please enter password',
        'auth.fail_login': 'Login failed.',
        'nav.home': 'Home',
        'nav.companies': 'Companies',
        'nav.meetings': 'Meetings',
        'nav.chats': 'Chats',
        'nav.carbon': 'Carbon Points',
        'nav.profile': 'Profile',
        'nav.settings': 'Settings',
        'nav.notifications': 'Notifications',
        'nav.help': 'Help',
        'nav.events': 'Events',
        'nav.activities': 'Activities',
        'nav.map': 'Map',
        'nav.matching': 'Matching',
        'profile.language_setting': 'Language',
        'profile.language_desc': 'Change app language',
        'profile.change_language': 'Change Language',
        'profile.interests_saved': 'Interests saved successfully.',
        'profile.interests_saved_refreshing': 'Interests saved. Matching will refresh shortly.',
        'profile.interests_no_change': 'No changes to save.',
        'profile.interests_save_failed': 'Failed to save interests.',
        'profile.my_info': 'My Info',
        'profile.profile_settings': 'Account settings',
        'profile.company_info': 'Company Info',
        'profile.company_info_desc': 'Manage company profile & products',
        'profile.carbon_points': 'Carbon Points',
        'profile.carbon_desc': 'Carbon footprint history',
        'profile.bookmarks': 'Bookmarks',
        'profile.bookmarks_desc': 'Saved events and companies',
        'profile.notifications': 'Notifications',
        'profile.notifications_desc': 'Push & Email settings',
        'profile.settings': 'Settings',
        'profile.settings_desc': 'App version & config',
        'profile.activity_details': 'Activity Details',
        'profile.total_matches': 'Total Matches',
        'profile.completed_meetings': 'Completed Meetings',
        'profile.completed_events': 'Attended Events',
        // 🔥 [Added] Profile dynamic texts
        'profile.carbon_total_saved': '🌱 Total {amount}kg Reduced',
        'profile.tree_effect': '🌲 Equiv. to {count} trees',
        'profile.activity_hint': '🌱 Record your activity!',
        'profile.badges': 'Badges',
        'profile.no_badges': 'Visit booths to earn badges.',
        'profile.badge.explorer': 'Explorer',
        'profile.badge.curator': 'Curator',
        'profile.badge.master': 'Master',
        'profile.badge_threshold': '{count} booths',
        'profile.badge_tooltip': '{name} (visit {count} booths)',
        'settings.title': 'App Settings',
        'settings.language': 'Language',
        'settings.language_label': 'App Language',
        'settings.language_desc': '* Some content may appear in original language.',
        'settings.screen': 'Screen',
        'settings.theme': 'Theme',
        'settings.theme_light': 'Light Mode',
        'settings.theme_dark': 'Dark Mode',
        'settings.theme_system': 'System',
        'settings.font_size': 'Font Size',
        'settings.font_small': 'Small',
        'settings.font_medium': 'Medium',
        'settings.font_large': 'Large',
        'settings.media': 'Media',
        'settings.autoplay': 'Auto-play Video',
        'settings.autoplay_desc': 'Auto-play on Wi-Fi',
        'settings.data_saver': 'Data Saver',
        'settings.data_saver_desc': 'Reduce image quality to save data',
        'settings.data_mgmt': 'Data Management',
        'settings.clear_cache': 'Clear Cache',
        'settings.clear_cache_desc': 'Delete temporary data',
        'settings.clear_data': 'Clear All Data',
        'settings.clear_data_desc': 'Logout and delete all data',
        'settings.app_info': 'App Info',
        'settings.version': 'Version',
        'settings.build': 'Build',
        'settings.developer': 'Developer',
        'settings.terms': 'Terms of Service',
        'settings.privacy': 'Privacy Policy',
        'settings.licenses': 'Open Source Licenses',
        'settings.alert_cache_cleared': 'Cache cleared.',
        'settings.modal_delete_title': '⚠️ Delete Data',
        'settings.modal_delete_confirm': 'Delete all data? This cannot be undone.',
        'settings.font_size_desc': 'Change the font size for the entire app.',
        'settings.data_saver_on': '✓ Enabled - Using thumbnail images',
        'settings.data_saver_off': 'Disabled - Using high quality images',


        'myinfo.title': 'Edit Profile',
        'myinfo.profile_image': 'Profile Image',
        'myinfo.profile_alt': 'Profile Picture',
        'myinfo.change_photo': 'Change Photo',
        'myinfo.photo_change_coming_soon': 'Photo change feature is coming soon.',
        // 🔥 [Added] Carbon impact card keys
        'myinfo.carbon_impact': 'My Carbon Impact',
        'myinfo.tree_equivalent': 'Trees Equivalent',
        'myinfo.trees': 'trees',
        'myinfo.view_carbon_details': 'View Carbon Details',
        'myinfo.basic_info': 'Basic Info',
        'myinfo.name': 'Name',
        'myinfo.name_placeholder': 'Enter name',
        'myinfo.email': 'Email',
        'myinfo.email_desc': 'Email cannot be changed',
        'myinfo.email_unchangeable': 'Email cannot be changed.',
        'myinfo.phone': 'Phone',
        'myinfo.phone_placeholder': 'Enter phone number',
        'myinfo.work_info': 'Work Info',
        'myinfo.department': 'Department',
        'myinfo.department_placeholder': 'Enter department',
        'myinfo.position': 'Position',
        'myinfo.position_select': 'Select your position',
        'myinfo.position.staff': 'Staff',
        'myinfo.position.senior': 'Senior Staff',
        'myinfo.position.assistant_manager': 'Assistant Manager',
        'myinfo.position.manager': 'Manager',
        'myinfo.position.deputy_manager': 'Deputy Manager',
        'myinfo.position.general_manager': 'General Manager',
        'myinfo.position.director': 'Director',
        'myinfo.position.executive': 'Executive',
        'myinfo.position.ceo': 'CEO',
        'myinfo.position.other': 'Other',
        'myinfo.bio': 'Bio',
        'myinfo.bio_title': 'One-line Bio',
        'myinfo.bio_label': 'One-line Bio',
        'myinfo.bio_placeholder': 'Brief introduction (Visible in B2B matching)',
        'myinfo.categories_count': 'Selected',
        'myinfo.error_max_categories': 'You can select up to 5 interest categories.',
        'myinfo.interests_title': 'Interests',
        'myinfo.interests_desc': 'Select categories you are interested in and describe specific products or technologies you are looking for.',
        'myinfo.interest_text_label': 'Products/Technologies of Interest (Free text)',
        'myinfo.interest_text_placeholder': 'Describe the products or technologies you are interested in.',
        'myinfo.save_interests': 'Save Interests',
        'myinfo.alert_name_required': 'Name is required',
        'myinfo.error_name_required': 'Name is required.',
        'myinfo.alert_phone_invalid': 'Invalid phone number',
        'myinfo.error_phone_invalid': 'Please enter a valid phone number.',
        'myinfo.photo_updated': 'Profile photo has been updated.',
        'myinfo.business_card': 'Business Card',
        'myinfo.business_card_desc': 'Your business card will be displayed to partners during business meetings.',
        'myinfo.business_card_alt': 'Business card image',
        'myinfo.business_card_uploaded': 'Business card uploaded. Click Save to apply.',
        'myinfo.change_business_card': 'Change Card',
        'myinfo.remove_business_card': 'Remove Card',
        'myinfo.confirm_remove_business_card': 'Are you sure you want to remove the business card?',
        'myinfo.upload_business_card': 'Upload Business Card',
        'myinfo.upload_business_card_hint': 'Click to select an image (max 5MB)',
        'myinfo.business_card_notice': 'Click "Save" button below to apply business card changes.',
        // 🔥 [Added] Security/Password keys
        'myinfo.security': 'Security',
        'myinfo.password_desc': 'We recommend changing your password regularly for account security.',
        'myinfo.change_password': 'Change Password',
        'myinfo.current_password': 'Current Password',
        'myinfo.new_password': 'New Password',
        'myinfo.confirm_password': 'Confirm New Password',
        'myinfo.password_changed': 'Password has been changed successfully.',
        'myinfo.password_hint': 'Password must be at least 8 characters.',
        'myinfo.change_password_btn': 'Change',
        // Account Initial Setup (ChangePasswordPage)
        'setup.title': 'Account Setup',
        'setup.subtitle': 'Please change your temporary password for security.',
        'setup.subtitle_survey': 'You will be redirected to {survey} after setup.',
        'setup.survey': 'survey',
        'setup.current_password': 'Current (temporary) password *',
        'setup.change_info': 'New Information',
        'setup.new_email': 'Email to use (will be your ID) *',
        'setup.contact_name': 'Contact name *',
        'setup.phone_label': 'Contact phone number',
        'setup.phone_required': '* Verification required',
        'setup.phone_placeholder': 'Enter phone number',
        'setup.select_country': 'Select country',
        'setup.no_results': 'No results',
        'setup.send_code': 'Send Code',
        'setup.resend_code': 'Resend',
        'setup.verified': 'Verified',
        'setup.new_password': 'New password (min 8 chars) *',
        'setup.confirm_password': 'Confirm new password *',
        'setup.sms_consent': 'I agree to receive SMS notifications (optional)',
        'setup.submit': 'Complete Setup',
        'setup.complete_alert': 'Account setup is complete.\nPlease log in again with your new email.',
        'setup.email_invalid': 'Invalid email format.',
        'setup.name_required': 'Please enter your name.',
        'setup.phone_invalid_kr': 'Please enter a valid phone number. (e.g. 010-1234-5678)',
        'setup.overseas_notice': 'For international numbers, exclude country code and leading 0.',
        'setup.sms_description': 'Receive meeting reminders, event notifications, and more via SMS.',
        'setup.sms_optional_note': 'Declining does not limit account setup or use of the service.',
        'setup.notice': 'After setup, you must log in again with your new email (ID).',
        'setup.notice_label': 'Note',
        'setup.processing': 'Processing...',
        'setup.phone_verified': 'Phone number has been verified.',
        'setup.enter_code': 'Enter the 6-digit verification code sent to your phone.',
        'setup.code_placeholder': '6-digit code',
        'setup.confirm': 'Confirm',
        'myinfo.error_current_password_required': 'Please enter your current password.',
        'myinfo.error_new_password_required': 'Please enter a new password.',
        'myinfo.error_password_min_length': 'Password must be at least 8 characters.',
        'myinfo.error_confirm_password_required': 'Please confirm your password.',
        'myinfo.error_password_mismatch': 'Passwords do not match.',
        'myinfo.error_current_password_wrong': 'Current password is incorrect.',
        // 🔥 [Added] Password real-time validation messages
        'myinfo.password_valid': 'Password is valid.',
        'myinfo.password_match': 'Passwords match.',
        'myinfo.current_password_valid': 'Password verified.',

        // 🔥 [Added] Additional password change keys
        'error.password_change_failed': 'Failed to change password.',
        'myinfo.current_password_placeholder': 'Enter current password',
        'myinfo.new_password_placeholder': 'At least 8 characters',
        'myinfo.confirm_password_placeholder': 'Re-enter new password',
        'myinfo.intl_phone_hint': 'Select your country code and enter your phone number.',
        'myinfo.phone_placeholder_intl': 'Phone number (without country code)',

        'common.processing': 'Processing...',


        'company_info.title': 'Company Info',
        'company_info.no_company': 'No Company Registered',
        'company_info.no_company_desc': 'Register to use B2B matching services.',
        'company_info.register_btn': 'Register Company',
        'company_info.basic': 'Basic Info',
        'company_info.name': 'Company Name',
        'company_info.industry': 'Industry',
        'company_info.desc_label': 'Description',
        'company_info.desc_placeholder': 'Brief company description',
        'company_info.details': 'Details',
        'company_info.founded': 'Founded Year',
        'company_info.employees': 'Employees',
        'company_info.website': 'Website',
        'company_info.address': 'Address',
        'company_info.contact': 'Contact',
        'company_info.email': 'Main Email',
        'company_info.phone': 'Main Phone',
        'company_info.visit': 'Visit',
        'company_info.main_products_label': 'Main Products/Services',
        'company_info.main_products_placeholder': 'Enter product names separated by commas',
        'company_info.main_products_hint': 'e.g., Industrial Drone, Camera Equipment, Software Solution',
        'company_info.media_section': 'Banner & Promo Video',
        'company_info.banner_label': 'Banner image',
        'company_info.banner_hint': 'Shown at the top of your company page. Use a wide image (1200×400 or larger recommended).',
        'company_info.banner_upload': 'Upload banner',
        'company_info.banner_uploading': 'Uploading…',
        'company_info.banner_remove': 'Remove banner',
        'company_info.banner_upload_failed': 'Failed to upload the banner',
        'company_info.video_label': 'Promo video URL',
        'company_info.video_hint': 'Paste a video link (e.g., YouTube). It will be shown on your company page.',
        'company_info.video_view': 'Watch video',
        'company_info.edit_submitted': 'Your edit request has been submitted for admin review.',
        'company_info.pending_edit': 'You have a pending edit request. Changes will be applied after admin approval.',
        'company_info.no_changes': 'No changes detected.',
        'company_info.edit_restricted_notice': 'You can edit the description, main products, banner, and promo video. Contact admin for other changes.',
        'company_info.editable_section': 'Editable Fields',
        'company_info.submit_edit': 'Submit Edit Request',
        'company_info.description': 'Company Description',
        'company_info.description_placeholder': 'Write a detailed introduction about your company',
        'company_info.established_year': 'Founded',
        'company_info.employee_count': 'Employees',
        'company_info.year_suffix': '',
        'company_info.industry_select': 'Select Industry',
        'company_info.industry.drone_manufacturing': 'Drone Manufacturing',
        'company_info.industry.drone_service': 'Drone Service',
        'company_info.industry.software': 'Software',
        'company_info.industry.hardware': 'Hardware',
        'company_info.industry.logistics': 'Logistics',
        'company_info.industry.agriculture': 'Agriculture',
        'company_info.industry.construction': 'Construction',
        'company_info.industry.media': 'Media',
        'company_info.industry.security': 'Security',
        'company_info.industry.education': 'Education',
        'company_info.industry.research': 'Research',
        'company_info.industry.government': 'Government',
        'company_info.industry.other': 'Other',
        'company_info.industry.uncategorized': 'Uncategorized',
        'common.submitting': 'Submitting...',

        // Carbon (🔥 Added missing keys)
        'carbon.title': 'Carbon Calculator',
        'carbon.activity': 'Carbon Reduction Activity',
        'carbon.activities': 'Carbon Activities',
        'carbon.activities_desc': 'Check routes and reduction',
        'carbon.history': 'History',
        'carbon.history_desc': 'View past records',
        'carbon.status_title': '🌱 My Carbon Reduction',
        'carbon.saved': 'Saved',
        'carbon.trees': 'Trees',
        'carbon.trees_empty': 'Plant Trees',
        'carbon.environmental_impact': 'Env. Impact',
        'carbon.loading': 'Calculating...',
        'carbon.offset_needed': 'Offset Needed! ✈️',
        'carbon.great_job': 'Great Job! 🌿',
        'carbon.reduction_label': 'Total Reduction',
        'carbon.input_title': '📍 Travel Info',
        'carbon.origin_label': 'Where from?',
        'carbon.origin_custom': 'Custom Distance (km)',
        'carbon.transport_label': 'Transport Mode?',
        'carbon.passengers_label': 'Passengers (Carpool bonus)',
        'carbon.calc_btn': 'Calculate',
        'carbon.result_title': '🌱 Result',
        'carbon.distance': 'Distance',
        'carbon.transport': 'Mode',
        'carbon.baseline': 'Baseline (Car)',
        'carbon.actual': 'Actual',
        'carbon.reduction': 'Reduction',
        'carbon.tree_equiv': '🌳 Eqv. to {count} trees/year',
        'carbon.reward_text': 'Record now to get',
        'carbon.reward_suffix': '!',
        'carbon.submit_btn': '🎉 Earn Points',
        'carbon.submit_loading': 'Earning...',
        'carbon.history_title': 'History',
        'carbon.history_empty': 'No history yet',
        'carbon.fold': 'Fold',
        'carbon.unfold': 'Unfold',
        'carbon.guide_title': 'Reduction Tips',
        'carbon.guide_1': 'Public transport saves up to 80% CO₂ vs cars',
        'carbon.guide_2': 'EVs/Hybrids save 50%+ vs regular cars',
        'carbon.guide_3': 'Walk/Bike -> Zero Emissions!',
        'carbon.guide_4': 'Offset air travel with eco-activities',
        'carbon.transport.car': 'Car',
        'carbon.transport.bus': 'Bus',
        'carbon.transport.train': 'Train',
        'carbon.transport.plane': 'Plane',
        'carbon.transport.bicycle': 'Bicycle',
        'carbon.transport.walk': 'Walk',
        'carbon.points': 'Points',
        'carbon.earned': 'Earned',
        'carbon.total': 'Total',
        'carbon.redeem': 'Redeem',
        'carbon.leaderboard': 'Leaderboard',
        'carbon.reduce': 'Reduction',
        'carbon.trees_unit': 'trees',
        'carbon.hours_unit': 'hours',
        'carbon.cups_unit': 'cups',
        'carbon.offset_guide': 'Air travel has high emissions. Try offsetting with other activities!',
        'carbon.no_survey': 'Select departure and transport mode below to calculate.',
        // 🔥 [Added] Sidebar carbon reduction display
        'carbon.my_reduction': 'My Carbon Reduction',        
        'carbon.my_journey': 'My Journey to {venueName}',
        'carbon.survey_loaded': 'Survey data loaded automatically.',
        // 🔥 [Added] Region selection keys
        'carbon.province_city': 'Province/City',
        'carbon.select_region': 'Select Region',
        'carbon.district': 'District',
        'carbon.optional': 'Optional',
        'carbon.select_all': 'All / None',
        'carbon.departure_region': 'Departure Region',
        'carbon.transport_mode': 'Transport Mode',
        'carbon.recalculate': 'Recalculate',
        'carbon.saving': 'Saving...',
        'carbon.save_result': 'Save Result',
        'carbon.save_success': 'Saved successfully!',
        'carbon.save_failed': 'Failed to save.',
        'carbon.total_stats': 'Total Stats',
        'carbon.total_saved': 'Total Saved',
        'carbon.trees_equivalent': 'Trees Equivalent',
        'carbon.calc_note_1': 'Compared to a gasoline car (150g CO₂/km).',
        'carbon.calc_note_2': 'Destination',
        'carbon.exhibition_center': 'Exhibition Center 1',
        'carbon.calc_note_3': 'Distance calculated via Haversine formula (×1.35 road factor).',
        'carbon.excess': 'Excess Emissions',
        'carbon.my_emission_excess': 'Carbon Emission Excess',



// ==================== 🔥🔥🔥 [Added] Meeting Detail Page ====================
        'meeting.detail_title': 'Meeting Details',
        'meeting.phone_required_title': 'Phone Number Required',
        'meeting.phone_required': 'To request a meeting, a phone number is required so you can receive SMS notifications when the other party accepts or reschedules. Please register your phone number in your profile and try again.',
        'meeting.btn_go_register_phone': 'Register in Profile',
        'meeting.loading': 'Loading meeting information...',
        'meeting.load_error': 'Unable to load meeting.',
        'meeting.calendar_notice': 'This meeting will be saved to your in-app calendar',
        'meeting.time_location': 'Time & Location',
        'meeting.location_tbd': 'Location TBD',
        'meeting.description': 'Description',
        'meeting.agenda': 'Agenda',
        'meeting.agenda_item': 'Agenda Item',
        'meeting.participants': 'Participants',
        'meeting.organizer': 'Organizer',
        'meeting.notes': 'Notes',
        
        // Meeting status
        'meeting.status_confirmed': 'Confirmed',
        'meeting.status_scheduled': 'Scheduled',
        'meeting.status_in_progress': 'In Progress',
        'meeting.status_pending': 'Pending',
        'meeting.status_proposed': 'Proposed',
        'meeting.status_requested': 'Requested',
        'meeting.status_cancelled': 'Cancelled',
        'meeting.status_declined': 'Declined',
        'meeting.status_completed': 'Completed',
        'meeting.status_accepted': 'Accepted',
        
        // Priority
        'meeting.priority_high': 'High',
        'meeting.priority_urgent': 'Urgent',
        'meeting.priority_medium': 'Medium',
        'meeting.priority_low': 'Low',
        
        // Action buttons
        'meeting.btn_accept_with_notification': 'Accept (Set Reminder)',
        'meeting.cancel_meeting': 'Cancel Meeting',
        'meeting.join_meeting': 'Join Video Meeting',
        'meeting.join_in_progress': 'Join Video Meeting (In Progress)',
        'meeting.already_accepted': 'You have accepted this meeting',
        
        // Confirm/Error messages
        'meeting.confirm_accept': 'Would you like to accept this meeting?',
        'meeting.accept_error': 'Failed to accept meeting.',
        'meeting.decline_reason_prompt': 'Please enter reason for declining (optional):',
        'meeting.decline_error': 'Failed to decline meeting.',
        'meeting.cancel_reason_prompt': 'Please enter reason for cancellation (optional):',
        'meeting.cancelled': 'Meeting has been cancelled.',
        'meeting.cancel_error': 'Failed to cancel meeting.',
        'meeting.cancel_reason_required': 'Please enter a cancellation reason of at least 5 characters.',
        'meeting.cancel_reason_required_label': 'Cancellation Reason (Required)',
        'meeting.cancel_reason_placeholder_required': 'Please enter the cancellation reason.',
        'meeting.cancel_reason_min_hint': 'At least 5 characters are required.',
        
        // Time warnings
        'meeting.warning_time_passed': '⚠️ Meeting time has already passed.\nWould you like to propose a new time?',
        'meeting.warning_time_soon': '⚠️ Only {minutes} minutes until meeting starts.\nPreparation time may be insufficient. Would you like to propose a new time?',
        
        // Reschedule
        'meeting.reschedule_title': 'Reschedule Meeting',
        'meeting.new_proposed_time': 'New Proposed Time',
        'meeting.select_new_time': 'Please select a new time.',
        'meeting.change_reason': 'Reason for Change (optional)',
        'meeting.change_reason_placeholder': 'Please enter the reason for rescheduling.',
        'meeting.time_change_reason': 'Time change request',
        'meeting.send_reschedule': 'Send Reschedule Request',
        'meeting.reschedule_sent': 'Reschedule proposal sent.',
        'meeting.reschedule_error': 'Failed to send reschedule request.',
        'meeting.reschedule_target_missing': 'Cannot find the other participant for this meeting.',
        'meeting.counterpart': 'Counterpart',
        'meeting.reschedule_initial_title': '{title} - Reschedule Request',
        'meeting.original_meeting_time': 'Original meeting time',
        'meeting.reschedule_default_note': 'I would like to propose a new meeting time. Please review the suggested options and choose the time that works best for you.',
        'meeting.reschedule_reason_hint': 'Optional: add a reason or note for the schedule change.',
        'meeting.field_reschedule_message': 'Message to recipient',
        'meeting.placeholder_reschedule_notes': 'Optionally add a brief reason or note for the schedule change.',
        'meeting.reschedule_modal_title': 'Propose New Meeting Times',
        'meeting.btn_send_reschedule_proposal': 'Send Reschedule Proposal',
        'meeting.toast_reschedule_proposal_sent': 'Reschedule proposal sent.',
        'meeting.reschedule_proposal_message': 'Meeting reschedule request\n\nPlease select one of the proposed times.',
        'meeting.cancel_meeting': 'Cancel Meeting',
        'meeting.cancel_confirm_message': 'Are you sure you want to cancel this meeting? Participants will be notified.',
        'meeting.cancel_reason': 'Cancellation Reason (Optional)',
        'meeting.cancel_reason_placeholder': 'Enter cancellation reason...',
        'meeting.btn_confirm_cancel': 'Cancel Meeting',
        'meeting.decline_meeting': 'Decline Meeting',
        'meeting.decline_confirm_message': 'Do you want to decline this meeting invitation?',
        'meeting.decline_reason': 'Decline Reason (Optional)',
        'meeting.decline_reason_placeholder': 'Enter decline reason...',
        'meeting.reschedule_meeting': 'Reschedule Meeting',
        'meeting.reschedule_confirm_message': 'Do you want to reschedule this meeting?',
        'meeting.reschedule_reason': 'Reschedule Reason (Optional)',
        'meeting.reschedule_reason_placeholder': 'Enter reschedule reason...',
        'meeting.new_datetime': 'New Date & Time',
        'meeting.request': 'Meeting Request',
        'meeting.waiting_for_response': 'Waiting for the other party to respond',
        'meeting.propose_reschedule': 'Propose Reschedule',
        'meeting.next_actions': 'Next Actions',
        'meeting.manage_meeting': 'Manage Meeting',
        'meeting.action_hint_final': 'This meeting is already closed. You can review the details only.',
        'meeting.action_hint_pending': 'Respond to the invitation, or coordinate a new time in chat if needed.',
        'meeting.action_hint_waiting': 'Waiting for the other party. You can send another reschedule proposal if needed.',
        'meeting.action_hint_accepted': 'This meeting is confirmed. Use the buttons below to propose a change or cancel.',
        'meeting.action_hint_available': 'Available actions depend on the current meeting status.',
        'meeting.location_hint': 'Default location is set to the recipient\'s booth number',
        'meeting.error_past_datetime': 'Meeting time must be at least 5 minutes from now.',
        'meeting.error_self_meeting': 'You cannot request a meeting with yourself.',
        'meeting.booth_hint': 'Default: {location}',



        // 🔥 Survey data (Added missing keys)
        'survey.busan': 'Busan (Local)',
        'survey.seoul': 'Seoul/Gyeonggi',
        'survey.gyeongsang': 'Gyeongsang',
        'survey.chungcheong': 'Chungcheong',
        'survey.jeolla': 'Jeolla',
        'survey.gangwon': 'Gangwon',
        'survey.jeju': 'Jeju',
        'survey.overseas': 'Overseas',
        // 🔥 [Added] Transport mode keys
        'survey.walk': 'Walk',
        'survey.bicycle': 'Bicycle',
        'survey.subway': 'Subway',
        'survey.bus': 'Bus',
        'survey.car': 'Car',
        'survey.train': 'Train/KTX',
        'survey.walk_bicycle': 'Walk/Bike',
        'survey.public': 'Public Transit',
        'survey.car_internal': 'Car (Gas)',
        'survey.car_electric': 'EV/Hybrid',
        'survey.airplane': 'Airplane',
        

        'survey.transport_q': 'How do you plan to travel to the venue ({venueName})?',
        'survey.transport_desc': 'Carbon reduction is calculated based on your choice.',
        'survey.transport_public': 'Public Transit (Subway/Bus)',
        'survey.transport_train': 'Train / KTX',
        'survey.transport_airplane': 'Airplane',
        'survey.transport_car_internal': 'Car (Gasoline/Diesel)',
        'survey.transport_car_electric': 'Car (EV/Hydrogen/Hybrid)',
        'survey.transport_walk_bicycle': 'Walk / Bicycle',


        'noti.title': 'Notifications',
        'noti.saving': 'Saving...',
        'noti.push_title': 'Push Notifications',
        'noti.push_desc_on': 'Receive app notifications',
        'noti.push_desc_off': 'Blocked by browser',
        'noti.chat': 'Chat',
        'noti.chat_desc': 'New messages',
        'noti.meeting': 'Meetings',
        'noti.meeting_desc': 'Requests, schedules',
        'noti.matching': 'Matching',
        'noti.matching_desc': 'AI recommendations',
        'noti.event': 'Events',
        'noti.event_desc': 'Event updates',
        'noti.carbon': 'Carbon',
        'noti.carbon_desc': 'Points, goals',
        'noti.email_title': 'Email Notifications',
        'noti.email_desc': 'Receive via email',
        'noti.newsletter': 'Newsletter',
        'noti.newsletter_desc': 'Industry news',
        'noti.email_meeting': 'Meeting Summary',
        'noti.email_meeting_desc': 'Confirmations, reminders',
        'noti.email_matching': 'Matching Report',
        'noti.email_matching_desc': 'Weekly recommendations',
        'noti.email_event': 'Event News',
        'noti.email_event_desc': 'Event updates',
        'noti.sms_title': 'SMS Notifications',
        'noti.sms_desc': 'Receive text messages',
        'noti.sms_meeting': 'Meeting Reminder',
        'noti.sms_meeting_desc': '30 mins before',
        'noti.dnd_title': '🌙 Do Not Disturb',
        'noti.dnd_desc': 'Silence notifications during these hours',
        'noti.start_time': 'Start',
        'noti.end_time': 'End',
        'noti.current_setting': 'Current',
        'noti.help_title': 'Help',
        'noti.help_1': 'Push requires browser permission.',
        'noti.help_2': 'Emails sent to registered address.',
        'noti.help_3': 'Critical alerts bypass DND.',
        // 🔥 [Added] Notification settings page missing keys
        'noti.push_section': 'Push Notifications',
        'noti.push_desc_denied': 'Notifications are blocked in your browser',
        'noti.push_chat': 'Chat Messages',
        'noti.push_chat_desc': 'Notify when new messages arrive',
        'noti.push_meeting': 'Meeting Notifications',
        'noti.push_meeting_desc': 'Meeting requests, confirmations, and reminders',
        'noti.push_matching': 'Matching Notifications',
        'noti.push_matching_desc': 'AI recommendations and matching requests',
        'noti.push_event': 'Event Notifications',
        'noti.push_event_desc': 'Bookmarked events and new events',
        'noti.push_carbon': 'Carbon Activity Notifications',
        'noti.push_carbon_desc': 'Points earned and goals achieved',
        'noti.email_section': 'Email Notifications',
        'noti.email_newsletter': 'Newsletter',
        'noti.email_newsletter_desc': 'Drone industry news and event updates',
        'noti.sms_section': 'SMS Notifications',
        'noti.email_section': 'Email',
        'noti.email_marketing_title': 'Marketing email consent',
        'noti.email_marketing_desc': 'Receive event news and promotional emails',
        'noti.email_marketing_note': 'Essential service emails (meetings, matching) are sent regardless of this consent. You can withdraw anytime.',
        'noti.dnd_section': 'Do Not Disturb',
        'noti.dnd_start': 'Start Time',
        'noti.dnd_end': 'End Time',
        'noti.dnd_current': 'Current setting: No notifications from {start} to {end}.',
        'noti.help_4': 'Settings are saved automatically.',
        'noti.alert_not_supported': 'This browser does not support notifications.',
        'noti.alert_granted': 'Notification permission has been granted.',
        'noti.alert_denied': 'Notification permission was denied. Please change it in your browser settings.',
        'noti.sms_1hour_before': '1 Hour Before',
        'noti.sms_10min_before': '10 Minutes Before',
        'noti.sms_event': 'Event Reminder',
        'noti.sms_event_desc': 'Notifications for key exhibition schedules and events',
        'noti.help_sms_1': 'SMS notifications are sent to your registered phone number.',
        'noti.help_sms_2': 'Meeting reminders are sent before your scheduled consultation.',
        'noti.help_sms_3': 'Event reminders notify you of key exhibition schedules.',
        'sms_consent_popup.title': 'SMS Notification Consent',
        'sms_consent_popup.message': 'Would you like to receive important information such as meeting alerts and event reminders via SMS?',
        'sms_consent_popup.benefit_meeting': 'Meeting start reminders',
        'sms_consent_popup.benefit_event': 'Event reminders',
        'sms_consent_popup.profile_hint': 'You can turn notifications on or off anytime in your profile settings.',
        'sms_consent_popup.decline': 'Maybe Later',
        'sms_consent_popup.agree': 'Agree',


        
        'events.bookmarks': 'Bookmarked Companies/Events',
        'events.no_bookmarks': 'No bookmarked events yet.',
        'events.no_official': 'No official events available',
        'events.coming_soon': 'New events coming soon',
        'events.date_tbd': 'TBD',

        // 🔥 [Added] EventsPage keys
        'today': 'Today',
        'official_event': 'Official',
        'search_events': 'Search events...',
        'filters': 'Filters',
        'grid_view': 'Grid',
        'category': 'Category',
        'category_exhibition': 'Exhibition',
        'category_conference': 'Conference',
        'category_trade_show': 'Trade Show',
        'category_workshop': 'Workshop',
        'category_seminar': 'Seminar',
        'category_networking': 'Networking',
        'category_community': 'Community',
        'clear_filters': 'Clear Filters',
        'retry': 'Retry',
        'no_events_found': 'No events found.',
        
        // 🔥 [Added] EventDetailPage keys
        'event_not_found': 'Event not found.',
        'go_back': 'Go Back',
        'bookmark_removed': 'Bookmark removed.',
        'reminder_1h': 'starts in 1 hour.',
        'reminder_10m': 'starts in 10 minutes!',
        'bookmark_added_with_reminder': 'Event bookmarked. We will notify you before it starts!',
        'bookmark_failed': 'Could not complete the action.',
        'registration_success': 'Registration completed.',
        'registration_failed': 'Registration failed.',
        'link_copied': 'Link copied.',
        'cancelled': 'Cancelled',
        'date': 'Date',
        'location': 'Location',
        'participants': 'Participants',
        'waiting_list': 'Waiting List',
        'carbon_impact': 'Carbon Impact',
        'eco_friendly_event': 'Eco-friendly Event',
        'description': 'Description',
        'no_description': 'No description available.',
        'show_less': 'Show Less',
        'show_more': 'Show More',
        'opening_hours': 'Opening Hours',
        'pricing': 'Pricing',
        'regular_fee': 'Regular Fee',
        'early_bird': 'Early Bird',
        'organizer_info': 'Organizer Info',
        'host': 'Host',
        'organizer': 'Organizer',
        'contact_person': 'Contact Person',
        'contact': 'Contact',
        'official_website': 'Official Website',
        'tags': 'Tags',
        'gallery': 'Gallery',
        'history': 'History',
        'visitors': 'visitors',
        'registering': 'Registering...',
        'event_cancelled': 'Event Cancelled',
        'register': 'Register',
        'registered': 'Registered',
        'bookmarked': 'Bookmarked',
        'add_bookmark': 'Add Bookmark',
        'edition_number': '',
        'edition_suffix': 'th Edition',



        'calendar.year': '',
        'calendar.month': '',
        'calendar.sun': 'Sun',
        'calendar.mon': 'Mon',
        'calendar.tue': 'Tue',
        'calendar.wed': 'Wed',
        'calendar.thu': 'Thu',
        'calendar.fri': 'Fri',
        'calendar.sat': 'Sat',
        'error.networkError': 'Please check your network connection',
        'error.serverError': 'A server error occurred',
        'error.unknownError': 'An unknown error occurred',
        'error.authError': 'Authentication failed',
        'error.notFound': 'Page not found',
        'error.forbidden': 'Access denied',
        'error.timeout': 'Request timeout',
        'error.badRequest': 'Bad request: {message}',
        'error.unauthorized': 'Authentication required. Please sign in again.',
        'error.notFoundResource': 'The requested resource was not found.',
        'error.unprocessable': 'Please check your input: {message}',
        'error.tooManyRequests': 'Too many requests. Please try again shortly.',
        'error.serverErrorRetry': 'A server error occurred. Please try again shortly.',
        'error.unknownStatus': 'Server error ({status}): {message}',
        'error.save_failed': 'Failed to save.',
        'error.invalid_image_type': 'Only image files can be uploaded.',
        'error.file_too_large': 'File size must be 5MB or less.',
        'error.upload_failed': 'Upload failed.',
        'error.phone_format': 'Please enter a valid phone number.',
        'error.sms_send_failed': 'Failed to send SMS.',
        'error.code_length': 'Please enter a 6-digit verification code.',
        'error.invalid_code': 'Invalid verification code.',
        'error.phone_verification_required': 'Phone number verification is required.',
        'sms.code_sent': 'Verification code has been sent.',
        'sms.verified': 'Phone number verified successfully.',
        'sms.resend': 'Resend',
        'sms.request': 'Verify',
        'sms.code_placeholder': '6-digit code',
        'sms.phone_verified': 'Phone number verified successfully.',
        'common.verifying': 'Verifying...',
        'myinfo.phone_change_notice': 'SMS verification is required when changing phone number.',
        'myinfo.remove_business_card_title': 'Remove Business Card',


        'welcome.message': 'Welcome, {name}!',
        'welcome.greeting': 'Hello',
        'welcome.description': 'Welcome to DSK',
        
        // Home (🔥 Added missing keys)
        'home.greeting.morning': 'Good morning, {name}',
        'home.greeting.afternoon': 'Good afternoon, {name}',
        'home.greeting.evening': 'Good evening, {name}',
        'home.activity_summary': 'Activity Summary',
        'home.recent_activity': 'Recent Activity',
        'home.featured_companies': 'Featured Companies',
        'home.featured_events': 'Featured Events',
        'home.upcoming_schedule': 'Upcoming Schedule',
        'home.view_all': 'View All',
        'home.more': 'More',
        'home.loading': 'Loading data...',
        'home.no_activity': 'No activities recorded yet.',
        'home.no_activity_hint': 'Complete the survey or record an activity to see it here.',
        'home.record_activity': 'Record Activity',
        // 🔥🔥🔥 [2026-01-09 Added] Homepage recommendation keys
        'home.recommended_for_you': 'Recommended for You',
        'home.recommended_companies': 'Recommended Companies',


        'company.search': 'Search Companies',
        'company.filter': 'Filter Companies',
        'company.details': 'Company Details',
        'company.contact': 'Contact',
        'company.interests': 'Interests',
        'company.products': 'Products',
        'company.services': 'Services',
        'company.matchScore': 'Match Score',
        'company.requestMeeting': 'Request Meeting',
        'company.website': 'Website',
        'company.email': 'Email',
        'company.phone': 'Phone',
        'company.chat_btn': 'Chat Inquiry',
        'company.map_btn': 'View Map',
        'company.booth': 'Booth',
        'company.booth_tbd': 'TBD',
        'company.tab_overview': 'Overview',
        'company.tab_products': 'Products',
        'company.video_title': 'Promo Video',
        'company.video_view': 'Watch video',
        'company.tab_news': 'News',
        'company.info_employees': 'Employees',
        'company.info_founded': 'Founded',
        'company.info_location': 'Location',
        'company.info_industry': 'Industry',
        'company.service_title': 'Main Services',
        'company.empty_products': 'No products listed.',
        'company.empty_news': 'No news available.',
        'company.loading': 'Loading company information...',
        'company.error_not_found': 'Company not found',
        'company.online_status': 'Online',
        'company.no_description': 'No company description available.',
        'company.alert_login_required': 'Please log in first.',
        'company.alert_chat_error': 'Failed to create chat room.',

        'companies.title': 'Exhibitor List',
        'companies.search_placeholder': 'Search by name, industry, tag...',
        'companies.filter_title': 'Select Category',
        'companies.filter_all': 'All Categories',
        'companies.search_label': 'Search',
        'companies.sort_label': 'Sort',
        'companies.filter_active_count': '{count} categories',
        'companies.filters_active': '{count} filters active',
        'companies.clear_filters': 'Clear filters',
        'companies.sort_name_asc': 'Name (A-Z)',
        'companies.sort_name_desc': 'Name (Z-A)',
        'companies.sort_booth_asc': 'Booth No. (Low-High)',
        'companies.sort_booth_desc': 'Booth No. (High-Low)',
        'companies.result_count': '{count} Companies',
        'companies.empty_title': 'No companies found',
        'companies.empty_search': 'No search results',
        'companies.semantic_search': 'AI Semantic Search', // 🔥 [2026-06-10 B-2] 자연어(시맨틱) 검색 라벨
        'companies.reset': 'Reset',
        'companies.apply': 'Apply',
        'companies.end_of_list': 'No more companies to show',
        'matching.title': 'AI Matching',
        'matching.subtitle': 'Find your best business partners.',
        'matching.mode_ai': 'AI Recommend',
        'matching.mode_search': 'Search',
        'matching.search_placeholder': 'Search tech, product, company...',
        'matching.btn_search': 'Search',
        'matching.result_count': '{count} Recommendations',
        'matching.analyzed': 'Analyzed',
        'matching.empty_ai': 'No bookmarked companies yet.',
        'matching.empty_ai_desc': 'Update profile for better results.',
        'matching.empty_search': 'No results found.',
        'matching.empty_search_desc': 'Try different keywords.',
        'matching.section_ai': 'AI Integrated Recommendations',
        'matching.section_desc': 'Similar Business',
        'matching.section_prod': 'Similar Products',
        'matching.action_chat': 'Send Message',
        'matching.booth_prefix': 'Booth',
        'matching.logo_alt': 'Logo',
        'matching.no_description': 'No description available.',
        'matching.action_location': 'Location',
        'matching.top_rank': 'TOP {rank}',
        'matching.loading': 'AI Analyzing...',
        'matching.info_message': 'DSK AI Valentine analyzes your profile to suggest the best partners.',
        'matching.alert_login_required': 'Login required.',
        'matching.alert_load_error': 'Failed to load data.',
        'matching.alert_min_chars': 'Please enter at least 2 characters.',
        'matching.alert_search_error': 'Search failed.',
        'matching.alert_chat_error': 'Failed to create chat.',
        'matching.alert_booth_location': 'Booth Location: {booth}',
        'matching.buyer_title': 'AI Interest Search',
        'matching.buyer_subtitle': 'We found companies matching your interests.',
        'matching.buyer_mode_ai': 'Interest Match',
        'matching.buyer_empty_title': 'No matches found',
        'matching.buyer_empty_desc': 'Try adding more specific interests for better accuracy.',
        'matching.buyer_analyzed': 'Analysis Complete',
        'matching.buyer_info_message': "DSK AI Valentine analyzes your profile to suggest the best partners.",
        'matching.buyer_reason': 'High similarity to your interests.',
        'matching.section_interest': 'Companies matching your interests',
        'matching.section_recommended': 'Recommended Companies',
        'matching.company_reason': 'AI Recommendation',
        'matching.access_denied_title': 'Access Restricted',
        'matching.access_denied_message': 'This feature is available for business members only.',
        'matching.btn_business_upgrade': 'Apply for Business Visitor',
        'matching.alert_no_booth': 'Booth location is not assigned.',
        'matching.pending_approval_title': 'Pending Approval',
        'matching.pending_approval_message': 'Your request is awaiting approval from the secretariat.\nYou can use the matching feature once approved.',

        // ─── businessUpgrade ───
        'businessUpgrade.title': 'Business Member Application',
        'businessUpgrade.description': 'Please fill in all information below\nto use chat, matching, and meeting features.',
        'businessUpgrade.profile_label': 'Profile Photo',
        'businessUpgrade.profile_required': '*Required (your photo)',
        'businessUpgrade.profile_placeholder': 'Click to upload',
        'businessUpgrade.profile_error': 'Please upload your photo',
        'businessUpgrade.company_placeholder': 'Company / Organization *',
        'businessUpgrade.company_error': 'Please enter your company name',
        'businessUpgrade.department_placeholder': 'Department (optional)',
        'businessUpgrade.job_title_placeholder': 'Job Title *',
        'businessUpgrade.job_title_error': 'Please enter your job title',
        'businessUpgrade.card_label': 'Business Card',
        'businessUpgrade.card_required': '*Required',
        'businessUpgrade.card_error': 'Please upload your business card',
        'businessUpgrade.notice': 'Secretariat approval is required after submission. Once approved, you can use chat, matching, and meeting features.',
        'businessUpgrade.btn_cancel': 'Cancel',
        'businessUpgrade.btn_submit': 'Request Approval',
        'businessUpgrade.btn_uploading': 'Uploading...',
        'businessUpgrade.btn_processing': 'Processing...',
        'businessUpgrade.success_title': 'Application Submitted',
        'businessUpgrade.success_message': 'Your business member application has been submitted.\nYou can use chat, matching, and meeting features once approved by the secretariat.',
        'businessUpgrade.success_confirm': 'OK',
        'businessUpgrade.error_file_size': 'File size must be 5MB or less.',
        'businessUpgrade.error_file_type': 'Only image files are accepted.',
        'businessUpgrade.error_profile_required': 'Profile photo is required. Please upload your photo.',
        'businessUpgrade.error_card_required': 'Business card image is required.',
        'businessUpgrade.error_submit': 'An error occurred. Please try again.',
        'businessUpgrade.success_toast': 'Application submitted successfully!',

        'matching.hero_title': 'AI Matching',
        'matching.hero_subtitle': 'We recommend the best business partners based on your profile.',
        'matching.btn_ai_analysis': 'Interest-Based Analysis',
        'matching.btn_direct_search': 'Direct Search',
        'matching.result_count_suffix': ' recommended companies',
        'matching.before_title': 'Matching not started',
        'matching.before_desc': "This year's matching hasn't started yet. Recommended companies will appear once it begins.",
        'matching.calculating_title': 'AI Analyzing...',
        'matching.calculating_desc': 'Finding the best business partners for you.',
        'matching.calculating_progress': 'Analysis in progress...',
        'matching.calculating_almost': 'Almost done...',
        'matching.search_empty_title': 'No search results',
        'matching.search_empty_desc': 'Try searching with different keywords.',
        'matching.info_modal_title': 'AI Company Matching Guide',
        'matching.reason_buyer': 'AI Analysis: This company matches your interests.',
        'matching.reason_default': 'AI Analysis: This company matches your interests.',
        'matching.reason_high': 'Very strong match with your interests',
        'matching.reason_high_cat': "Very strong match with your interest in '{category}'",
        'matching.reason_mid': 'Related to your interests',
        'matching.reason_mid_cat': "'{category}' aligns with your interests",
        'matching.reason_low': 'A potential new business opportunity',
        'matching.reason_low_buyer': 'A potential supplier for your business',
        'matching.show_more': 'Show more',
        'matching.score_explain': 'The match score (%) combines category fit, interest alignment, and profile completeness — the higher, the better the fit.',
        'matching.no_company_name': 'No company name',
        'matching.no_category': 'No category',
        'matching.booth_tbd': 'TBD',
        'matching.btn_location': 'View Location',
        'matching.btn_meeting': 'Request Meeting',
        'matching.btn_message': 'Message',
        'matching.booth': 'Booth',
        'matching.section_title': 'Integrated Recommendations',
        'matching.no_interest_title': 'Please enter your interests',
        'matching.no_interest_desc': 'Please enter your interested technologies/products in the survey for AI matching.',
        'matching.no_interest_btn': 'Complete Survey',
        'matching.buyer_no_interest_title': 'Please enter your interests',
        'matching.buyer_no_interest_desc': 'Please enter your interested technologies/products in the survey for AI matching.',
        'matching.buyer_no_interest_btn': 'Complete Buyer Survey',
        'matching.alert_min_chars': 'Please enter at least 2 characters.',
        'matching.alert_search_error': 'An error occurred while searching.',
        'matching.search_result_count_suffix': ' search results',



        'meeting.schedule': 'Schedule',
        'meeting.upcoming': 'Upcoming Meetings',
        'meeting.past': 'Past Meetings',
        'meeting.cancel': 'Cancel',
        'meeting.reschedule': 'Reschedule',
        'meeting.confirm': 'Confirm',
        'meeting.location': 'Location',
        'meeting.time': 'Time',
        'meeting.type': 'Type',
        'meeting.participants': 'Participants',
        'meeting.notes': 'Notes',
        'meetings.title': 'My Schedule',
        'meetings.view_list': 'List',
        'meetings.view_calendar': 'Calendar',
        'meetings.empty': 'No upcoming meetings.',
        'meetings.go_book': 'Book a Meeting',
        'meetings.location_tbd': 'Location TBD',
        'meetings.upcoming': 'Upcoming Meetings',
        'meetings.no_schedule': 'No schedule for this day',
        'meetings.no_upcoming': 'No upcoming meetings',
        'meetings.schedule_new': 'Schedule a new meeting',
        'meeting.modal_title': 'Propose a Meeting',
        'meeting.target_company': 'Target Company',
        'meeting.field_title': 'Meeting Subject',
        'meeting.field_date': 'Date',
        'meeting.field_time': 'Time',
        'meeting.field_location': 'Location (Optional)',
        'meeting.default_title': 'Business Meeting',
        'meeting.booth_location': 'I would like to meet at your booth.',
        'meeting.placeholder_title': 'e.g., Product introduction and partnership discussion',
        'meeting.placeholder_location': 'e.g., Hall A, Booth 102',
        'meeting.notice_proposal': 'Your meeting proposal will be sent to the company. It will be added to your calendar once accepted.',
        'meeting.sending_proposal': 'Sending meeting proposal...',
        'meeting.btn_send_proposal': 'Send Meeting Proposal',
        'meeting.btn_go_to_chat': 'Go to Chat',
        'meeting.proposal_sent_title': 'Proposal Sent',
        'meeting.confirm_navigate_to_chat': 'Your meeting proposal has been sent successfully. Would you like to check the response in the chat room?',
        'meeting.toast_proposal_sent': 'Meeting proposal sent.',
        'meeting.error_title_required': 'Please enter a meeting subject.',
        'meeting.error_date_required': 'Please select a date.',
        'meeting.error_time_required': 'Please select a time.',
        'meeting.error_no_company': 'Company information not found.',
        'meeting.error_chat_creation': 'Failed to create chat room.',
        'meeting.error_invalid_datetime': 'Invalid date or time.',
        'meeting.error_proposal_failed': 'Failed to send meeting proposal.',
        'meeting.field_notes': 'Meeting Agenda & Message',
        'meeting.placeholder_notes': 'Enter detailed agenda or message to share with the other party.',
        // 🔥 [2026-04-22] MeetingProposalModal missing-key backfill
        'meeting.matrix_label': 'Choose candidate times',
        'meeting.loading_matrix': 'Loading available times…',
        'meeting.no_active_event_notice': 'A proposal requires an active event period.',
        'meeting.reload_matrix_button': 'Reload time grid',
        'meeting.max_candidates_notice': 'You can select up to {count} time slots.',
        'meeting.select_slots_required': 'Please select at least one time slot.',
        // 🔥 [2026-04-23] ChatRoom meeting proposal card missing keys
        'meeting.expires_at_label': 'Expires',
        'meeting.accept_slot_button': 'Accept this time',
        // 🔥 [2026-04-22] MeetingAvailabilityGrid missing-key backfill
        'meeting.matrix_status_available': 'Available',
        'meeting.matrix_status_me_busy': 'My schedule conflict',
        'meeting.matrix_status_other_busy': 'Their schedule conflict',
        'meeting.matrix_status_room_full': 'No room',
        'meeting.matrix_status_past': 'Past',
        'meeting.matrix_status_seminar': 'Seminar (unavailable)',
        'meeting.selected_slots_count': 'Selected',
        'meeting.recommended_slots_label': 'Recommended',
        'meeting.matrix_time': 'Time',
        'meeting.matrix_selected': 'Selected',
        'meeting.available_rooms_count': '{count} rooms',
        'meeting.room_selection_title': 'Meeting room per slot',
        'meeting.no_available_room_option': 'No available room',
        'meeting.room_capacity_persons': '{count} people',

        'chat.messages': 'Messages',
        'chat.send': 'Send',
        'chat.typing': 'Typing...',
        'chat.online': 'Online',
        'chat.offline': 'Offline',
        'chat.newMessage': 'New Message',
        'chat.readReceipt': 'Read',
        'chat.unread': 'Unread',
        'chat.read_by_other': 'Read',
        'chat.unread_by_other': 'Unread',
        'chat.title_default': 'Chat Room',
        'chat.input_placeholder': 'Type a message...',
        'chat.meeting_detected_title': 'Meeting Keyword Detected',
        'chat.confirm_meeting_proposal': 'Do you want to send a meeting proposal?',
        'chat.btn_send_card': 'Send Meeting Card',
        'chat.btn_send_text': 'Send as Text',
        'chat.meeting_proposal': 'Meeting Proposal',
        'chat.meeting_reschedule': 'Reschedule Proposal',
        'chat.btn_accept': 'Accept',
        'chat.btn_reschedule': 'Reschedule',
        'chat.btn_decline': 'Decline',
        'chat.status_accepted': 'Accepted',
        'chat.status_declined': 'Declined',
        'chat.status_pending': 'Pending',
        'chat.status_countered': 'Counter proposal sent',
        'chat.leave_room': 'Leave Room',
        'chat.confirm_leave': 'Are you sure you want to leave? Chat history will be deleted.',
        'chat.alert_leave_error': 'Failed to leave the chat room.',
        'chat.empty_messages': 'No Messages',
        'chat.empty_messages_desc': 'The partner may not accept or responses may be delayed. On-site visits are recommended; please use this as a supplementary tool.',
        'chat.meeting_proposal_title': '📅 Meeting Proposal',
        'chat.meeting_reschedule_title': '🕒 Reschedule Proposal',
        'chat.meeting_proposal_format': '{title}\nTime: {datetime}\nLocation: {location}',
        'chat.meeting_reschedule_format': 'Reschedule: {title}\nNew Time: {datetime}\nLocation: {location}',
        'chat.meeting_keywords': 'meeting,meet,schedule,appointment,reservation',
        // 🔥 [Added] Chat loading & meeting keyword detection modal
        'chat.loading': 'Loading chat...',
        'chat.ask_send_meeting_card': 'Meeting keyword detected. Would you like to send a meeting card?',
        'chat.btn_no_thanks': 'No thanks',
        'chat.toast_meeting_sent': 'Meeting proposal sent.',
        'chat.toast_meeting_accepted': 'Meeting accepted.',
        'chat.toast_meeting_declined': 'Meeting declined.',
        'chat.meeting_added_to_calendar': 'Meeting added to calendar.',
        'chat.modal_meeting_title': 'Propose a Meeting',
        'chat.modal_reschedule_title': 'Reschedule Meeting',
        'chat.modal_meeting_subject': 'Subject',
        'chat.modal_meeting_subject_placeholder': 'Ex: Partnership discussion',
        'chat.modal_date': 'Date',
        'chat.modal_time': 'Time',
        'chat.modal_location': 'Location',
        'chat.modal_location_placeholder': 'Ex: Hall A, Booth 102',
        'chat.modal_original_title': 'Original Meeting',
        'chat.modal_new_date': 'New Date',
        'chat.modal_new_time': 'New Time',
        'chat.modal_new_location': 'New Location',
        'chat.btn_send_proposal': 'Send Proposal',
        'chat.btn_send_reschedule': 'Send Reschedule',
        'chat.alert_meeting_required': 'Subject, Date, and Time are required.',
        'chat.alert_no_receiver': 'Cannot find the chat partner.',
        'chat.error_invalid_datetime': 'Invalid date or time.',
        'chat.location_tbd': 'Location TBD',
        'chat.alert_meeting_sent': 'Meeting proposal sent.',
        'chat.alert_meeting_send_error': 'Failed to send meeting proposal.',
        'chat.alert_reschedule_sent': 'Reschedule proposal sent.',
        'chat.other_party': 'Partner',
        'chat.meeting_accepted_message': 'Accepted the meeting proposal.',
        'chat.meeting_declined_message': 'Declined the meeting proposal.',
        'chat.alert_no_meeting_id': 'Meeting information not found.',
        'chat.alert_meeting_accepted': 'Meeting accepted.',
        'chat.alert_meeting_declined': 'Meeting declined.',
        'chat.alert_meeting_accept_error': 'Error accepting meeting: {message}',
        'chat.alert_meeting_decline_error': 'Error declining meeting: {message}',
        'chat.alert_no_reschedule_info': 'Original meeting info not found.',
        'chat.alert_reschedule_error': 'No meeting info to reschedule.',
        'chat.preview_image': '📷 Photo',
        'chat.preview_file': '📎 File',
        'chat.preview_system': '📢 System message',
        'chat.preview_meeting_reschedule_sent': '🕒 You sent a reschedule proposal.',
        'chat.preview_meeting_reschedule_received': '🕒 Reschedule proposal received.',
        'chat.preview_meeting_rescheduled_sent': '🕒 You accepted the reschedule.',
        'chat.preview_meeting_rescheduled_received': '🕒 Meeting was rescheduled.',
        'chat.preview_meeting_cancelled_sent': '🚫 You cancelled the meeting.',
        'chat.preview_meeting_cancelled_received': '🚫 Meeting was cancelled.',
        'chat.status_message_accepted': '{name} accepted the meeting.',
        'chat.status_message_declined': '{name} declined the meeting.',
        'chat.status_message_rescheduled': '{name} accepted the reschedule.',
        'chat.status_message_cancelled': '{name} cancelled the meeting.',
        'chat.status_message_time': 'Time',
        'chat.status_message_location': 'Location',
        'chat.status_message_reason': 'Reason',

        'notifications.title': 'Notifications',
        'notifications.mark_all_read': 'Mark all read',
        'notifications.mark_all_read_success': 'All notifications marked as read.',
        'notifications.empty': 'No new notifications.',
        'notifications.swipe_delete_hint': 'Swipe left to delete',
        'notifications.time_just_now': 'Just now',
        'notifications.time_minutes_ago': '{count}m ago',
        'notifications.time_hours_ago': '{count}h ago',
        'notifications.new_arrived': '🔔 New notification arrived!',
        'notifications.delete_failed': 'Failed to delete notification.',
        'notifications.load_failed': 'Failed to load notifications.',
        'notifications.default_title': 'Notification',
        'notifications.meeting_default_title': 'Meeting',
        'notifications.meeting_proposed.title': 'Meeting proposal',
        'notifications.meeting_proposed.message': '{name} proposed meeting times for {title}.',
        'notifications.meeting_accepted.title': 'Meeting accepted',
        'notifications.meeting_accepted.message': '{name} accepted {title}. {time}',
        'notifications.meeting_rejected.title': 'Meeting declined',
        'notifications.meeting_rejected.message': '{name} declined {title}. {reason}',
        'notifications.meeting_rescheduled.title': 'Reschedule accepted',
        'notifications.meeting_rescheduled.message': '{name} accepted the reschedule for {title}. {time}',
        'notifications.meeting_cancelled.title': 'Meeting cancelled',
        'notifications.meeting_cancelled.message': '{title} was cancelled. {reason}',
        'notifications.meeting_reminder_1day.title': 'Meeting tomorrow',
        'notifications.meeting_reminder_1day.message': '{title} is scheduled for tomorrow. {time}',
        'notifications.meeting_reminder_1hour.title': 'Meeting in 1 hour',
        'notifications.meeting_reminder_1hour.message': '{title} starts in 1 hour. {time}',
        'notifications.meeting_reminder_10min.title': 'Meeting in 10 minutes',
        'notifications.meeting_reminder_10min.message': '{title} starts in 10 minutes. {time}',
        'notifications.meeting_pending_reminder.title': 'Meeting response pending',
        'notifications.meeting_pending_reminder.message': 'You have not responded to {title} yet.',
        'notifications.meeting_expired_reminder.title': 'Meeting proposal expired',
        'notifications.meeting_expired_reminder.message': 'The proposal for {title} has expired.',
        'notifications.survey_reminder.title': 'Survey request',
        'notifications.survey_reminder.message': 'Please complete the survey for {title}.',
        'notifications.event_reminder.title': 'Event reminder',
        'notifications.event_reminder.message': '{title} starts soon. {time}',

        // 🔥🔥🔥 미팅 미리보기 - 보낸사람용
        'chat.preview_meeting_sent': '📅 You sent a meeting proposal.',
        'chat.preview_meeting_accepted_sent': '✅ You accepted the meeting.',
        'chat.preview_meeting_rejected_sent': '❌ You declined the meeting.',

        // 🔥🔥🔥 미팅 미리보기 - 받는사람용
        'chat.preview_meeting_received': '📅 Meeting proposal received.',
        'chat.preview_meeting_accepted_received': '✅ Meeting was accepted.',
        'chat.preview_meeting_rejected_received': '❌ Meeting was declined.',

        // 기존 키 (하위 호환성)
        'chat.preview_meeting': '📅 Meeting proposal received.',
        'chat.preview_meeting_accepted': '✅ Meeting accepted.',
        'chat.preview_meeting_rejected': '❌ Meeting declined.',
        'chat.preview_new_message': '📅 You have a new message.',
        'chat.pending_title': 'Pending Approval',
        'chat.pending_message': 'Your request is awaiting approval from the secretariat.\nYou can use the chat feature once approved.',
        'chat.access_denied_title': 'Members Only',
        'chat.guest_message': 'Chat feature is available for members only.',
        'chat.visitor_message': 'Chat, matching, and meeting request features are for business members only.\nWould you like to apply for business membership?',
        'chat.alert_load_error': 'Failed to load chat room.',
        'chat.alert_send_error': 'Failed to send message.',
        'chat.alert_reschedule_send_error': 'Failed to send reschedule proposal: {message}',
        'chat.view_profile': 'View Profile',
        'chat.unknown_user': 'Unknown User',
        'chat.profile_title': 'Profile',
        'chat.business_card': 'Business Card',
        'chat.click_to_enlarge': 'Click to enlarge',
        // 🔥🔥🔥 [2026-01-09 Added] File attachment keys
        'chat.max_files_exceeded': 'You can only select up to {count} files.',
        'chat.file_too_large': 'File size exceeds 10MB.',
        'chat.file_type_not_allowed': 'This file type is not supported.',
        'chat.upload_cancelled': 'Upload has been cancelled.',
        'chat.file_sent': 'File sent successfully.',
        'chat.files_sent': '{count} files sent successfully.',
        'chat.file_send_error': 'Failed to send file.',
        'chat.attach_file': 'Attach File',
        'chat.add_caption': 'Add caption (optional)',
        'chat.drop_files_here': 'Drop files here',
        'chat.max_files_info': 'Up to 5 files, max 10MB each',
        'chat.download': 'Download',
        'chat.preview_not_available': 'Preview not available.',

        // 🔥🔥🔥 [2026-01-09 Added] Voice message keys
        'chat.voice_permission_denied': 'Microphone permission is required.',
        'chat.voice_message': 'Voice Message',
        'chat.voice_sent': 'Voice message sent.',
        'chat.voice_send_error': 'Failed to send voice message.',

        // 🔥🔥🔥 [2026-01-09 Added] Video message keys
        'chat.video_permission_denied': 'Camera permission is required.',
        'chat.video_message': 'Video Message',
        'chat.video_sent': 'Video message sent.',
        'chat.video_send_error': 'Failed to send video message.',
        'chat.record_video': 'Record Video',
        'chat.retake': 'Retake',
        'chat.start_recording': 'Start Recording',


        'activities.start_conversation': 'Start a conversation',
        'activity.tab_chat': 'Chats',
        'activity.tab_match': 'Matches',
        'activity.empty_chat': 'No chats yet',
        'activity.empty_chat_sub': 'Explore companies and start chatting',
        'activity.explore_btn': 'Explore Companies',
        'events.explore_btn': 'Explore Events',
        'activities.tab_chats': 'Chats',
        'activities.tab_matching': 'Matching',
        'activities.loading_chats': 'Loading chats...',
        'activities.loading_matches': 'Analyzing matches...',
        'activities.empty_chats': 'No chats yet',
        'activities.empty_chats_desc': 'Explore companies and start a conversation.',
        'activities.empty_matches': 'No matches found yet',
        'activities.empty_matches_desc': 'Update your profile for better recommendations.',
        'activities.btn_explore': 'Explore Companies',
        'activities.btn_retry': 'Retry',
        'activities.btn_preferences': 'Update Preferences',
        'activities.btn_send_message': 'Send Message',
        'activities.no_name': 'No Name',
        'activities.no_messages': 'No messages yet.',
        'activities.no_company_name': 'Unknown Company',
        'activities.no_industry': 'Unknown Industry',
        'activities.booth_prefix': 'Booth',
        'activities.time_just_now': 'Just now',
        'activities.time_minutes_ago': '{minutes}m ago',
        'activities.time_hours_ago': '{hours}h ago',
        'activities.time_days_ago': '{days}d ago',
        'activities.error_load_matches': 'Failed to load matches.',
        'activities.delete_chat': 'Delete Chat',
        'activities.delete_chat_title': 'Leave Chat Room',
        'activities.delete_chat_message': 'Are you sure you want to leave "{name}"?\nChat history will be permanently deleted and cannot be recovered.',
        'activities.confirm_delete': 'Are you sure you want to leave this chat?\nChat history cannot be recovered.',
        'activities.delete_confirm': 'Leave',
        'activities.delete_failed': 'Failed to delete the chat room.',


        'chatbot.title': 'Valentine',
        'chatbot.subtitle': 'DSK',
        'chatbot.welcome': 'Hello! 👋\n\nI am the DSK Valentine.\nAsk me about companies or booth locations!',
        'chatbot.placeholder': 'Type a message...',
        'chatbot.suggestions_label': 'Recommended Questions',
        'chatbot.q1': 'Where is the restroom?',
        'chatbot.q2': 'Recommend drone companies',
        'chatbot.q3': 'Recommend companies based on my interests',
        'chatbot.clear': 'Clear',
        'chatbot.close': 'Close',
        'chatbot.relatedCompanies': 'Related Companies',
        'chatbot.fold': 'Fold',
        'chatbot.unfold': 'Unfold',
        'chatbot.detail': 'Details',
        'chatbot.booth': 'Booth',
        'chatbot.thinking': 'Thinking...',
        'chatbot.error': 'An error occurred.',
        'chatbot.confirmClear': 'Delete conversation?',
        'chatbot.resetMessage': 'Conversation reset.',
        'chatbot.responding': 'Responding...',
        'chatbot.nav_error': 'Sorry, an error occurred during navigation.',
        'chatbot.lang_change_ko': '한국어로 변경',
        'chatbot.lang_change_en': 'Change to English',
        'chatbot.lang_change_ja': '日本語に変更',
        'chatbot.lang_change_zh': '切换到中文',
        'chatbot.lang_accept_ko': '네, 한국어로 변경할게요',
        'chatbot.lang_accept_en': 'Yes, change to English',
        'chatbot.lang_accept_ja': 'はい、日本語に変更します',
        'chatbot.lang_accept_zh': '是的，切换到中文',
        'chatbot.lang_decline': 'No, keep current language',
        'chatbot.meeting_header': 'Business Meeting Request',
        'chatbot.meeting_reserve': 'Request Meeting',
        'chatbot.more_companies': '{count} more companies',
        'chatbot.survey_shortcut': 'Go to Survey',
        'chatbot.send_failed': 'Send failed',

        // 🔥 [Added] 3D Booth Map keys
        'map.exhibition_hall_title': '{venueFullName}',
        'map.exhibition_subtitle': '{eventName}',
        'map.passable': 'Passway',
        'map.search_result': 'Search Result',
        'map.selected': 'Selected',
        'map.no_info': 'No Info',
        'map.more_companies': '+{count} more companies',
        'map.rotate_left': 'Rotate Left',
        'map.rotate_right': 'Rotate Right',
        'map.reset_view': 'Reset View',
        'map.entrance': 'Entrance',
        'map.exit': 'Exit',
        'map.toilet': 'Restroom',
        'map.booths_showing': '{count} booths showing',
        'map.reset': 'Reset',
        'map.facility_desc': 'Facility for visitors.',
        'map.tap_to_highlight_all': 'Tap to highlight all locations',
        'map.places': 'locations',
        'map.no_company_info': 'No company information registered.',
        'map.search_placeholder': 'Search booth number or company...',
        'map.loading_map': 'Loading map data...',
        'map.loading_3d': 'Loading 3D view...',
        'map.load_failed': 'Failed to load map data',
        'map.preparing': 'Preparing booth map...',
        'map.coming_soon_title': '🗺️ Booth Map Coming Soon',
        'map.coming_soon_message': 'This feature will be available on February 2nd.',
        'map.alert_select_both': 'Please select both start and destination.',
        'map.alert_no_route': 'No valid route found at this time.',
        'map.alert_route_error': 'An error occurred while finding the route.',
        'map.alert_invalid_booth': 'Invalid booth number. Please check and try again.',
        'map.nav_title': '🧭 Navigation',
        'map.nav_reset': 'Reset',
        'map.nav_select_start': 'Select Start',
        'map.nav_select_end': 'Select Destination',
        'map.nav_routing': 'Route Active',
        'map.nav_hide': 'Hide',
        'map.nav_calculating': 'Calculating route...',
        'map.nav_start': 'Start Navigation',
        'map.nav_set_start': 'Start',
        'map.nav_set_end': 'Destination',
        'map.company_detail': 'Company Details',
        'map.company_count': '{count} companies',
        'map.company_detail_short': 'Details',
        'map.booth_count': '{count} booths',
        'map.company_extra': '+{count} more',
        'map.optimal_route_active': 'Optimal Route Active',
        'map.booth_count_label': '{count} booths',
        'map.approx_distance': '~{distance}m',
        'map.location_prompt_title': '📍 Where are you now?',
        'map.location_prompt_desc': 'Enter your nearest booth number\nto calculate the optimal route.',
        'map.location_prompt_placeholder': 'e.g. A01, D18',
        'map.congestion_title': 'Live Congestion',
        'map.congestion_low': 'Low',
        'map.congestion_medium': 'Moderate',
        'map.congestion_high': 'Crowded',


        // 🔥 [Added] Booth map favorite companies traverse
        'boothMap.favoriteRoute': 'Favorite Companies Route',
        'boothMap.favoriteTraverse': 'Visit Favorites',
        'boothMap.noFavoriteCompanies': 'No favorite companies.',
        'boothMap.routeCalculationFailed': 'Unable to calculate route.',
        'boothMap.routeCalculationError': 'An error occurred while calculating the route.',
      },

      ja: {
        'app.name': 'DSK',
        'app.subtitle': '{eventName}',
        'app.description': '{eventName} - カーボンニュートラル展示プラットフォーム',
        'userTypes.VISITOR': '一般観覧客',
        'userTypes.BUSINESS': 'ビジネス',
        'userTypes.COMPANY': '出展企業',
        'userTypes.BUYER': 'バイヤー',
        'userTypes.ADMIN': '管理者',
        'userTypes.BUSINESS_PENDING': '承認待ち',
        'userTypes.INVESTOR': '投資家',
        'userTypes.BUYER_PENDING': '承認待ち',


        'common.ok': 'OK',
        'common.cancel': 'キャンセル',
        'common.save': '保存',
        'common.delete': '削除',
        'common.edit': '編集',
        'common.search': '検索',
        'common.filter': 'フィルター',
        'common.sort': '並び替え',
        'common.loading': '読み込み中...',
        'common.error': 'エラーが発生しました',
        'common.success': '成功',
        'common.retry': '再試行',
        'common.load_failed': 'データを読み込めませんでした。ネットワーク状態をご確認ください。',
        'survey.complete_first': 'アンケート完了後にご利用いただけます。',
        'auth.verification_code_required': '6桁の認証コードを入力してください。',
        'auth.verification_code_invalid': '認証コードが一致しません。',
        'auth.sms_verify_first': 'まずSMS認証を完了してください。',
        'common.refresh': '更新',
        'common.back': '戻る',
        'common.next': '次へ',
        'common.previous': '前へ',
        'common.close': '閉じる',
        'common.share': '共有',
        'common.copy': 'コピー',
        'common.download': 'ダウンロード',
        'common.upload': 'アップロード',
        'common.send': '送信',
        'common.submit': '提出',
        'common.reset': 'リセット',
        'common.confirm': '確認',
        'common.logout': 'ログアウト',
        'common.guest': 'ゲスト',
        'common.saved': '保存されました。',
        'common.deleted': '削除されました。',
        'common.confirm_delete': '本当に削除しますか？',
        'common.uncategorized': '未分類',
        'common.count_unit': '件',
        'common.count_suffix': '回',
        'common.minutes': '分',
        'common.hours': '時間',
        'common.days': '日',
        'common.user': 'ユーザー',
        'common.no_email': 'メールなし',
        'common.no_name': '名前なし',
        'common.sending': '送信中...',
        'common.close': '閉じる',
        'common.uploading': 'アップロード中...',
        'common.saving': '保存中...',

        'auth.login': 'ログイン',
        'auth.login_required': 'ログインが必要です。',

        // 🔥 [追加] 企業詳細ページおよびカテゴリ関連
        'company.intro_title': '会社紹介',
        'category.all': '全て',
        'category.drone_platforms': 'ドローン機体・ハードウェア',
        'category.military_defense_uavs': '軍事・国防・セキュリティドローン',
        'category.industrial_uavs': '産業・商業用ドローン活用',
        'category.autonomous_flight_utm': '自律飛行・管制・UAM',
        'category.battery_power_charging': 'バッテリー・電源・充電ソリューション',
        'category.motors_propellers_components': 'モーター・プロペラ・部品',
        'category.ai_software_data': 'AI・ソフトウェア・データ',
        'category.sensors_navigation_cameras': 'カメラ・センサー・通信・航法技術',
        'category.ugv_usv_robotics': '無人移動体拡張分野',
        'category.space_aerospace': '宇宙・航空宇宙技術',
        'category.training_education_services': '教育・訓練・サービス',
        'category.sustainable_aviation': '持続可能な航空技術',
        'category.other_interest': 'その他',
        'category.group_basic': '基本',
        'category.group_airframe': '機体/プラットフォーム',
        'category.group_technology': '技術/システム',
        'category.group_power': '動力/部品',
        'category.group_unmanned': '無人移動体',
        'category.group_services': '教育/サービス',
        'category.group_other': 'その他',
        'auth.logout': 'ログアウト',
        'auth.register': '新規登録',
        'auth.spc_exhibitor_notice': '出展企業の方ですか？ビジネス来場者として登録した後、企業認証を経て企業会員にアップグレードされます。', // 🔥 [2026-06-10 B-3 D1] SPC exhibitor 유입 안내
        'welcome.return_to_guide': '展示ガイドに戻る', // 🔥 [2026-06-10 B-3 D4] SPC return_url CTA
        'auth.email': 'メール',
        'auth.login_id': 'メールまたはID（会社名）',
        'year_consent.title': '{year} 利用同意の確認',
        'year_consent.message': '既存のアカウントで {year} のイベントもそのままご利用いただけます。今年のご利用のため、以下の同意を改めてご確認ください。',
        'year_consent.all': 'すべてに同意',
        'year_consent.terms': '[必須] 利用規約',
        'year_consent.privacy': '[必須] 個人情報の収集・利用',
        'year_consent.third_party': '[必須] 個人情報の第三者提供（ビジネスマッチング）',
        'year_consent.third_party_info': 'ビジネスマッチング時に相手企業へ会社名・担当者名・連絡先・関心分野が提供されます。',
        'year_consent.sms': '[任意] SMS通知の受信',
        'year_consent.email': '[任意] メールでの広告情報の受信',
        'year_consent.night': '[任意] 夜間（21～08時）広告の受信',
        'year_consent.view': '表示',
        'year_consent.hint': '任意項目はプロフィール > 通知設定でいつでも変更できます。',
        'year_consent.required_missing': '必須項目に同意してください。',
        'year_consent.save_failed': '保存できませんでした。もう一度お試しください。',
        'year_consent.logout': 'ログアウト',
        'year_consent.submit': '同意して続ける',
        'year_consent.terms_title': '利用規約',
        'year_consent.privacy_title': 'プライバシーポリシー',
        'year_consent.confirm_agree': '確認して同意',
        'year_consent.close': '閉じる',
        'auth.password': 'パスワード',
        'auth.show_password': 'パスワードを表示',
        'auth.hide_password': 'パスワードを非表示',
        'auth.passwordConfirm': 'パスワード確認',
        'auth.name': '名前',
        'auth.phone': '電話番号',
        'auth.company': '会社名',
        'auth.forgotPassword': 'パスワードを忘れた',
        'auth.resetPassword': 'パスワードリセット',
        'auth.socialLogin': 'ソーシャルログイン',
        'auth.loginWithGoogle': 'Googleでログイン',
        'auth.loginWithKakao': 'Kakaoでログイン',
        'auth.loginWithNaver': 'Naverでログイン',
        'auth.loginWithApple': 'Appleでログイン',
        'auth.title_sub': '{eventName}',
        'auth.guest': 'ゲスト',
        'auth.guest_login': 'ゲスト',
        'auth.auto_login': '自動ログイン',
        'auth.forgot_pw': 'パスワード検索',
        'auth.no_account': 'アカウントがありませんか？',
        'auth.error_email': 'メールを入力してください',
        'auth.error_pw': 'パスワードを入力してください',
        'auth.fail_login': 'ログインに失敗しました。',
        'nav.home': 'ホーム',
        'nav.companies': '企業',
        'nav.meetings': 'ミーティング',
        'nav.chats': 'チャット',
        'nav.carbon': 'カーボンポイント',
        'nav.profile': 'プロフィール',
        'nav.settings': '設定',
        'nav.notifications': '通知',
        'nav.help': 'ヘルプ',
        'nav.events': 'イベント',
        'nav.activities': '活動',
        'nav.map': 'マップ',
        'nav.matching': 'マッチング',
        'profile.language_setting': '言語設定',
        'profile.language_desc': 'アプリ全体の言語を変更します',
        'profile.change_language': '言語変更',
        'profile.interests_saved': '関心分野が保存されました。',
        'profile.interests_saved_refreshing': '関心分野が保存されました。マッチングは間もなく更新されます。',
        'profile.interests_no_change': '変更された項目がありません。',
        'profile.interests_save_failed': '関心分野の保存に失敗しました。',
        'profile.my_info': 'マイ情報',
        'profile.profile_settings': 'プロフィールおよびアカウント設定',
        'profile.company_info': '所属会社情報管理',
        'profile.company_info_desc': '企業紹介および製品情報管理',
        'profile.carbon_points': 'カーボンポイント',
        'profile.carbon_desc': '移動手段別カーボン削減量計算',
        'profile.bookmarks': '関心イベント',
        'profile.bookmarks_desc': '関心企業およびイベント統合管理',
        'profile.notifications': '通知',
        'profile.notifications_desc': 'プッシュおよびメール通知管理',
        'profile.settings': '設定',
        'profile.settings_desc': '言語、バージョン情報',
        'profile.activity_details': '活動詳細',
        'profile.total_matches': 'マッチング回数',
        'profile.completed_meetings': '完了したミーティング',
        'profile.completed_events': '参加イベント',
        // 🔥 [追加] プロフィール動的テキスト
        'profile.carbon_total_saved': '🌱 合計 {amount}kg 削減',
        'profile.tree_effect': '🌲 木 {count}本分の効果',
        'profile.activity_hint': '🌱 活動を記録しましょう！',
        'profile.badges': 'バッジ',
        'profile.no_badges': 'ブースを訪問するとバッジを獲得できます。',
        'profile.badge.explorer': '探検家',
        'profile.badge.curator': 'キュレーター',
        'profile.badge.master': 'マスター',
        'profile.badge_threshold': '{count}ブース',
        'profile.badge_tooltip': '{name}（ブース{count}件訪問）',
        'settings.title': 'アプリ設定',
        'settings.language': '言語',
        'settings.language_label': 'アプリ言語',
        'settings.language_desc': '* 一部のコンテンツは元の言語で表示されることがあります。',
        'settings.screen': '画面',
        'settings.theme': 'テーマ',
        'settings.theme_light': 'ライトモード',
        'settings.theme_dark': 'ダークモード',
        'settings.theme_system': 'システム設定',
        'settings.font_size': 'フォントサイズ',
        'settings.font_small': '小',
        'settings.font_medium': '中',
        'settings.font_large': '大',
        'settings.media': 'メディア',
        'settings.autoplay': '動画自動再生',
        'settings.autoplay_desc': 'Wi-Fi接続時に動画を自動再生',
        'settings.data_saver': 'データ節約モード',
        'settings.data_saver_desc': '画質を下げてデータ使用量を削減',
        'settings.data_mgmt': 'データ管理',
        'settings.clear_cache': 'キャッシュ削除',
        'settings.clear_cache_desc': '一時データを削除',
        'settings.clear_data': '全データ削除',
        'settings.clear_data_desc': 'ログアウトして全データ削除',
        'settings.app_info': 'アプリ情報',
        'settings.version': 'バージョン',
        'settings.build': 'ビルド番号',
        'settings.developer': '開発者',
        'settings.terms': '利用規約',
        'settings.privacy': 'プライバシーポリシー',
        'settings.licenses': 'オープンソースライセンス',
        'settings.alert_cache_cleared': 'キャッシュが削除されました。',
        'settings.modal_delete_title': '⚠️ データ削除',
        'settings.modal_delete_confirm': '本当に全データを削除しますか？\nこの操作は元に戻せません。',
        'settings.font_size_desc': 'アプリ全体のフォントサイズを変更します。',
        'settings.data_saver_on': '✓ 有効 - サムネイル画像を使用中',
        'settings.data_saver_off': '無効 - 高画質画像を使用中',


        'myinfo.title': 'マイ情報編集',
        'myinfo.profile_image': 'プロフィール写真',
        'myinfo.profile_alt': 'プロフィール画像',
        'myinfo.change_photo': '写真変更',
        'myinfo.photo_change_coming_soon': '写真変更機能は準備中です。',
        // 🔥 [追加] カーボン貢献度カード関連キー
        'myinfo.carbon_impact': '私のカーボン貢献度',
        'myinfo.tree_equivalent': '木換算',
        'myinfo.trees': '本',
        'myinfo.view_carbon_details': 'カーボン削減詳細を見る',
        'myinfo.basic_info': '基本情報',
        'myinfo.name': '名前',
        'myinfo.name_placeholder': '名前を入力',
        'myinfo.email': 'メール',
        'myinfo.email_desc': 'メールは変更できません',
        'myinfo.email_unchangeable': 'メールは変更できません。',
        'myinfo.phone': '電話番号',
        'myinfo.phone_placeholder': '電話番号を入力',
        'myinfo.work_info': '職場情報',
        'myinfo.department': '部署',
        'myinfo.department_placeholder': '部署を入力',
        'myinfo.position': '役職',
        'myinfo.position_select': '役職を選択してください',
        'myinfo.position.staff': '社員',
        'myinfo.position.senior': '主任',
        'myinfo.position.assistant_manager': '代理',
        'myinfo.position.manager': '課長',
        'myinfo.position.deputy_manager': '次長',
        'myinfo.position.general_manager': '部長',
        'myinfo.position.director': '取締役',
        'myinfo.position.executive': '役員',
        'myinfo.position.ceo': '代表',
        'myinfo.position.other': 'その他',
        'myinfo.bio': '自己紹介',
        'myinfo.bio_title': '一行紹介',
        'myinfo.bio_label': '一行紹介',
        'myinfo.bio_placeholder': '簡単な自己紹介を作成してください (B2Bマッチング時に表示)',
        'myinfo.categories_count': '選択',
        'myinfo.error_max_categories': '関心分野は最大5つまで選択できます。',
        'myinfo.interests_title': '関心分野',
        'myinfo.interests_desc': '関心のあるカテゴリを選択し、具体的に探している製品や技術を自由に入力してください。',
        'myinfo.interest_text_label': '関心のある製品/技術 (自由記入)',
        'myinfo.interest_text_placeholder': '関心のある製品や技術を自由に入力してください。',
        'myinfo.save_interests': '関心分野を保存',
        'myinfo.alert_name_required': '名前を入力してください',
        'myinfo.error_name_required': '名前は必須入力項目です。',
        'myinfo.alert_phone_invalid': '正しい電話番号形式ではありません',
        'myinfo.error_phone_invalid': '有効な電話番号形式ではありません。',
        'myinfo.photo_updated': 'プロフィール写真が変更されました。',
        'myinfo.business_card': '名刺',
        'myinfo.business_card_desc': '名刺画像を登録すると、ビジネスミーティング時に相手に表示されます。',
        'myinfo.business_card_alt': '名刺画像',
        'myinfo.business_card_uploaded': '名刺画像がアップロードされました。保存ボタンを押して反映してください。',
        'myinfo.change_business_card': '名刺変更',
        'myinfo.remove_business_card': '名刺削除',
        'myinfo.confirm_remove_business_card': '名刺画像を削除しますか？',
        'myinfo.upload_business_card': '名刺画像アップロード',
        'myinfo.upload_business_card_hint': 'クリックして画像を選択 (最大5MB)',
        'myinfo.business_card_notice': '名刺画像の変更後、下部の「保存」ボタンを押して反映してください。',
        // 🔥 [追加] セキュリティ/パスワード関連キー
        'myinfo.security': 'セキュリティ',
        'myinfo.password_desc': 'アカウントのセキュリティのため、定期的にパスワードを変更することをお勧めします。',
        'myinfo.change_password': 'パスワード変更',
        'myinfo.current_password': '現在のパスワード',
        'myinfo.new_password': '新しいパスワード',
        'myinfo.confirm_password': '新しいパスワードの確認',
        'myinfo.password_changed': 'パスワードが正常に変更されました。',
        'myinfo.password_hint': 'パスワードは8文字以上で設定してください。',
        'myinfo.change_password_btn': '変更する',
        // アカウント初期設定 (ChangePasswordPage)
        'setup.title': 'アカウント初期設定',
        'setup.subtitle': 'セキュリティのため仮パスワードを変更してください。',
        'setup.subtitle_survey': '設定完了後、{survey}に移動します。',
        'setup.survey': 'アンケート',
        'setup.current_password': '現在の（仮）パスワード *',
        'setup.change_info': '変更する情報',
        'setup.new_email': '使用するメールアドレス（IDとして使用） *',
        'setup.contact_name': '担当者名 *',
        'setup.phone_label': '担当者電話番号',
        'setup.phone_required': '* 認証必須',
        'setup.phone_placeholder': '電話番号を入力',
        'setup.select_country': '国を選択',
        'setup.no_results': '検索結果なし',
        'setup.send_code': '認証要求',
        'setup.resend_code': '再送信',
        'setup.verified': '認証完了',
        'setup.new_password': '新しいパスワード（8文字以上） *',
        'setup.confirm_password': '新しいパスワードの確認 *',
        'setup.sms_consent': 'SMS受信に同意します（任意）',
        'setup.submit': '設定完了',
        'setup.complete_alert': 'アカウント初期設定が完了しました。\n入力したメールアドレスで再度ログインしてください。',
        'setup.email_invalid': '正しいメール形式ではありません。',
        'setup.name_required': '名前を入力してください。',
        'setup.phone_invalid_kr': '正しい携帯電話番号を入力してください。（例：010-1234-5678）',
        'setup.overseas_notice': '海外番号は国番号を除き、先頭の0を省略して入力してください。',
        'setup.sms_description': 'ミーティングリマインダー、イベント通知などをSMSで受け取れます。',
        'setup.sms_optional_note': '同意しなくても、アカウント設定およびサービスのご利用に制限はありません。',
        'setup.notice': '設定完了後、入力したメールアドレス(ID)で再度ログインする必要があります。',
        'setup.notice_label': 'ご案内',
        'setup.processing': '処理中...',
        'setup.phone_verified': '電話番号の認証が完了しました。',
        'setup.enter_code': 'SMSで送信された6桁の認証コードを入力してください。',
        'setup.code_placeholder': '認証コード6桁',
        'setup.confirm': '確認',
        'myinfo.error_current_password_required': '現在のパスワードを入力してください。',
        'myinfo.error_new_password_required': '新しいパスワードを入力してください。',
        'myinfo.error_password_min_length': 'パスワードは8文字以上である必要があります。',
        'myinfo.error_confirm_password_required': 'パスワードの確認を入力してください。',
        'myinfo.error_password_mismatch': 'パスワードが一致しません。',
        'myinfo.error_current_password_wrong': '現在のパスワードが正しくありません。',
        // 🔥 [追加] パスワードリアルタイム検証メッセージ
        'myinfo.password_valid': '使用可能なパスワードです。',
        'myinfo.password_match': 'パスワードが一致しています。',
        'myinfo.current_password_valid': 'パスワードが確認されました。',

        // 🔥 [追加] パスワード変更関連追加キー
        'error.password_change_failed': 'パスワードの変更に失敗しました。',
        'myinfo.current_password_placeholder': '現在のパスワードを入力',
        'myinfo.new_password_placeholder': '8文字以上入力',
        'myinfo.confirm_password_placeholder': '新しいパスワードを再入力',
        'myinfo.intl_phone_hint': '国コードを選択して電話番号を入力してください。',
        'myinfo.phone_placeholder_intl': '電話番号（国コード除く）',

        'common.processing': '処理中...',


        

        'company_info.title': '会社情報',
        'company_info.no_company': '登録された会社がありません',
        'company_info.no_company_desc': '会社を登録するとB2Bマッチングサービスを利用できます。',
        'company_info.register_btn': '会社登録',
        'company_info.basic': '基本情報',
        'company_info.name': '会社名',
        'company_info.industry': '業種分類',
        'company_info.desc_label': '会社紹介',
        'company_info.desc_placeholder': '会社の簡単な紹介を作成してください',
        'company_info.details': '詳細情報',
        'company_info.founded': '設立年度',
        'company_info.employees': '従業員数',
        'company_info.website': 'ウェブサイト',
        'company_info.address': '住所',
        'company_info.contact': '連絡先',
        'company_info.email': '代表メール',
        'company_info.phone': '代表電話番号',
        'company_info.visit': '訪問する',
        'company_info.main_products_label': '主要製品/サービス',
        'company_info.main_products_placeholder': '製品名をカンマ(,)で区切って入力してください',
        'company_info.main_products_hint': '例: 産業用ドローン、撮影機器、ソフトウェアソリューション',
        'company_info.media_section': 'バナー・紹介動画',
        'company_info.banner_label': 'バナー画像',
        'company_info.banner_hint': '企業詳細ページの上部に表示されます。横長の画像(推奨1200×400以上)をご使用ください。',
        'company_info.banner_upload': 'バナーをアップロード',
        'company_info.banner_uploading': 'アップロード中…',
        'company_info.banner_remove': 'バナーを削除',
        'company_info.banner_upload_failed': 'バナーのアップロードに失敗しました',
        'company_info.video_label': '紹介動画URL',
        'company_info.video_hint': 'YouTubeなどの動画リンクを入力してください。企業詳細ページに表示されます。',
        'company_info.video_view': '動画を見る',
        'company_info.edit_submitted': '修正リクエストが管理者レビューのために送信されました。',
        'company_info.pending_edit': '保留中の修正リクエストがあります。管理者承認後に反映されます。',
        'company_info.no_changes': '変更はありません。',
        'company_info.edit_restricted_notice': '企業紹介・主要製品・バナー・紹介動画を編集できます。その他は管理者にお問い合わせください。',
        'company_info.editable_section': '編集可能な項目',
        'company_info.submit_edit': '修正リクエスト',
        'company_info.description': '企業紹介',
        'company_info.description_placeholder': '企業の詳細な紹介を記載してください',
        'company_info.established_year': '設立年',
        'company_info.employee_count': '従業員数',
        'company_info.year_suffix': '年',
        'company_info.industry_select': '業種選択',
        'company_info.industry.drone_manufacturing': 'ドローン製造',
        'company_info.industry.drone_service': 'ドローンサービス',
        'company_info.industry.software': 'ソフトウェア',
        'company_info.industry.hardware': 'ハードウェア',
        'company_info.industry.logistics': '物流',
        'company_info.industry.agriculture': '農業',
        'company_info.industry.construction': '建設',
        'company_info.industry.media': 'メディア',
        'company_info.industry.security': 'セキュリティ',
        'company_info.industry.education': '教育',
        'company_info.industry.research': '研究',
        'company_info.industry.government': '公共機関',
        'company_info.industry.other': 'その他',
        'company_info.industry.uncategorized': '未分類',
        'common.submitting': '送信中...',

        // Carbon (🔥 Added missing keys)
        'carbon.title': 'カーボン活動計算機',
        'carbon.activity': 'カーボン削減活動',
        'carbon.activities': 'カーボン活動',
        'carbon.activities_desc': '移動経路および削減量確認',
        'carbon.history': '活動履歴',
        'carbon.history_desc': '過去の削減記録',
        'carbon.status_title': '🌱 私のカーボン削減現況',
        'carbon.saved': '削減量',
        'carbon.trees': '木換算',
        'carbon.trees_empty': '木を植える',
        'carbon.environmental_impact': '環境貢献',
        'carbon.loading': '計算中...',
        'carbon.offset_needed': 'カーボンオフセットが必要です！✈️',
        'carbon.great_job': '素晴らしい！🌿',
        'carbon.reduction_label': '総CO₂削減量',
        'carbon.input_title': '📍 移動情報入力',
        'carbon.origin_label': 'どこから来ましたか？',
        'carbon.origin_custom': '距離直接入力 (km)',
        'carbon.transport_label': '何に乗って来ましたか？',
        'carbon.passengers_label': '搭乗人数 (カープール時削減量増加)',
        'carbon.calc_btn': '計算する',
        'carbon.result_title': '🌱 カーボン削減結果',
        'carbon.distance': '移動距離',
        'carbon.transport': '交通手段',
        'carbon.baseline': '乗用車基準排出量',
        'carbon.actual': '実際の排出量',
        'carbon.reduction': '乗用車比削減量',
        'carbon.tree_equiv': '🌳 木{count}本が1年間吸収する量',
        'carbon.reward_text': '今記録すると',
        'carbon.reward_suffix': 'を獲得！',
        'carbon.submit_btn': '🎉 ポイント獲得',
        'carbon.submit_loading': '獲得中...',
        'carbon.history_title': '活動履歴',
        'carbon.history_empty': 'まだ記録された活動がありません',
        'carbon.fold': '折りたたむ',
        'carbon.unfold': '展開する',
        'carbon.guide_title': 'カーボン削減ヒント',
        'carbon.guide_1': '公共交通機関利用時、乗用車比最大80%カーボン節減',
        'carbon.guide_2': 'EV/ハイブリッドは一般車両比50%以上節減',
        'carbon.guide_3': '徒歩/自転車はカーボン排出ゼロ！',
        'carbon.guide_4': '航空移動時は他のエコ活動でオフセットしてください',
        'carbon.transport.car': '乗用車',
        'carbon.transport.bus': '高速バス',
        'carbon.transport.train': 'KTX/列車',
        'carbon.transport.plane': '航空',
        'carbon.transport.bicycle': '自転車',
        'carbon.transport.walk': '徒歩',
        'carbon.points': 'ポイント',
        'carbon.earned': '獲得',
        'carbon.total': '合計',
        'carbon.redeem': '使用',
        'carbon.leaderboard': 'ランキング',
        'carbon.reduce': '削減量',
        'carbon.trees_unit': '本',
        'carbon.hours_unit': '時間',
        'carbon.cups_unit': '杯',
        'carbon.offset_guide': '航空移動はカーボン排出が多いです。他の活動でオフセットしてください！',
        'carbon.no_survey': '下で出発地域と交通手段を選択し、計算ボタンを押してください。',
        // 🔥 [追加] サイドバー カーボン削減量表示
        'carbon.my_reduction': '私のカーボン削減量',        
        'carbon.my_journey': '私の{venueName}旅行',
        'carbon.survey_loaded': 'アンケート回答が自動的に読み込まれました。',
        // 🔥 [追加] 地域選択関連キー
        'carbon.province_city': '道/市',
        'carbon.select_region': '地域を選択してください',
        'carbon.district': '市/郡/区',
        'carbon.optional': '任意',
        'carbon.select_all': '全体 / 選択しない',
        'carbon.departure_region': '出発地域',
        'carbon.transport_mode': '移動手段',
        'carbon.recalculate': '削減量計算',
        'carbon.saving': '保存中...',
        'carbon.save_result': '削減量を保存',
        'carbon.save_success': '保存されました！',
        'carbon.save_failed': '保存に失敗しました。',
        'carbon.total_stats': '累積現況',
        'carbon.total_saved': '総削減量',
        'carbon.trees_equivalent': '木換算',
        'carbon.calc_note_1': 'この計算はガソリン乗用車(150g CO₂/km)を基準に比較します。',
        'carbon.calc_note_2': '目的地',
        'carbon.exhibition_center': '第1展示場',
        'carbon.calc_note_3': '距離はHaversine公式で計算され、道路曲率補正(×1.35)が適用されます。',
        'carbon.excess': '超過排出量',
        'carbon.my_emission_excess': 'カーボン超過排出',



// ==================== 🔥🔥🔥 [追加] ミーティング詳細ページ ====================
        'meeting.detail_title': 'ミーティング詳細',
        'meeting.phone_required_title': '電話番号の登録が必要です',
        'meeting.phone_required': 'ミーティングリクエストには、相手の承諾・変更通知をSMSで受け取るために電話番号の登録が必要です。プロフィールで登録後、もう一度お試しください。',
        'meeting.btn_go_register_phone': 'プロフィールで登録',
        'meeting.loading': 'ミーティング情報を読み込み中...',
        'meeting.load_error': 'ミーティングを読み込めません。',
        'meeting.calendar_notice': 'このミーティングはアプリ内カレンダーに保存されます',
        'meeting.time_location': '日時・場所',
        'meeting.location_tbd': '場所未定',
        'meeting.description': '説明',
        'meeting.agenda': '議題',
        'meeting.agenda_item': '議題項目',
        'meeting.participants': '参加者',
        'meeting.organizer': '主催者',
        'meeting.notes': 'メモ',
        
        // ミーティングステータス
        'meeting.status_confirmed': '確定',
        'meeting.status_scheduled': '予定済み',
        'meeting.status_in_progress': '進行中',
        'meeting.status_pending': '保留中',
        'meeting.status_proposed': '提案済み',
        'meeting.status_requested': 'リクエスト済み',
        'meeting.status_cancelled': 'キャンセル済み',
        'meeting.status_declined': '拒否済み',
        'meeting.status_completed': '完了',
        'meeting.status_accepted': '承諾済み',
        
        // 優先度
        'meeting.priority_high': '高',
        'meeting.priority_urgent': '緊急',
        'meeting.priority_medium': '中',
        'meeting.priority_low': '低',
        
        // アクションボタン
        'meeting.btn_accept_with_notification': '承諾（通知設定）',
        'meeting.cancel_meeting': 'ミーティングをキャンセル',
        'meeting.join_meeting': 'ビデオ会議に参加',
        'meeting.join_in_progress': 'ビデオ会議に参加（進行中）',
        'meeting.already_accepted': 'このミーティングを承諾しました',
        
        // 確認/エラーメッセージ
        'meeting.confirm_accept': 'このミーティングを承諾しますか？',
        'meeting.accept_error': 'ミーティングの承諾に失敗しました。',
        'meeting.decline_reason_prompt': '拒否理由を入力してください（任意）:',
        'meeting.decline_error': 'ミーティングの拒否に失敗しました。',
        'meeting.cancel_reason_prompt': 'キャンセル理由を入力してください（任意）:',
        'meeting.cancelled': 'ミーティングをキャンセルしました。',
        'meeting.cancel_error': 'ミーティングのキャンセルに失敗しました。',
        'meeting.cancel_reason_required': 'キャンセル理由を5文字以上で入力してください。',
        'meeting.cancel_reason_required_label': 'キャンセル理由（必須）',
        'meeting.cancel_reason_placeholder_required': 'キャンセル理由を入力してください。',
        'meeting.cancel_reason_min_hint': '5文字以上で入力してください。',
        
        // 時間警告
        'meeting.warning_time_passed': '⚠️ ミーティング時間が過ぎています。\n新しい時間を提案しますか？',
        'meeting.warning_time_soon': '⚠️ ミーティングまで{minutes}分です。\n準備時間が不足する可能性があります。新しい時間を提案しますか？',
        
        // リスケジュール
        'meeting.reschedule_title': '日程変更の提案',
        'meeting.new_proposed_time': '新しい提案時間',
        'meeting.select_new_time': '新しい時間を選択してください。',
        'meeting.change_reason': '変更理由（任意）',
        'meeting.change_reason_placeholder': '日程変更が必要な理由を入力してください。',
        'meeting.time_change_reason': '時間変更リクエスト',
        'meeting.send_reschedule': '変更提案を送信',
        'meeting.reschedule_sent': '日程変更提案を送信しました。',
        'meeting.reschedule_error': '変更提案の送信に失敗しました。',
        'meeting.reschedule_target_missing': 'ミーティング相手の情報が見つかりません。',
        'meeting.counterpart': '相手',
        'meeting.reschedule_initial_title': '{title} - 日程変更リクエスト',
        'meeting.original_meeting_time': '元のミーティング時間',
        'meeting.reschedule_default_note': '新しいミーティング時間を提案します。候補時間をご確認のうえ、ご都合のよい時間をお選びください。',
        'meeting.reschedule_reason_hint': '日程変更の理由や補足事項を入力してください。',
        'meeting.field_reschedule_message': '相手に送るメッセージ',
        'meeting.placeholder_reschedule_notes': '日程変更の理由や補足事項を任意で入力してください。',
        'meeting.reschedule_modal_title': '新しいミーティング時間を提案',
        'meeting.btn_send_reschedule_proposal': '日程変更提案を送信',
        'meeting.toast_reschedule_proposal_sent': '日程変更提案を送信しました。',
        'meeting.reschedule_proposal_message': 'ミーティング日程変更リクエスト\n\n提案された時間から1つ選択してください。',
        'meeting.cancel_meeting': 'ミーティングをキャンセル',
        'meeting.cancel_confirm_message': '本当にこのミーティングをキャンセルしますか？参加者に通知されます。',
        'meeting.cancel_reason': 'キャンセル理由（任意）',
        'meeting.cancel_reason_placeholder': 'キャンセル理由を入力してください...',
        'meeting.btn_confirm_cancel': 'キャンセルする',
        'meeting.decline_meeting': 'ミーティングを辞退',
        'meeting.decline_confirm_message': 'このミーティングの招待を辞退しますか？',
        'meeting.decline_reason': '辞退理由（任意）',
        'meeting.decline_reason_placeholder': '辞退理由を入力してください...',
        'meeting.reschedule_meeting': '日程変更',
        'meeting.reschedule_confirm_message': 'ミーティングの日程を変更しますか？',
        'meeting.reschedule_reason': '変更理由（任意）',
        'meeting.reschedule_reason_placeholder': '日程変更の理由を入力してください...',
        'meeting.new_datetime': '新しい日時',
        'meeting.request': 'ミーティング依頼',
        'meeting.waiting_for_response': '相手の返答を待っています',
        'meeting.propose_reschedule': '日程変更を提案',
        'meeting.next_actions': '次の操作',
        'meeting.manage_meeting': 'ミーティング管理',
        'meeting.action_hint_final': 'このミーティングは終了しています。詳細のみ確認できます。',
        'meeting.action_hint_pending': '招待に回答するか、必要に応じてチャットで新しい時間を調整してください。',
        'meeting.action_hint_waiting': '相手の回答を待っています。必要に応じて日程変更提案を再送できます。',
        'meeting.action_hint_accepted': '確定済みのミーティングです。変更提案またはキャンセルが必要な場合は下のボタンを使用してください。',
        'meeting.action_hint_available': '利用可能な操作はミーティングの状態によって異なります。',
        'meeting.location_hint': '相手のブース番号がデフォルトで設定されます',
        'meeting.error_past_datetime': 'ミーティング時間は現在より最低5分後でなければなりません。',
        'meeting.error_self_meeting': '自分自身にミーティングをリクエストすることはできません。',
        'meeting.booth_hint': 'デフォルト: {location}',




        // 🔥 Survey data (Added missing keys)
        'survey.busan': '釜山',
        'survey.seoul': 'ソウル/京畿/仁川',
        'survey.gyeongsang': '慶尚圏(釜山除く)',
        'survey.chungcheong': '忠清圏',
        'survey.jeolla': '全羅圏',
        'survey.gangwon': '江原圏',
        'survey.jeju': '済州圏',
        'survey.overseas': '海外',
        // 🔥 [追加] 交通手段キー
        'survey.walk': '徒歩',
        'survey.bicycle': '自転車',
        'survey.subway': '地下鉄',
        'survey.bus': 'バス',
        'survey.car': '自家用車',
        'survey.train': '列車/KTX',
        'survey.walk_bicycle': '徒歩/自転車',
        'survey.public': '公共交通',
        'survey.car_internal': '自家用車(内燃機関)',
        'survey.car_electric': 'EV/ハイブリッド',
        'survey.airplane': '航空機',
        

        'survey.transport_q': '会場({venueName})までどのような交通手段を利用されますか？',
        'survey.transport_desc': '選択した交通手段に基づいてCO₂削減量が計算されます。',
        'survey.transport_public': '公共交通機関 (地下鉄/バス)',
        'survey.transport_train': '列車 / KTX',
        'survey.transport_airplane': '航空機',
        'survey.transport_car_internal': '自家用車 (ガソリン/ディーゼル)',
        'survey.transport_car_electric': '自家用車 (EV/水素/ハイブリッド)',
        'survey.transport_walk_bicycle': '徒歩 / 自転車',


        'noti.title': '通知設定',
        'noti.saving': '保存中...',
        'noti.push_title': 'プッシュ通知',
        'noti.push_desc_on': 'アプリから送信される通知を受信',
        'noti.push_desc_off': 'ブラウザで通知がブロックされています',
        'noti.chat': 'チャットメッセージ',
        'noti.chat_desc': '新しいメッセージが届いたら通知',
        'noti.meeting': 'ミーティング通知',
        'noti.meeting_desc': 'ミーティング依頼、承認、日程通知',
        'noti.matching': 'マッチング通知',
        'noti.matching_desc': 'AI推薦企業、マッチング依頼',
        'noti.event': 'イベント通知',
        'noti.event_desc': '関心イベント日程、新イベント',
        'noti.carbon': 'カーボン活動通知',
        'noti.carbon_desc': 'ポイント獲得、目標達成',
        'noti.email_title': 'メール通知',
        'noti.email_desc': 'メールで通知を受信',
        'noti.newsletter': 'ニュースレター',
        'noti.newsletter_desc': 'ドローン産業ニュース、イベント情報',
        'noti.email_meeting': 'ミーティング要約',
        'noti.email_meeting_desc': 'ミーティング確定、日程リマインダー',
        'noti.email_matching': 'マッチングレポート',
        'noti.email_matching_desc': '週間AI推薦企業レポート',
        'noti.email_event': 'イベント情報',
        'noti.email_event_desc': '関心イベントアップデート',
        'noti.sms_title': 'SMS通知',
        'noti.sms_desc': 'テキストメッセージで通知を受信',
        'noti.sms_meeting': 'ミーティングリマインダー',
        'noti.sms_meeting_desc': 'ミーティング30分前SMS通知',
        'noti.dnd_title': '🌙 おやすみモード',
        'noti.dnd_desc': '設定された時間は通知を受信しません',
        'noti.start_time': '開始時間',
        'noti.end_time': '終了時間',
        'noti.current_setting': '現在の設定',
        'noti.help_title': '通知関連ヘルプ',
        'noti.help_1': 'プッシュ通知はブラウザ権限が必要です。',
        'noti.help_2': 'メール通知は登録時のメールに送信されます。',
        'noti.help_3': 'おやすみモード中でも重要な通知は受信されます。',
        // 🔥 [追加] 通知設定ページ欠落キー
        'noti.push_section': 'プッシュ通知',
        'noti.push_desc_denied': 'ブラウザで通知がブロックされています',
        'noti.push_chat': 'チャットメッセージ',
        'noti.push_chat_desc': '新しいメッセージが届いたら通知',
        'noti.push_meeting': 'ミーティング通知',
        'noti.push_meeting_desc': 'ミーティング依頼、承認、日程通知',
        'noti.push_matching': 'マッチング通知',
        'noti.push_matching_desc': 'AI推薦企業、マッチング依頼',
        'noti.push_event': 'イベント通知',
        'noti.push_event_desc': '関心イベント日程、新イベント',
        'noti.push_carbon': 'カーボン活動通知',
        'noti.push_carbon_desc': 'ポイント獲得、目標達成',
        'noti.email_section': 'メール通知',
        'noti.email_newsletter': 'ニュースレター',
        'noti.email_newsletter_desc': 'ドローン産業ニュース、イベント情報',
        'noti.sms_section': 'SMS通知',
        'noti.email_section': 'メール',
        'noti.email_marketing_title': '広告メール受信の同意',
        'noti.email_marketing_desc': 'イベント案内・お知らせなどの広告メールを受け取ります',
        'noti.email_marketing_note': 'ミーティング・マッチングなどサービス必須の通知メールは本同意とは無関係に送信されます。同意はいつでも撤回できます。',
        'noti.dnd_section': 'おやすみモード',
        'noti.dnd_start': '開始時間',
        'noti.dnd_end': '終了時間',
        'noti.dnd_current': '現在の設定: {start}から{end}まで通知を受信しません。',
        'noti.help_4': '設定は自動的に保存されます。',
        'noti.alert_not_supported': 'このブラウザは通知をサポートしていません。',
        'noti.alert_granted': '通知権限が許可されました。',
        'noti.alert_denied': '通知権限が拒否されました。ブラウザ設定で変更してください。',
        'noti.sms_1hour_before': '1時間前通知',
        'noti.sms_10min_before': '10分前通知',
        'noti.sms_event': 'イベントリマインダー',
        'noti.sms_event_desc': '展示会の主要日程およびイベント通知',
        'noti.help_sms_1': 'SMS通知は登録された携帯電話番号に送信されます。',
        'noti.help_sms_2': 'ミーティング通知は予約された相談開始前に送信されます。',
        'noti.help_sms_3': 'イベントリマインダーは展示会の主要日程をお知らせします。',
        'sms_consent_popup.title': 'SMS通知の受信同意',
        'sms_consent_popup.message': 'ミーティング通知やイベントリマインダーなどの重要な情報をSMSで受け取りますか？',
        'sms_consent_popup.benefit_meeting': 'ミーティング開始前通知',
        'sms_consent_popup.benefit_event': 'イベントリマインダー',
        'sms_consent_popup.profile_hint': 'プロフィールページでいつでも通知のオン・オフを切り替えられます。',
        'sms_consent_popup.decline': '後で',
        'sms_consent_popup.agree': '同意する',


        'events.bookmarks': '関心企業・イベント',
        'events.no_bookmarks': 'まだブックマークしたイベントがありません。',
        'events.no_official': '現在登録された公式イベントがありません',
        'events.coming_soon': '新しいイベントが間もなく公開されます',
        'events.date_tbd': '未定',

        // 🔥 [追加] EventsPage関連キー
        'today': '今日',
        'official_event': '公式',
        'search_events': 'イベント検索...',
        'filters': 'フィルター',
        'grid_view': 'グリッド',
        'category': 'カテゴリ',
        'category_exhibition': '展示会',
        'category_conference': 'カンファレンス',
        'category_trade_show': 'トレードショー',
        'category_workshop': 'ワークショップ',
        'category_seminar': 'セミナー',
        'category_networking': 'ネットワーキング',
        'category_community': 'コミュニティ',
        'clear_filters': 'フィルターをクリア',
        'retry': '再試行',
        'no_events_found': 'イベントがありません。',
        
        // 🔥 [追加] EventDetailPage関連キー
        'event_not_found': 'イベントが見つかりません。',
        'go_back': '戻る',
        'bookmark_removed': 'ブックマークが解除されました。',
        'reminder_1h': '開始1時間前です。',
        'reminder_10m': '開始10分前です！',
        'bookmark_added_with_reminder': 'イベントをブックマークしました。開始前に通知します！',
        'bookmark_failed': '操作を完了できませんでした。',
        'registration_success': '登録が完了しました。',
        'registration_failed': '登録に失敗しました。',
        'link_copied': 'リンクがコピーされました。',
        'cancelled': 'キャンセル済み',
        'date': '日時',
        'location': '場所',
        'participants': '参加者',
        'waiting_list': '待機リスト',
        'carbon_impact': 'カーボン影響',
        'eco_friendly_event': 'エコフレンドリーイベント',
        'description': '詳細説明',
        'no_description': '詳細説明はありません。',
        'show_less': '折りたたむ',
        'show_more': 'もっと見る',
        'opening_hours': '開場時間',
        'pricing': '料金情報',
        'regular_fee': '一般料金',
        'early_bird': '早割',
        'organizer_info': '主催/主管',
        'host': '主催',
        'organizer': '主管',
        'contact_person': '担当者',
        'contact': '連絡先',
        'official_website': '公式ウェブサイト',
        'tags': 'タグ',
        'gallery': 'ギャラリー',
        'history': '沿革',
        'visitors': '名',
        'registering': '登録中...',
        'event_cancelled': 'イベントがキャンセルされました',
        'register': '参加申請',
        'registered': '登録完了',
        'bookmarked': 'ブックマーク済み',
        'add_bookmark': 'ブックマーク追加',
        'edition_number': '第',
        'edition_suffix': '回',



        'calendar.year': '年',
        'calendar.month': '月',
        'calendar.sun': '日',
        'calendar.mon': '月',
        'calendar.tue': '火',
        'calendar.wed': '水',
        'calendar.thu': '木',
        'calendar.fri': '金',
        'calendar.sat': '土',
        'error.networkError': 'ネットワーク接続を確認してください',
        'error.serverError': 'サーバーエラーが発生しました',
        'error.unknownError': '不明なエラーが発生しました',
        'error.authError': '認証に失敗しました',
        'error.notFound': 'ページが見つかりません',
        'error.forbidden': 'アクセスが拒否されました',
        'error.timeout': 'リクエストタイムアウト',
        'error.badRequest': '不正なリクエスト: {message}',
        'error.unauthorized': '認証が必要です。再度ログインしてください。',
        'error.notFoundResource': '要求された情報が見つかりませんでした。',
        'error.unprocessable': '入力情報をご確認ください: {message}',
        'error.tooManyRequests': 'リクエストが多すぎます。しばらくしてから再試行してください。',
        'error.serverErrorRetry': 'サーバーエラーが発生しました。しばらくしてから再試行してください。',
        'error.unknownStatus': 'サーバーエラー ({status}): {message}',
        'error.save_failed': '保存に失敗しました。',
        'error.invalid_image_type': '画像ファイルのみアップロード可能です。',
        'error.file_too_large': 'ファイルサイズは5MB以下にしてください。',
        'error.upload_failed': 'アップロードに失敗しました。',
        'error.phone_format': '正しい携帯電話番号を入力してください。',
        'error.sms_send_failed': 'SMS送信に失敗しました。',
        'error.code_length': '認証番号6桁を入力してください。',
        'error.invalid_code': '認証番号が正しくありません。',
        'error.phone_verification_required': '変更された電話番号の認証が必要です。',
        'sms.code_sent': '認証コードが送信されました。',
        'sms.verified': '電話番号の認証が完了しました。',
        'sms.resend': '再送信',
        'sms.request': '認証要求',
        'sms.code_placeholder': '認証番号6桁',
        'sms.phone_verified': '電話番号の認証が完了しました。',
        'common.verifying': '確認中...',
        'myinfo.phone_change_notice': '電話番号変更時はSMS認証が必要です。',
        'myinfo.remove_business_card_title': '名刺削除',


        'welcome.message': 'ようこそ、{name}さん！',
        'welcome.greeting': 'こんにちは',
        'welcome.description': 'DSKへようこそ',
        
        // Home (🔥 Added missing keys)
        'home.greeting.morning': 'おはようございます、{name}さん',
        'home.greeting.afternoon': 'こんにちは、{name}さん',
        'home.greeting.evening': 'こんばんは、{name}さん',
        'home.activity_summary': '活動概要',
        'home.recent_activity': '最近の活動',
        'home.featured_companies': '注目企業',
        'home.featured_events': '注目イベント',
        'home.upcoming_schedule': '今後の予定',
        'home.view_all': 'すべて見る',
        'home.more': 'もっと見る',
        'home.loading': '読み込み中...',
        'home.no_activity': 'まだ記録された活動がありません。',
        'home.no_activity_hint': 'アンケートを完了するか、活動を記録するとここに表示されます。',
        'home.record_activity': '活動を記録',
        // 🔥🔥🔥 [2026-01-09 追加] ホームページ推薦関連キー
        'home.recommended_for_you': 'おすすめ企業',
        'home.recommended_companies': '推薦企業',


        
        'company.search': '企業検索',
        'company.filter': '企業フィルター',
        'company.details': '企業詳細',
        'company.contact': '連絡先',
        'company.interests': '興味',
        'company.products': '製品',
        'company.services': 'サービス',
        'company.matchScore': 'マッチングスコア',
        'company.requestMeeting': 'ミーティング依頼',
        'company.website': 'ウェブサイト',
        'company.email': 'メール',
        'company.phone': '電話',
        'company.chat_btn': 'チャット問い合わせ',
        'company.map_btn': 'ブース地図を見る',
        'company.booth': 'ブース位置',
        'company.booth_tbd': '未定',
        'company.tab_overview': '概要',
        'company.tab_products': '製品',
        'company.video_title': '紹介動画',
        'company.video_view': '動画を見る',
        'company.tab_news': '成果/ニュース',
        'company.info_employees': '従業員数',
        'company.info_founded': '設立年度',
        'company.info_location': '位置',
        'company.info_industry': '業種',
        'company.service_title': '主要サービス',
        'company.empty_products': '登録された製品がありません。',
        'company.empty_news': '登録されたニュースがありません。',
        'company.loading': '企業情報を読み込み中...',
        'company.error_not_found': '企業情報が見つかりません',
        'company.online_status': 'オンライン',
        'company.no_description': '登録された企業紹介がありません。',
        'company.alert_login_required': 'ログインが必要です。',
        'company.alert_chat_error': 'チャットルームの作成に失敗しました。',



        'companies.title': '出展企業一覧',
        'companies.search_placeholder': '企業名、業種、タグで検索...',
        'companies.filter_title': 'カテゴリ選択',
        'companies.filter_all': '全カテゴリ',
        'companies.search_label': '検索',
        'companies.sort_label': '並び替え',
        'companies.filter_active_count': 'カテゴリ {count} 件',
        'companies.filters_active': 'フィルター {count} 件適用中',
        'companies.clear_filters': 'フィルター解除',
        'companies.sort_name_asc': '名前順 (昇順)',
        'companies.sort_name_desc': '名前順 (降順)',
        'companies.sort_booth_asc': 'ブース番号順 (昇順)',
        'companies.sort_booth_desc': 'ブース番号順 (降順)',
        'companies.result_count': '全 {count} 社',
        'companies.empty_title': '登録された企業がありません',
        'companies.empty_search': '検索結果がありません',
        'companies.semantic_search': 'AI意味検索', // 🔥 [2026-06-10 B-2] 자연어(시맨틱) 검색 라벨
        'companies.reset': 'リセット',
        'companies.apply': '適用',
        'companies.end_of_list': 'これ以上表示できる企業はありません',
        'matching.title': 'AI企業マッチング',
        'matching.subtitle': '最適なビジネスパートナーを探しましょう。',
        'matching.mode_ai': 'AIおすすめ',
        'matching.mode_search': '直接検索',
        'matching.search_placeholder': '技術、製品、企業名を検索...',
        'matching.btn_search': '検索',
        'matching.result_count': '{count}社のおすすめ',
        'matching.analyzed': '分析完了',
        'matching.empty_ai': 'まだ関心登録された企業がありません。',
        'matching.empty_ai_desc': 'プロフィールを更新してください。',
        'matching.empty_search': '検索結果がありません。',
        'matching.empty_search_desc': '他のキーワードをお試しください。',
        'matching.section_ai': 'AI統合分析おすすめ',
        'matching.section_desc': '事業内容が似ている企業',
        'matching.section_prod': '主要製品が類似した企業',
        'matching.action_chat': 'メッセージ送信',
        'matching.booth_prefix': 'ブース',
        'matching.logo_alt': 'ロゴ',
        'matching.no_description': '企業紹介がありません。',
        'matching.action_location': '位置を見る',
        'matching.top_rank': 'TOP {rank}',
        'matching.loading': 'AI分析中...',
        'matching.info_message': 'DSK AI Valentineがあなたのプロフィールと関心分野を分析し、最適なパートナーを推薦します。',
        'matching.alert_login_required': 'ログインが必要です。',
        'matching.alert_load_error': 'データの読み込みに失敗しました。',
        'matching.alert_min_chars': '2文字以上入力してください。',
        'matching.alert_search_error': '検索中にエラーが発生しました。',
        'matching.alert_chat_error': 'チャットルームの作成に失敗しました。',
        'matching.alert_booth_location': 'ブース位置: {booth}',
        'matching.buyer_title': 'AI関心企業探索',
        'matching.buyer_subtitle': 'お客様の関心を分析し、最適な企業を見つけました。',
        'matching.buyer_mode_ai': '関心事マッチング',
        'matching.buyer_empty_title': 'マッチした企業がありません',
        'matching.buyer_empty_desc': '関心事をより具体的に作成すると精度が向上します。',
        'matching.buyer_analyzed': '分析完了',
        'matching.buyer_info_message': "DSK AI Valentineがあなたのプロフィールと関心分野を分析し、最適なパートナーを推薦します。",
        'matching.buyer_reason': 'お客様の関心事と高い類似度を示します。',
        'matching.section_interest': 'お客様の関心事と一致する企業',
        'matching.section_recommended': '推薦企業',
        'matching.company_reason': 'AI推薦マッチング',
        'matching.access_denied_title': 'アクセス制限',
        'matching.access_denied_message': 'この機能はビジネス会員専用です。',
        'matching.btn_business_upgrade': 'ビジネスビジター申請',
        'matching.alert_no_booth': 'ブース位置が指定されていません。',
        'matching.pending_approval_title': '承認待ち',
        'matching.pending_approval_message': '現在、事務局の承認待ちです。\n承認が完了するとマッチング機能をご利用いただけます。',

        // ─── businessUpgrade ───
        'businessUpgrade.title': 'ビジネス会員申請',
        'businessUpgrade.description': 'チャット、マッチング、ミーティング機能を利用するために\n以下の情報をすべて入力してください。',
        'businessUpgrade.profile_label': 'プロフィール写真',
        'businessUpgrade.profile_required': '*必須（本人写真）',
        'businessUpgrade.profile_placeholder': 'クリックして登録',
        'businessUpgrade.profile_error': '本人写真を登録してください',
        'businessUpgrade.company_placeholder': '会社/所属名 *',
        'businessUpgrade.company_error': '会社/所属名を入力してください',
        'businessUpgrade.department_placeholder': '部署（任意）',
        'businessUpgrade.job_title_placeholder': '役職 *',
        'businessUpgrade.job_title_error': '役職を入力してください',
        'businessUpgrade.card_label': '名刺画像',
        'businessUpgrade.card_required': '*必須',
        'businessUpgrade.card_error': '名刺画像をアップロードしてください',
        'businessUpgrade.notice': '申請後、事務局の承認が必要です。承認後にチャット、マッチング、ミーティング機能をご利用いただけます。',
        'businessUpgrade.btn_cancel': 'キャンセル',
        'businessUpgrade.btn_submit': '承認申請',
        'businessUpgrade.btn_uploading': 'アップロード中...',
        'businessUpgrade.btn_processing': '処理中...',
        'businessUpgrade.success_title': '申請完了',
        'businessUpgrade.success_message': 'ビジネス会員申請が完了しました。\n事務局承認後にチャット、マッチング、ミーティング機能をご利用いただけます。',
        'businessUpgrade.success_confirm': '確認',
        'businessUpgrade.error_file_size': 'ファイルサイズは5MB以下にしてください。',
        'businessUpgrade.error_file_type': '画像ファイルのみアップロード可能です。',
        'businessUpgrade.error_profile_required': 'プロフィール写真は必須です。本人写真を登録してください。',
        'businessUpgrade.error_card_required': '名刺画像は必須です。',
        'businessUpgrade.error_submit': '申請処理中にエラーが発生しました。もう一度お試しください。',
        'businessUpgrade.success_toast': '申請が完了しました！',

        'matching.hero_title': 'AIマッチング',
        'matching.hero_subtitle': 'お客様のプロフィールに基づいて最適なビジネスパートナーを推薦します。',
        'matching.btn_ai_analysis': '関心分野ベース分析',
        'matching.btn_direct_search': '直接検索',
        'matching.result_count_suffix': '社の推薦企業',
        'matching.before_title': 'マッチング前',
        'matching.before_desc': '今年のマッチングはまだ始まっていません。開始されるとおすすめ企業を表示します。',
        'matching.calculating_title': 'AI分析中...',
        'matching.calculating_desc': '最適なビジネスパートナーを探しています。',
        'matching.calculating_progress': '分析進行中...',
        'matching.calculating_almost': 'もうすぐ完了します...',
        'matching.search_empty_title': '検索結果がありません',
        'matching.search_empty_desc': '他のキーワードで検索してください。',
        'matching.info_modal_title': 'AI企業マッチングガイド',
        'matching.reason_buyer': 'AI分析: お客様の関心事に関連する企業です。',
        'matching.reason_default': 'AI分析: お客様の関心事に関連する企業です。',
        'matching.reason_high': 'あなたの関心分野との一致度が非常に高い企業です',
        'matching.reason_high_cat': '「{category}」分野であなたの関心と非常に高く一致しています',
        'matching.reason_mid': 'あなたの関心分野に関連する企業です',
        'matching.reason_mid_cat': '「{category}」分野があなたの関心と関連しています',
        'matching.reason_low': '新たなビジネスチャンスとなり得る企業です',
        'matching.reason_low_buyer': '潜在的な取引先となり得る企業です',
        'matching.show_more': 'もっと見る',
        'matching.score_explain': 'マッチングスコア（%）はカテゴリ適合度・関心分野の一致・企業情報の充実度を総合した値で、高いほど相性の良いパートナーです。',
        'matching.no_company_name': '会社名なし',
        'matching.no_category': 'カテゴリ未指定',
        'matching.booth_tbd': '未定',
        'matching.btn_location': '位置を見る',
        'matching.btn_meeting': 'ミーティング依頼',
        'matching.btn_message': 'メッセージ',
        'matching.booth': 'ブース',
        'matching.section_title': '統合おすすめ',
        'matching.no_interest_title': '関心分野を入力してください',
        'matching.no_interest_desc': 'AIマッチングのために、関心のある技術・製品をアンケートで入力してください。',
        'matching.no_interest_btn': 'アンケートを作成する',
        'matching.buyer_no_interest_title': '関心分野を入力してください',
        'matching.buyer_no_interest_desc': 'AIマッチングのために、関心のある技術・製品をアンケートで入力してください。',
        'matching.buyer_no_interest_btn': 'バイヤーアンケートを作成する',
        'matching.alert_min_chars': '検索語を2文字以上入力してください。',
        'matching.alert_search_error': '検索中にエラーが発生しました。',
        'matching.search_result_count_suffix': '件の検索結果',



        'meeting.schedule': 'スケジュール',
        'meeting.upcoming': '今後のミーティング',
        'meeting.past': '過去のミーティング',
        'meeting.cancel': 'キャンセル',
        'meeting.reschedule': '再スケジュール',
        'meeting.confirm': '確認',
        'meeting.location': '場所',
        'meeting.time': '時間',
        'meeting.type': '種類',
        'meeting.participants': '参加者',
        'meeting.notes': 'メモ',
        'meetings.title': 'マイスケジュール',
        'meetings.view_list': 'リスト',
        'meetings.view_calendar': 'カレンダー',
        'meetings.empty': '予定されたミーティングはありません。',
        'meetings.go_book': 'ミーティングを予約する',
        'meetings.location_tbd': '場所未定',
        'meetings.upcoming': '今後のミーティング',
        'meetings.no_schedule': 'この日は予定がありません',
        'meetings.no_upcoming': '予定されたミーティングがありません',
        'meetings.schedule_new': '新しいミーティングを予約しましょう',
        'meeting.modal_title': 'ミーティングを提案',
        'meeting.target_company': '対象企業',
        'meeting.field_title': 'ミーティング主題',
        'meeting.field_date': '日付',
        'meeting.field_time': '時間',
        'meeting.field_location': '場所（任意）',
        'meeting.default_title': 'ビジネスミーティング',
        'meeting.booth_location': '御社のブースでお会いしたいです。',
        'meeting.placeholder_title': '例：製品紹介および協力協議',
        'meeting.placeholder_location': '例：Aホール102号ブース',
        'meeting.notice_proposal': 'ミーティング提案が相手に送信され、承諾されるとカレンダーに自動登録されます。',
        'meeting.sending_proposal': 'ミーティング提案を送信中...',
        'meeting.btn_send_proposal': 'ミーティング提案を送信',
        'meeting.btn_go_to_chat': 'チャットルームへ移動',
        'meeting.proposal_sent_title': '提案完了',
        'meeting.confirm_navigate_to_chat': 'ミーティング提案が正常に送信されました。チャットルームで相手の返答を確認しますか？',
        'meeting.toast_proposal_sent': 'ミーティング提案を送信しました。',
        'meeting.error_title_required': 'ミーティング主題を入力してください。',
        'meeting.error_date_required': '日付を選択してください。',
        'meeting.error_time_required': '時間を選択してください。',
        'meeting.error_no_company': '企業情報がありません。',
        'meeting.error_chat_creation': 'チャットルームの作成に失敗しました。',
        'meeting.error_invalid_datetime': '無効な日付/時間です。',
        'meeting.error_proposal_failed': 'ミーティング提案に失敗しました。',
        'meeting.field_notes': 'ミーティング議題・メッセージ',
        'meeting.placeholder_notes': '相手に伝えたい詳細な議題やメッセージを入力してください。',
        // 🔥 [2026-04-22] MeetingProposalModal 不足キー追加
        'meeting.matrix_label': '候補時間の選択',
        'meeting.loading_matrix': '空き時間を読み込み中です。',
        'meeting.no_active_event_notice': '有効なイベント期間がないと提案できません。',
        'meeting.reload_matrix_button': '時間表を再読み込み',
        'meeting.max_candidates_notice': '最大{count}件まで選択できます。',
        'meeting.select_slots_required': '候補時間を選択してください。',
        // 🔥 [2026-04-23] ChatRoom ミーティング提案カードの不足キー
        'meeting.expires_at_label': '期限',
        'meeting.accept_slot_button': 'この時間で承諾',
        // 🔥 [2026-04-22] MeetingAvailabilityGrid 不足キー追加
        'meeting.matrix_status_available': '利用可能',
        'meeting.matrix_status_me_busy': '自分の予定と重複',
        'meeting.matrix_status_other_busy': '相手の予定と重複',
        'meeting.matrix_status_room_full': '部屋なし',
        'meeting.matrix_status_past': '過去',
        'meeting.matrix_status_seminar': 'セミナー (選択不可)',
        'meeting.selected_slots_count': '選択済み候補',
        'meeting.recommended_slots_label': '推奨候補',
        'meeting.matrix_time': '時間',
        'meeting.matrix_selected': '選択済み',
        'meeting.available_rooms_count': '{count}部屋',
        'meeting.room_selection_title': '候補別ミーティングルーム',
        'meeting.no_available_room_option': '選択可能な部屋なし',
        'meeting.room_capacity_persons': '{count}名',


        'chat.messages': 'メッセージ',
        'chat.send': '送信',
        'chat.typing': '入力中...',
        'chat.online': 'オンライン',
        'chat.offline': 'オフライン',
        'chat.newMessage': '新しいメッセージ',
        'chat.readReceipt': '既読',
        'chat.unread': '未読',
        'chat.read_by_other': '既読',
        'chat.unread_by_other': '未読',
        'chat.title_default': 'チャットルーム',
        'chat.input_placeholder': 'メッセージを入力...',
        'chat.meeting_detected_title': 'ミーティングキーワード感知',
        'chat.confirm_meeting_proposal': 'ミーティング提案カードを送信しますか？',
        'chat.btn_send_card': '提案カードを送信',
        'chat.btn_send_text': 'テキストで送信',
        'chat.meeting_proposal': 'ミーティング提案',
        'chat.meeting_reschedule': '修正提案',
        'chat.btn_accept': '承諾',
        'chat.btn_reschedule': '修正提案',
        'chat.btn_decline': '拒否',
        'chat.status_accepted': '承諾済み',
        'chat.status_declined': '拒否済み',
        'chat.status_pending': '応答待ち',
        'chat.status_countered': '修正提案を送信済み',
        'chat.leave_room': '退出する',
        'chat.confirm_leave': 'チャットルームを退出しますか？ 会話履歴が削除されます。',
        'chat.alert_leave_error': '退出に失敗しました。',
        'chat.empty_messages': 'メッセージがありません',
        'chat.empty_messages_desc': '相手が承諾しない場合や、状況により返信が遅れることがあります。現場訪問を推奨しており、この機能は補助手段です。',
        'chat.meeting_proposal_title': '📅 ミーティング提案',
        'chat.meeting_reschedule_title': '🕒 日程変更提案',
        'chat.meeting_proposal_format': '{title}\n日時: {datetime}\n場所: {location}',
        'chat.meeting_reschedule_format': '日程変更提案: {title}\n変更日時: {datetime}\n場所: {location}',
        'chat.meeting_keywords': 'ミーティング,会議,約束,会う,日程',
        // 🔥 [追加] チャット読み込み＆ミーティングキーワード検出モーダル
        'chat.loading': 'チャットを読み込み中...',
        'chat.ask_send_meeting_card': 'ミーティング関連キーワードが検出されました。ミーティングカードを送信しますか？',
        'chat.btn_no_thanks': '結構です',
        'chat.toast_meeting_sent': 'ミーティング提案を送信しました。',
        'chat.toast_meeting_accepted': 'ミーティングが承諾されました。',
        'chat.toast_meeting_declined': 'ミーティングが拒否されました。',
        'chat.meeting_added_to_calendar': 'ミーティングが日程に追加されました。',
        'chat.modal_meeting_title': 'ミーティング提案',
        'chat.modal_reschedule_title': '日程変更',
        'chat.modal_meeting_subject': '件名',
        'chat.modal_meeting_subject_placeholder': '例: パートナーシップ協議',
        'chat.modal_date': '日付',
        'chat.modal_time': '時間',
        'chat.modal_location': '場所',
        'chat.modal_location_placeholder': '例: Aホール102号ブース',
        'chat.modal_original_title': '既存の予定',
        'chat.modal_new_date': '変更日付',
        'chat.modal_new_time': '変更時間',
        'chat.modal_new_location': '変更場所',
        'chat.btn_send_proposal': '提案を送信',
        'chat.btn_send_reschedule': '変更提案を送信',
        'chat.alert_meeting_required': '件名、日付、時間は必須項目です。',
        'chat.alert_no_receiver': '相手が見つかりません。',
        'chat.error_invalid_datetime': '無効な日付/時間です。',
        'chat.location_tbd': '場所未定',
        'chat.alert_meeting_sent': 'ミーティング提案を送信しました。',
        'chat.alert_meeting_send_error': 'ミーティング提案の送信に失敗しました。',
        'chat.alert_reschedule_sent': '日程変更提案を送信しました。',
        'chat.other_party': '相手',
        'chat.meeting_accepted_message': 'ミーティング提案を承諾しました。',
        'chat.meeting_declined_message': 'ミーティング提案を拒否しました。',
        'chat.alert_no_meeting_id': 'ミーティング情報が見つかりません。',
        'chat.alert_meeting_accepted': 'ミーティングが承諾されました。',
        'chat.alert_meeting_declined': 'ミーティングが拒否されました。',
        'chat.alert_meeting_accept_error': 'ミーティング承諾中にエラーが発生しました: {message}',
        'chat.alert_meeting_decline_error': 'ミーティング拒否中にエラーが発生しました: {message}',
        'chat.alert_no_reschedule_info': '変更するミーティング情報が見つかりません。',
        'chat.alert_reschedule_error': '変更するミーティング情報がありません。',
        'chat.preview_image': '📷 写真',
        'chat.preview_file': '📎 ファイル',
        'chat.preview_system': '📢 システムメッセージ',
        'chat.preview_meeting_reschedule_sent': '🕒 日程変更提案を送信しました。',
        'chat.preview_meeting_reschedule_received': '🕒 日程変更提案が届きました。',
        'chat.preview_meeting_rescheduled_sent': '🕒 日程変更を承諾しました。',
        'chat.preview_meeting_rescheduled_received': '🕒 ミーティング日程が変更されました。',
        'chat.preview_meeting_cancelled_sent': '🚫 ミーティングをキャンセルしました。',
        'chat.preview_meeting_cancelled_received': '🚫 ミーティングがキャンセルされました。',
        'chat.status_message_accepted': '{name}さんがミーティングを承諾しました。',
        'chat.status_message_declined': '{name}さんがミーティングを拒否しました。',
        'chat.status_message_rescheduled': '{name}さんが日程変更を承諾しました。',
        'chat.status_message_cancelled': '{name}さんがミーティングをキャンセルしました。',
        'chat.status_message_time': '時間',
        'chat.status_message_location': '場所',
        'chat.status_message_reason': '理由',

        'notifications.title': '通知',
        'notifications.mark_all_read': 'すべて既読',
        'notifications.mark_all_read_success': 'すべての通知を既読にしました。',
        'notifications.empty': '新しい通知はありません。',
        'notifications.swipe_delete_hint': '左にスワイプして削除',
        'notifications.time_just_now': 'たった今',
        'notifications.time_minutes_ago': '{count}分前',
        'notifications.time_hours_ago': '{count}時間前',
        'notifications.new_arrived': '🔔 新しい通知が届きました。',
        'notifications.delete_failed': '通知の削除に失敗しました。',
        'notifications.load_failed': '通知の読み込みに失敗しました。',
        'notifications.default_title': '通知',
        'notifications.meeting_default_title': 'ミーティング',
        'notifications.meeting_proposed.title': 'ミーティング提案',
        'notifications.meeting_proposed.message': '{name}さんが{title}の候補時間を提案しました。',
        'notifications.meeting_accepted.title': 'ミーティング承諾',
        'notifications.meeting_accepted.message': '{name}さんが{title}を承諾しました。{time}',
        'notifications.meeting_rejected.title': 'ミーティング拒否',
        'notifications.meeting_rejected.message': '{name}さんが{title}を拒否しました。{reason}',
        'notifications.meeting_rescheduled.title': '日程変更承諾',
        'notifications.meeting_rescheduled.message': '{name}さんが{title}の日程変更を承諾しました。{time}',
        'notifications.meeting_cancelled.title': 'ミーティングキャンセル',
        'notifications.meeting_cancelled.message': '{title}がキャンセルされました。{reason}',
        'notifications.meeting_reminder_1day.title': 'ミーティング前日通知',
        'notifications.meeting_reminder_1day.message': '{title}は明日予定されています。{time}',
        'notifications.meeting_reminder_1hour.title': 'ミーティング1時間前通知',
        'notifications.meeting_reminder_1hour.message': '{title}は1時間後に開始します。{time}',
        'notifications.meeting_reminder_10min.title': 'ミーティング10分前通知',
        'notifications.meeting_reminder_10min.message': '{title}は10分後に開始します。{time}',
        'notifications.meeting_pending_reminder.title': 'ミーティング返信待ち',
        'notifications.meeting_pending_reminder.message': '{title}への返信がまだ完了していません。',
        'notifications.meeting_expired_reminder.title': 'ミーティング提案期限切れ',
        'notifications.meeting_expired_reminder.message': '{title}の提案が期限切れになりました。',
        'notifications.survey_reminder.title': 'アンケート依頼',
        'notifications.survey_reminder.message': '{title}に関するアンケートにご回答ください。',
        'notifications.event_reminder.title': 'イベント通知',
        'notifications.event_reminder.message': '{title}がまもなく開始します。{time}',

        // 🔥🔥🔥 미팅 미리보기 - 보낸사람용
        'chat.preview_meeting_sent': '📅 ミーティング提案を送信しました。',
        'chat.preview_meeting_accepted_sent': '✅ ミーティングを承諾しました。',
        'chat.preview_meeting_rejected_sent': '❌ ミーティングをお断りしました。',

        // 🔥🔥🔥 미팅 미리보기 - 받는사람용
        'chat.preview_meeting_received': '📅 ミーティング提案が届きました。',
        'chat.preview_meeting_accepted_received': '✅ ミーティングが承諾されました。',
        'chat.preview_meeting_rejected_received': '❌ ミーティングがお断りされました。',

        // 기존 키 (하위 호환성)
        'chat.preview_meeting': '📅 ミーティング提案が届きました。',
        'chat.preview_meeting_accepted': '✅ ミーティングが承諾されました。',
        'chat.preview_meeting_rejected': '❌ ミーティングがお断りされました。',
        'chat.preview_new_message': '📅 新しいメッセージがあります。',
        'chat.pending_title': '承認待ち',
        'chat.pending_message': '現在、事務局の承認待ちです。\n承認が完了するとチャット機能をご利用いただけます。',
        'chat.access_denied_title': '会員専用機能',
        'chat.guest_message': 'チャット機能は会員のみご利用いただけます。',
        'chat.visitor_message': 'チャット、マッチング、ミーティング依頼機能はビジネス会員専用です。\nビジネス会員に申請しますか？',
        'chat.alert_load_error': 'チャットルームの読み込みに失敗しました。',
        'chat.alert_send_error': 'メッセージの送信に失敗しました。',
        'chat.alert_reschedule_send_error': '変更提案の送信に失敗しました: {message}',
        'chat.view_profile': 'プロフィールを見る',
        'chat.unknown_user': '不明なユーザー',
        'chat.profile_title': 'プロフィール',
        'chat.business_card': '名刺',
        'chat.click_to_enlarge': 'クリックで拡大',
        // 🔥🔥🔥 [2026-01-09 追加] ファイル添付関連キー
        'chat.max_files_exceeded': '最大{count}個のファイルのみ選択できます。',
        'chat.file_too_large': 'ファイルサイズが10MBを超えています。',
        'chat.file_type_not_allowed': 'サポートされていないファイル形式です。',
        'chat.upload_cancelled': 'アップロードがキャンセルされました。',
        'chat.file_sent': 'ファイルを送信しました。',
        'chat.files_sent': '{count}個のファイルを送信しました。',
        'chat.file_send_error': 'ファイルの送信に失敗しました。',
        'chat.attach_file': 'ファイル添付',
        'chat.add_caption': '説明を追加（任意）',
        'chat.drop_files_here': 'ここにファイルをドロップ',
        'chat.max_files_info': '最大5ファイル、各10MB以下',
        'chat.download': 'ダウンロード',
        'chat.preview_not_available': 'プレビューを使用できません。',

        // 🔥🔥🔥 [2026-01-09 追加] 音声メッセージ関連キー
        'chat.voice_permission_denied': 'マイクの権限が必要です。',
        'chat.voice_message': '音声メッセージ',
        'chat.voice_sent': '音声メッセージを送信しました。',
        'chat.voice_send_error': '音声メッセージの送信に失敗しました。',

        // 🔥🔥🔥 [2026-01-09 追加] ビデオメッセージ関連キー
        'chat.video_permission_denied': 'カメラの権限が必要です。',
        'chat.video_message': 'ビデオメッセージ',
        'chat.video_sent': 'ビデオメッセージを送信しました。',
        'chat.video_send_error': 'ビデオメッセージの送信に失敗しました。',
        'chat.record_video': 'ビデオ録画',
        'chat.retake': '撮り直す',
        'chat.start_recording': '録画開始',


        'activities.start_conversation': '会話を始めましょう',
        'activity.tab_chat': 'チャット',
        'activity.tab_match': 'マッチング',
        'activity.empty_chat': 'チャット履歴がありません',
        'activity.empty_chat_sub': '企業を探して会話を始めましょう',
        'activity.explore_btn': '企業を探す',
        'events.explore_btn': 'イベントを探す',
        'activities.tab_chats': 'チャット',
        'activities.tab_matching': 'マッチング',
        'activities.loading_chats': 'チャットを読み込み中...',
        'activities.loading_matches': 'マッチング分析中...',
        'activities.empty_chats': 'チャット履歴がありません',
        'activities.empty_chats_desc': '興味のある企業を見つけて会話を始めましょう。',
        'activities.empty_matches': 'おすすめ企業がまだありません',
        'activities.empty_matches_desc': 'プロフィールを更新すると、より正確なおすすめが表示されます。',
        'activities.btn_explore': '企業を探す',
        'activities.btn_retry': '再試行',
        'activities.btn_preferences': '設定変更',
        'activities.btn_send_message': 'メッセージ送信',
        'activities.no_name': '名称未設定',
        'activities.no_messages': 'メッセージがありません。',
        'activities.no_company_name': '企業名なし',
        'activities.no_industry': '業種情報なし',
        'activities.booth_prefix': 'ブース',
        'activities.time_just_now': 'たった今',
        'activities.time_minutes_ago': '{minutes}分前',
        'activities.time_hours_ago': '{hours}時間前',
        'activities.time_days_ago': '{days}日前',
        'activities.error_load_matches': 'マッチング情報の読み込みに失敗しました。',
        'activities.delete_chat': 'チャットルーム削除',
        'activities.delete_chat_title': 'チャットルームを退出',
        'activities.delete_chat_message': '「{name}」を退出しますか？\nチャット履歴は削除され、復元できません。',
        'activities.confirm_delete': 'このチャットルームを退出しますか？\nチャット履歴は復元できません。',
        'activities.delete_confirm': '退出する',
        'activities.delete_failed': 'チャットルームの削除に失敗しました。',



        'chatbot.title': 'Valentine',
        'chatbot.subtitle': 'DSK',
        'chatbot.welcome': 'こんにちは！👋\n\nDSK AIキュレーターです。\n企業やブースの位置について質問してください！',
        'chatbot.placeholder': 'メッセージを入力...',
        'chatbot.suggestions_label': 'おすすめの質問',
        'chatbot.q1': 'トイレはどこですか？',
        'chatbot.q2': 'ドローン企業をおすすめして',
        'chatbot.q3': '私の関心分野の企業をおすすめして',
        'chatbot.clear': '削除',
        'chatbot.close': '閉じる',
        'chatbot.relatedCompanies': '関連企業推薦',
        'chatbot.fold': '折りたたむ',
        'chatbot.unfold': '展開する',
        'chatbot.detail': '詳細',
        'chatbot.booth': 'ブース',
        'chatbot.thinking': '考え中...',
        'chatbot.error': 'エラーが発生しました。',
        'chatbot.confirmClear': '会話を削除しますか？',
        'chatbot.resetMessage': '会話がリセットされました。',
        'chatbot.responding': '応答中...',
        'chatbot.nav_error': '申し訳ありません。経路案内中にエラーが発生しました。',
        'chatbot.lang_change_ko': '한국어로 변경',
        'chatbot.lang_change_en': 'Change to English',
        'chatbot.lang_change_ja': '日本語に変更',
        'chatbot.lang_change_zh': '切换到中文',
        'chatbot.lang_accept_ko': '네, 한국어로 변경할게요',
        'chatbot.lang_accept_en': 'Yes, change to English',
        'chatbot.lang_accept_ja': 'はい、日本語に変更します',
        'chatbot.lang_accept_zh': '是的，切换到中文',
        'chatbot.lang_decline': 'いいえ、そのままで',
        'chatbot.meeting_header': 'ビジネス商談予約',
        'chatbot.meeting_reserve': '商談予約',
        'chatbot.more_companies': '他 {count}社',
        'chatbot.survey_shortcut': 'アンケートへ',
        'chatbot.send_failed': '送信失敗',

        // 🔥 [追加] 3Dブースマップ関連キー
        'map.exhibition_hall_title': '{venueFullName}',
        'map.exhibition_subtitle': '{eventName}',
        'map.passable': '通路',
        'map.search_result': '検索結果',
        'map.selected': '選択中',
        'map.no_info': '情報なし',
        'map.more_companies': '+{count}社の企業あり',
        'map.rotate_left': '左に回転',
        'map.rotate_right': '右に回転',
        'map.reset_view': '初期画面に戻す',
        'map.entrance': '入口',
        'map.exit': '出口',
        'map.toilet': 'お手洗い',
        'map.booths_showing': '{count}個のブースを表示中',
        'map.reset': '初期化',
        'map.facility_desc': '観覧客のための便宜施設です。',
        'map.tap_to_highlight_all': 'タップすると全ての位置を表示します',
        'map.places': 'か所',
        'map.no_company_info': '登録された企業情報がありません。',
        'map.search_placeholder': 'ブース番号または企業名で検索...',
        'map.loading_map': 'マップデータ読み込み中...',
        'map.loading_3d': '3Dビュー読み込み中...',
        'map.load_failed': 'マップデータの読み込みに失敗しました',
        'map.preparing': 'ブースマップ準備中...',
        'map.coming_soon_title': '🗺️ ブースマップ公開予定',
        'map.coming_soon_message': 'この機能は2月2日に公開予定です。',
        'map.alert_select_both': '出発地と目的地を両方選択してください。',
        'map.alert_no_route': '現在有効なルートが見つかりません。',
        'map.alert_route_error': 'ルート探索中にエラーが発生しました。',
        'map.alert_invalid_booth': '存在しないブース番号です。再度ご確認ください。',
        'map.nav_title': '🧭 ナビゲーション',
        'map.nav_reset': 'リセット',
        'map.nav_select_start': '出発地を選択',
        'map.nav_select_end': '目的地を選択',
        'map.nav_routing': 'ルート案内中',
        'map.nav_hide': '隠す',
        'map.nav_calculating': 'ルート計算中...',
        'map.nav_start': 'ナビゲーション開始',
        'map.nav_set_start': '出発地',
        'map.nav_set_end': '目的地',
        'map.company_detail': '企業詳細情報',
        'map.company_count': '{count}社入居',
        'map.company_detail_short': '詳細',
        'map.booth_count': '{count}個のブース',
        'map.company_extra': '他{count}社',
        'map.optimal_route_active': '最適ルート案内中',
        'map.booth_count_label': '{count}個のブース',
        'map.approx_distance': '約{distance}m',
        'map.location_prompt_title': '📍 現在どこにいますか？',
        'map.location_prompt_desc': '最短ルートを計算するために\n現在のブース番号を入力してください。',
        'map.location_prompt_placeholder': '例: A01, D18',
        'map.congestion_title': 'リアルタイム混雑度',
        'map.congestion_low': '空き',
        'map.congestion_medium': '普通',
        'map.congestion_high': '混雑',



        // 🔥 [追加] ブースマップ お気に入り企業巡回
        'boothMap.favoriteRoute': 'お気に入り企業ルート',
        'boothMap.favoriteTraverse': 'お気に入り巡回',
        'boothMap.noFavoriteCompanies': 'お気に入り企業がありません。',
        'boothMap.routeCalculationFailed': 'ルートを計算できませんでした。',
        'boothMap.routeCalculationError': 'ルート計算中にエラーが発生しました。',
      },

      zh: {
        'app.name': 'DSK',
        'app.subtitle': '{eventName}',
        'app.description': '{eventName} - 碳中和展览平台',
        'userTypes.VISITOR': '普通观众',
        'userTypes.BUSINESS': '商务',
        'userTypes.COMPANY': '参展企业',
        'userTypes.BUYER': '买家',
        'userTypes.ADMIN': '管理员',
        'userTypes.BUSINESS_PENDING': '等待审批',
        'userTypes.INVESTOR': '投资者',
        'userTypes.BUYER_PENDING': '等待审批',


        'common.ok': '确定',
        'common.cancel': '取消',
        'common.save': '保存',
        'common.delete': '删除',
        'common.edit': '编辑',
        'common.search': '搜索',
        'common.filter': '筛选',
        'common.sort': '排序',
        'common.loading': '加载中...',
        'common.error': '发生错误',
        'common.success': '成功',
        'common.retry': '重试',
        'common.load_failed': '数据加载失败，请检查网络连接。',
        'survey.complete_first': '完成问卷后即可使用。',
        'auth.verification_code_required': '请输入6位验证码。',
        'auth.verification_code_invalid': '验证码不一致。',
        'auth.sms_verify_first': '请先完成短信验证。',
        'common.refresh': '刷新',
        'common.back': '返回',
        'common.next': '下一步',
        'common.previous': '上一步',
        'common.close': '关闭',
        'common.share': '分享',
        'common.copy': '复制',
        'common.download': '下载',
        'common.upload': '上传',
        'common.send': '发送',
        'common.submit': '提交',
        'common.reset': '重置',
        'common.confirm': '确认',
        'common.logout': '退出',
        'common.guest': '访客',
        'common.saved': '已保存。',
        'common.deleted': '已删除。',
        'common.confirm_delete': '确定要删除吗？',
        'common.uncategorized': '未分类',
        'common.count_unit': '项',
        'common.count_suffix': '次',
        'common.minutes': '分钟',
        'common.hours': '小时',
        'common.days': '天',
        'common.user': '用户',
        'common.no_email': '无邮箱',
        'common.no_name': '无名称',
        'common.sending': '发送中...',
        'common.close': '关闭',
        'common.uploading': '上传中...',
        'common.saving': '保存中...',

        'auth.login': '登录',
        'auth.login_required': '请先登录。',

        // 🔥 [新增] 企业详情页面及类别相关
        'company.intro_title': '公司介绍',
        'category.all': '全部',
        'category.drone_platforms': '无人机平台及硬件',
        'category.military_defense_uavs': '军事·国防·安全无人机',
        'category.industrial_uavs': '工业·商业无人机应用',
        'category.autonomous_flight_utm': '自主飞行·管制·UAM',
        'category.battery_power_charging': '电池·电源·充电解决方案',
        'category.motors_propellers_components': '电机·螺旋桨·零部件',
        'category.ai_software_data': 'AI·软件·数据',
        'category.sensors_navigation_cameras': '相机·传感器·通信·导航技术',
        'category.ugv_usv_robotics': '无人移动平台扩展领域',
        'category.space_aerospace': '航天·航空航天技术',
        'category.training_education_services': '教育·培训·服务',
        'category.sustainable_aviation': '可持续航空技术',
        'category.other_interest': '其他',
        'category.group_basic': '基础',
        'category.group_airframe': '机体/平台',
        'category.group_technology': '技术/系统',
        'category.group_power': '动力/部件',
        'category.group_unmanned': '无人移动平台',
        'category.group_services': '教育/服务',
        'category.group_other': '其他',
        'auth.logout': '退出',
        'auth.register': '注册',
        'auth.spc_exhibitor_notice': '您是参展企业吗？请先以商务访客身份注册，完成企业认证后将升级为企业会员。', // 🔥 [2026-06-10 B-3 D1] SPC exhibitor 유입 안내
        'welcome.return_to_guide': '返回展会指南', // 🔥 [2026-06-10 B-3 D4] SPC return_url CTA
        'auth.email': '邮箱',
        'auth.login_id': '邮箱或账号（公司名称）',
        'year_consent.title': '{year} 使用同意确认',
        'year_consent.message': '您可以继续使用现有账号参加 {year} 活动。为今年的使用，请重新确认以下同意事项。',
        'year_consent.all': '全部同意',
        'year_consent.terms': '[必选] 服务条款',
        'year_consent.privacy': '[必选] 个人信息收集与使用',
        'year_consent.third_party': '[必选] 个人信息第三方提供（商务配对）',
        'year_consent.third_party_info': '进行商务配对时，将向对方企业提供公司名称、负责人姓名、联系方式及关注领域。',
        'year_consent.sms': '[可选] 接收短信通知',
        'year_consent.email': '[可选] 接收电子邮件广告信息',
        'year_consent.night': '[可选] 接收夜间（21~08时）广告',
        'year_consent.view': '查看',
        'year_consent.hint': '可选项目可随时在 个人资料 > 通知设置 中更改。',
        'year_consent.required_missing': '请同意必选项目。',
        'year_consent.save_failed': '保存失败，请重试。',
        'year_consent.logout': '退出登录',
        'year_consent.submit': '同意并继续',
        'year_consent.terms_title': '服务条款',
        'year_consent.privacy_title': '隐私政策',
        'year_consent.confirm_agree': '确认并同意',
        'year_consent.close': '关闭',
        'auth.password': '密码',
        'auth.show_password': '显示密码',
        'auth.hide_password': '隐藏密码',
        'auth.passwordConfirm': '确认密码',
        'auth.name': '姓名',
        'auth.phone': '电话',
        'auth.company': '公司',
        'auth.forgotPassword': '忘记密码',
        'auth.resetPassword': '重置密码',
        'auth.socialLogin': '社交登录',
        'auth.loginWithGoogle': '使用Google登录',
        'auth.loginWithKakao': '使用Kakao登录',
        'auth.loginWithNaver': '使用Naver登录',
        'auth.loginWithApple': '使用Apple登录',
        'auth.title_sub': '{eventName}',
        'auth.guest': '访客',
        'auth.guest_login': '访客',
        'auth.auto_login': '自动登录',
        'auth.forgot_pw': '忘记密码',
        'auth.no_account': '没有账号？',
        'auth.error_email': '请输入邮箱',
        'auth.error_pw': '请输入密码',
        'auth.fail_login': '登录失败',
        'nav.home': '首页',
        'nav.companies': '企业',
        'nav.meetings': '会议',
        'nav.chats': '聊天',
        'nav.carbon': '碳积分',
        'nav.profile': '个人资料',
        'nav.settings': '设置',
        'nav.notifications': '通知',
        'nav.help': '帮助',
        'nav.events': '活动',
        'nav.activities': '活动记录',
        'nav.map': '地图',
        'nav.matching': '匹配',
        'profile.language_setting': '语言设置',
        'profile.language_desc': '更改应用程序语言',
        'profile.change_language': '更改语言',
        'profile.interests_saved': '兴趣领域已保存。',
        'profile.interests_saved_refreshing': '兴趣领域已保存。匹配将稍后刷新。',
        'profile.interests_no_change': '没有需要保存的更改。',
        'profile.interests_save_failed': '保存兴趣领域失败。',
        'profile.my_info': '我的信息',
        'profile.profile_settings': '个人资料及账户设置',
        'profile.company_info': '公司信息管理',
        'profile.company_info_desc': '企业介绍及产品信息管理',
        'profile.carbon_points': '碳积分',
        'profile.carbon_desc': '按交通工具计算碳减排量',
        'profile.bookmarks': '关注的活动',
        'profile.bookmarks_desc': '关注的企业和活动统一管理',
        'profile.notifications': '通知',
        'profile.notifications_desc': '推送和电子邮件通知管理',
        'profile.settings': '设置',
        'profile.settings_desc': '语言、版本信息',
        'profile.activity_details': '活动详情',
        'profile.total_matches': '匹配次数',
        'profile.completed_meetings': '已完成的会议',
        'profile.completed_events': '参加活动',
        // 🔥 [新增] 个人资料动态文本
        'profile.carbon_total_saved': '🌱 共减排 {amount}kg',
        'profile.tree_effect': '🌲 相当于 {count}棵树',
        'profile.activity_hint': '🌱 请记录活动！',
        'profile.badges': '徽章',
        'profile.no_badges': '访问展位即可获得徽章。',
        'profile.badge.explorer': '探险家',
        'profile.badge.curator': '策展人',
        'profile.badge.master': '大师',
        'profile.badge_threshold': '{count}个展位',
        'profile.badge_tooltip': '{name}（参观{count}个展位）',
        'settings.title': '应用设置',
        'settings.language': '语言',
        'settings.language_label': '应用语言',
        'settings.language_desc': '* 部分内容可能以原始语言显示。',
        'settings.screen': '屏幕',
        'settings.theme': '主题',
        'settings.theme_light': '浅色模式',
        'settings.theme_dark': '深色模式',
        'settings.theme_system': '系统设置',
        'settings.font_size': '字体大小',
        'settings.font_small': '小',
        'settings.font_medium': '中',
        'settings.font_large': '大',
        'settings.media': '媒体',
        'settings.autoplay': '视频自动播放',
        'settings.autoplay_desc': 'Wi-Fi连接时自动播放视频',
        'settings.data_saver': '节省数据模式',
        'settings.data_saver_desc': '降低画质以减少数据使用',
        'settings.data_mgmt': '数据管理',
        'settings.clear_cache': '清除缓存',
        'settings.clear_cache_desc': '删除临时数据',
        'settings.clear_data': '清除所有数据',
        'settings.clear_data_desc': '退出并删除所有数据',
        'settings.app_info': '应用信息',
        'settings.version': '版本',
        'settings.build': '构建号',
        'settings.developer': '开发者',
        'settings.terms': '服务条款',
        'settings.privacy': '隐私政策',
        'settings.licenses': '开源许可',
        'settings.alert_cache_cleared': '已清除缓存。',
        'settings.modal_delete_title': '⚠️ 删除数据',
        'settings.modal_delete_confirm': '确定要删除所有数据吗？\n此操作无法撤销。',
        'settings.font_size_desc': '更改整个应用的字体大小。',
        'settings.data_saver_on': '✓ 已启用 - 使用缩略图',
        'settings.data_saver_off': '已禁用 - 使用高清图片',



        'myinfo.title': '编辑我的信息',
        'myinfo.profile_image': '个人照片',
        'myinfo.profile_alt': '个人头像',
        'myinfo.change_photo': '更改照片',
        'myinfo.photo_change_coming_soon': '照片更改功能准备中。',
        // 🔥 [新增] 碳贡献度卡片相关键值
        'myinfo.carbon_impact': '我的碳贡献度',
        'myinfo.tree_equivalent': '树木换算',
        'myinfo.trees': '棵',
        'myinfo.view_carbon_details': '查看碳减排详情',
        'myinfo.basic_info': '基本信息',
        'myinfo.name': '姓名',
        'myinfo.name_placeholder': '请输入姓名',
        'myinfo.email': '邮箱',
        'myinfo.email_desc': '邮箱不能更改',
        'myinfo.email_unchangeable': '邮箱不能更改。',
        'myinfo.phone': '电话号码',
        'myinfo.phone_placeholder': '请输入电话号码',
        'myinfo.work_info': '工作信息',
        'myinfo.department': '部门',
        'myinfo.department_placeholder': '请输入部门',
        'myinfo.position': '职位',
        'myinfo.position_select': '请选择职位',
        'myinfo.position.staff': '职员',
        'myinfo.position.senior': '主任',
        'myinfo.position.assistant_manager': '副理',
        'myinfo.position.manager': '课长',
        'myinfo.position.deputy_manager': '副部长',
        'myinfo.position.general_manager': '部长',
        'myinfo.position.director': '董事',
        'myinfo.position.executive': '高管',
        'myinfo.position.ceo': '总裁',
        'myinfo.position.other': '其他',
        'myinfo.bio': '自我介绍',
        'myinfo.bio_title': '一句话介绍',
        'myinfo.bio_label': '一句话介绍',
        'myinfo.bio_placeholder': '请撰写简短的自我介绍 (B2B匹配时显示)',
        'myinfo.categories_count': '已选择',
        'myinfo.error_max_categories': '最多可选择5个感兴趣的领域。',
        'myinfo.interests_title': '感兴趣的领域',
        'myinfo.interests_desc': '选择您感兴趣的类别，并自由输入您正在寻找的具体产品或技术。',
        'myinfo.interest_text_label': '感兴趣的产品/技术 (自由填写)',
        'myinfo.interest_text_placeholder': '请自由输入您感兴趣的产品或技术。',
        'myinfo.save_interests': '保存兴趣领域',
        'myinfo.alert_name_required': '请输入姓名',
        'myinfo.error_name_required': '姓名为必填项。',
        'myinfo.alert_phone_invalid': '电话号码格式不正确',
        'myinfo.error_phone_invalid': '请输入有效的电话号码。',
        'myinfo.photo_updated': '头像已更新。',
        'myinfo.business_card': '名片',
        'myinfo.business_card_desc': '上传名片图片后，在商务会议时将向对方展示。',
        'myinfo.business_card_alt': '名片图片',
        'myinfo.business_card_uploaded': '名片已上传。请点击保存按钮以生效。',
        'myinfo.change_business_card': '更换名片',
        'myinfo.remove_business_card': '删除名片',
        'myinfo.confirm_remove_business_card': '确定要删除名片图片吗？',
        'myinfo.upload_business_card': '上传名片图片',
        'myinfo.upload_business_card_hint': '点击选择图片（最大5MB）',
        'myinfo.business_card_notice': '更改名片后，请点击下方的"保存"按钮以生效。',
        // 🔥 [新增] 安全/密码相关键值
        'myinfo.security': '安全',
        'myinfo.password_desc': '为了账户安全，建议您定期更改密码。',
        'myinfo.change_password': '更改密码',
        'myinfo.current_password': '当前密码',
        'myinfo.new_password': '新密码',
        'myinfo.confirm_password': '确认新密码',
        'myinfo.password_changed': '密码已成功更改。',
        'myinfo.password_hint': '密码至少需要8个字符。',
        'myinfo.change_password_btn': '更改',
        // 账户初始设置 (ChangePasswordPage)
        'setup.title': '账户初始设置',
        'setup.subtitle': '为了安全，请更改临时密码。',
        'setup.subtitle_survey': '设置完成后将跳转到{survey}。',
        'setup.survey': '问卷调查',
        'setup.current_password': '当前（临时）密码 *',
        'setup.change_info': '变更信息',
        'setup.new_email': '实际使用的邮箱（用作ID） *',
        'setup.contact_name': '联系人姓名 *',
        'setup.phone_label': '联系人电话',
        'setup.phone_required': '* 需要验证',
        'setup.phone_placeholder': '输入电话号码',
        'setup.select_country': '选择国家',
        'setup.no_results': '无搜索结果',
        'setup.send_code': '发送验证码',
        'setup.resend_code': '重新发送',
        'setup.verified': '已验证',
        'setup.new_password': '新密码（至少8位） *',
        'setup.confirm_password': '确认新密码 *',
        'setup.sms_consent': '同意接收短信通知（可选）',
        'setup.submit': '完成设置',
        'setup.complete_alert': '账户初始设置已完成。\n请使用新邮箱重新登录。',
        'setup.email_invalid': '邮箱格式不正确。',
        'setup.name_required': '请输入姓名。',
        'setup.phone_invalid_kr': '请输入正确的手机号码。（例：010-1234-5678）',
        'setup.overseas_notice': '海外号码请去掉国家代码和首位0。',
        'setup.sms_description': '通过短信接收会议提醒、活动通知等。',
        'setup.sms_optional_note': '即使不同意，也不影响账户设置和服务的使用。',
        'setup.notice': '设置完成后，需要使用新邮箱(ID)重新登录。',
        'setup.notice_label': '提示',
        'setup.processing': '处理中...',
        'setup.phone_verified': '手机号码验证已完成。',
        'setup.enter_code': '请输入发送到手机的6位验证码。',
        'setup.code_placeholder': '6位验证码',
        'setup.confirm': '确认',
        'myinfo.error_current_password_required': '请输入当前密码。',
        'myinfo.error_new_password_required': '请输入新密码。',
        'myinfo.error_password_min_length': '密码至少需要8个字符。',
        'myinfo.error_confirm_password_required': '请确认密码。',
        'myinfo.error_password_mismatch': '密码不匹配。',
        'myinfo.error_current_password_wrong': '当前密码不正确。',
        // 🔥 [新增] 密码实时验证消息
        'myinfo.password_valid': '密码可用。',
        'myinfo.password_match': '密码匹配。',
        'myinfo.current_password_valid': '密码已确认。',

        // 🔥 [新增] 密码更改相关附加键值
        'error.password_change_failed': '密码更改失败。',
        'myinfo.current_password_placeholder': '输入当前密码',
        'myinfo.new_password_placeholder': '至少8个字符',
        'myinfo.confirm_password_placeholder': '再次输入新密码',
        'myinfo.intl_phone_hint': '请选择国家代码后输入电话号码。',
        'myinfo.phone_placeholder_intl': '电话号码（不含国家代码）',

        'common.processing': '处理中...',


        'company_info.title': '公司信息',
        'company_info.no_company': '没有注册的公司',
        'company_info.no_company_desc': '注册公司后可使用B2B匹配服务。',
        'company_info.register_btn': '注册公司',
        'company_info.basic': '基本信息',
        'company_info.name': '公司名称',
        'company_info.industry': '行业分类',
        'company_info.desc_label': '公司简介',
        'company_info.desc_placeholder': '请撰写公司简介',
        'company_info.details': '详细信息',
        'company_info.founded': '成立年份',
        'company_info.employees': '员工数',
        'company_info.website': '网站',
        'company_info.address': '地址',
        'company_info.contact': '联系方式',
        'company_info.email': '主要邮箱',
        'company_info.phone': '主要电话',
        'company_info.visit': '访问',
        'company_info.main_products_label': '主要产品/服务',
        'company_info.main_products_placeholder': '请用逗号(,)分隔输入产品名称',
        'company_info.main_products_hint': '例：工业无人机、摄影设备、软件解决方案',
        'company_info.media_section': '横幅与宣传视频',
        'company_info.banner_label': '横幅图片',
        'company_info.banner_hint': '显示在企业详情页顶部。请使用宽幅图片(建议1200×400以上)。',
        'company_info.banner_upload': '上传横幅',
        'company_info.banner_uploading': '上传中…',
        'company_info.banner_remove': '移除横幅',
        'company_info.banner_upload_failed': '横幅上传失败',
        'company_info.video_label': '宣传视频URL',
        'company_info.video_hint': '请输入视频链接(如YouTube)。将显示在企业详情页。',
        'company_info.video_view': '观看视频',
        'company_info.edit_submitted': '修改申请已提交，等待管理员审核。',
        'company_info.pending_edit': '有待处理的修改申请。管理员批准后将生效。',
        'company_info.no_changes': '未检测到更改。',
        'company_info.edit_restricted_notice': '可编辑企业简介、主要产品、横幅和宣传视频。如需修改其他信息，请联系管理员。',
        'company_info.editable_section': '可编辑项',
        'company_info.submit_edit': '提交修改申请',
        'company_info.description': '企业介绍',
        'company_info.description_placeholder': '请填写企业详细介绍',
        'company_info.established_year': '成立年份',
        'company_info.employee_count': '员工数',
        'company_info.year_suffix': '年',
        'company_info.industry_select': '选择行业',
        'company_info.industry.drone_manufacturing': '无人机制造',
        'company_info.industry.drone_service': '无人机服务',
        'company_info.industry.software': '软件',
        'company_info.industry.hardware': '硬件',
        'company_info.industry.logistics': '物流',
        'company_info.industry.agriculture': '农业',
        'company_info.industry.construction': '建筑',
        'company_info.industry.media': '媒体',
        'company_info.industry.security': '安全',
        'company_info.industry.education': '教育',
        'company_info.industry.research': '研究',
        'company_info.industry.government': '政府机构',
        'company_info.industry.other': '其他',
        'company_info.industry.uncategorized': '未分类',
        'common.submitting': '提交中...',

        // Carbon (🔥 Added missing keys)
        'carbon.title': '碳活动计算器',
        'carbon.activity': '碳减排活动',
        'carbon.activities': '碳活动',
        'carbon.activities_desc': '查看路线和减排量',
        'carbon.history': '历史',
        'carbon.history_desc': '查看过去记录',
        'carbon.status_title': '🌱 我的碳减排现状',
        'carbon.saved': '减排量',
        'carbon.trees': '树木换算',
        'carbon.trees_empty': '植树',
        'carbon.environmental_impact': '环境贡献',
        'carbon.loading': '计算中...',
        'carbon.offset_needed': '需要碳抵消！✈️',
        'carbon.great_job': '太棒了！🌿',
        'carbon.reduction_label': '总CO₂减排量',
        'carbon.input_title': '📍 出行信息输入',
        'carbon.origin_label': '您从哪里来？',
        'carbon.origin_custom': '直接输入距离 (km)',
        'carbon.transport_label': '您乘坐什么来？',
        'carbon.passengers_label': '乘客人数 (拼车时减排量增加)',
        'carbon.calc_btn': '计算',
        'carbon.result_title': '🌱 碳减排结果',
        'carbon.distance': '行驶距离',
        'carbon.transport': '交通工具',
        'carbon.baseline': '私家车基准排放量',
        'carbon.actual': '实际排放量',
        'carbon.reduction': '与私家车相比减排量',
        'carbon.tree_equiv': '🌳 相当于{count}棵树一年吸收量',
        'carbon.reward_text': '现在记录可获得',
        'carbon.reward_suffix': '！',
        'carbon.submit_btn': '🎉 获得积分',
        'carbon.submit_loading': '获取中...',
        'carbon.history_title': '活动历史',
        'carbon.history_empty': '暂无记录的活动',
        'carbon.fold': '收起',
        'carbon.unfold': '展开',
        'carbon.guide_title': '碳减排提示',
        'carbon.guide_1': '使用公共交通与私家车相比可节约高达80%碳排放',
        'carbon.guide_2': '电动车/混动车与普通车相比可节约50%以上',
        'carbon.guide_3': '步行/骑车零碳排放！',
        'carbon.guide_4': '航空出行时请用其他环保活动抵消',
        'carbon.transport.car': '私家车',
        'carbon.transport.bus': '高速巴士',
        'carbon.transport.train': 'KTX/火车',
        'carbon.transport.plane': '飞机',
        'carbon.transport.bicycle': '自行车',
        'carbon.transport.walk': '步行',
        'carbon.points': '积分',
        'carbon.earned': '获得',
        'carbon.total': '总计',
        'carbon.redeem': '使用',
        'carbon.leaderboard': '排行榜',
        'carbon.reduce': '减排量',
        'carbon.trees_unit': '棵',
        'carbon.hours_unit': '小时',
        'carbon.cups_unit': '杯',
        'carbon.offset_guide': '航空出行碳排放较多。请用其他活动抵消！',
        'carbon.no_survey': '请在下方选择出发地区和交通方式，然后点击计算按钮。',
        // 🔥 [新增] 侧边栏碳减排量显示
        'carbon.my_reduction': '我的碳减排量',        
        'carbon.my_journey': '我的{venueName}之旅',
        'carbon.survey_loaded': '问卷回答已自动加载。',
        // 🔥 [新增] 地区选择相关键值
        'carbon.province_city': '道/市',
        'carbon.select_region': '请选择地区',
        'carbon.district': '市/郡/区',
        'carbon.optional': '可选',
        'carbon.select_all': '全部 / 不选择',
        'carbon.departure_region': '出发地区',
        'carbon.transport_mode': '交通方式',
        'carbon.recalculate': '计算减排量',
        'carbon.saving': '保存中...',
        'carbon.save_result': '保存减排量',
        'carbon.save_success': '保存成功！',
        'carbon.save_failed': '保存失败。',
        'carbon.total_stats': '累计现状',
        'carbon.total_saved': '总减排量',
        'carbon.trees_equivalent': '树木换算',
        'carbon.calc_note_1': '本计算以汽油车(150g CO₂/km)为基准进行比较。',
        'carbon.calc_note_2': '目的地',
        'carbon.exhibition_center': '第一展馆',
        'carbon.calc_note_3': '距离使用Haversine公式计算，并应用道路曲率修正(×1.35)。',
        'carbon.excess': '超额排放量',
        'carbon.my_emission_excess': '碳超额排放',


// ==================== 🔥🔥🔥 [新增] 会议详情页面 ====================
        'meeting.detail_title': '会议详情',
        'meeting.phone_required_title': '需要登记电话号码',
        'meeting.phone_required': '发起会议请求需要登记电话号码，以便通过短信接收对方的接受/变更通知。请在个人资料中登记后重试。',
        'meeting.btn_go_register_phone': '前往个人资料登记',
        'meeting.loading': '正在加载会议信息...',
        'meeting.load_error': '无法加载会议。',
        'meeting.calendar_notice': '此会议将保存到应用内日历',
        'meeting.time_location': '时间和地点',
        'meeting.location_tbd': '地点待定',
        'meeting.description': '描述',
        'meeting.agenda': '议程',
        'meeting.agenda_item': '议程项目',
        'meeting.participants': '参与者',
        'meeting.organizer': '组织者',
        'meeting.notes': '备注',
        
        // 会议状态
        'meeting.status_confirmed': '已确认',
        'meeting.status_scheduled': '已安排',
        'meeting.status_in_progress': '进行中',
        'meeting.status_pending': '待处理',
        'meeting.status_proposed': '已提议',
        'meeting.status_requested': '已请求',
        'meeting.status_cancelled': '已取消',
        'meeting.status_declined': '已拒绝',
        'meeting.status_completed': '已完成',
        'meeting.status_accepted': '已接受',
        
        // 优先级
        'meeting.priority_high': '高',
        'meeting.priority_urgent': '紧急',
        'meeting.priority_medium': '中',
        'meeting.priority_low': '低',
        
        // 操作按钮
        'meeting.btn_accept_with_notification': '接受（设置提醒）',
        'meeting.cancel_meeting': '取消会议',
        'meeting.join_meeting': '加入视频会议',
        'meeting.join_in_progress': '加入视频会议（进行中）',
        'meeting.already_accepted': '您已接受此会议',
        
        // 确认/错误消息
        'meeting.confirm_accept': '是否接受此会议？',
        'meeting.accept_error': '接受会议失败。',
        'meeting.decline_reason_prompt': '请输入拒绝原因（可选）:',
        'meeting.decline_error': '拒绝会议失败。',
        'meeting.cancel_reason_prompt': '请输入取消原因（可选）:',
        'meeting.cancelled': '会议已取消。',
        'meeting.cancel_error': '取消会议失败。',
        'meeting.cancel_reason_required': '请输入至少 5 个字符的取消原因。',
        'meeting.cancel_reason_required_label': '取消原因（必填）',
        'meeting.cancel_reason_placeholder_required': '请输入取消原因。',
        'meeting.cancel_reason_min_hint': '至少需要输入 5 个字符。',
        
        // 时间警告
        'meeting.warning_time_passed': '⚠️ 会议时间已过。\n是否提议新的时间？',
        'meeting.warning_time_soon': '⚠️ 距离会议开始还有{minutes}分钟。\n准备时间可能不足。是否提议新的时间？',
        
        // 重新安排
        'meeting.reschedule_title': '重新安排会议',
        'meeting.new_proposed_time': '新提议时间',
        'meeting.select_new_time': '请选择新的时间。',
        'meeting.change_reason': '更改原因（可选）',
        'meeting.change_reason_placeholder': '请输入需要更改时间的原因。',
        'meeting.time_change_reason': '时间变更请求',
        'meeting.send_reschedule': '发送更改请求',
        'meeting.reschedule_sent': '已发送日程变更提议。',
        'meeting.reschedule_error': '发送更改请求失败。',
        'meeting.reschedule_target_missing': '找不到本次会议的对方信息。',
        'meeting.counterpart': '对方',
        'meeting.reschedule_initial_title': '{title} - 日程变更请求',
        'meeting.original_meeting_time': '原会议时间',
        'meeting.reschedule_default_note': '我想提议一个新的会议时间。请查看建议的时间选项，并选择最方便的时间。',
        'meeting.reschedule_reason_hint': '请输入日程变更原因或备注。',
        'meeting.field_reschedule_message': '发送给对方的留言',
        'meeting.placeholder_reschedule_notes': '可选择填写日程变更原因或补充说明。',
        'meeting.reschedule_modal_title': '提议新的会议时间',
        'meeting.btn_send_reschedule_proposal': '发送日程变更提议',
        'meeting.toast_reschedule_proposal_sent': '已发送日程变更提议。',
        'meeting.reschedule_proposal_message': '会议日程变更请求\n\n请从提议的时间中选择一个。',
        'meeting.cancel_meeting': '取消会议',
        'meeting.cancel_confirm_message': '确定要取消这个会议吗？参与者将收到通知。',
        'meeting.cancel_reason': '取消原因（可选）',
        'meeting.cancel_reason_placeholder': '请输入取消原因...',
        'meeting.btn_confirm_cancel': '确认取消',
        'meeting.decline_meeting': '拒绝会议',
        'meeting.decline_confirm_message': '确定要拒绝这个会议邀请吗？',
        'meeting.decline_reason': '拒绝原因（可选）',
        'meeting.decline_reason_placeholder': '请输入拒绝原因...',
        'meeting.reschedule_meeting': '更改日程',
        'meeting.reschedule_confirm_message': '确定要更改会议日程吗？',
        'meeting.reschedule_reason': '更改原因（可选）',
        'meeting.reschedule_reason_placeholder': '请输入更改原因...',
        'meeting.new_datetime': '新的日期时间',
        'meeting.request': '会议请求',
        'meeting.waiting_for_response': '正在等待对方回复',
        'meeting.propose_reschedule': '提议更改日程',
        'meeting.next_actions': '下一步操作',
        'meeting.manage_meeting': '会议管理',
        'meeting.action_hint_final': '此会议已结束。您只能查看详细信息。',
        'meeting.action_hint_pending': '请回复邀请，必要时可在聊天中协商新的时间。',
        'meeting.action_hint_waiting': '正在等待对方回复。必要时可以再次发送日程变更提议。',
        'meeting.action_hint_accepted': '此会议已确认。如需变更或取消，请使用下方按钮。',
        'meeting.action_hint_available': '可用操作取决于当前会议状态。',
        'meeting.location_hint': '默认设置为对方的展位号',
        'meeting.error_past_datetime': '会议时间必须至少在当前时间5分钟之后。',
        'meeting.error_self_meeting': '不能向自己发送会议请求。',
        'meeting.booth_hint': '默认值: {location}',



        // 🔥 Survey data (Added missing keys)
        'survey.busan': '釜山',
        'survey.seoul': '首尔/京畿/仁川',
        'survey.gyeongsang': '庆尚圈(釜山除外)',
        'survey.chungcheong': '忠清圈',
        'survey.jeolla': '全罗圈',
        'survey.gangwon': '江原圈',
        'survey.jeju': '济州圈',
        'survey.overseas': '海外',
        // 🔥 [新增] 交通方式键值
        'survey.walk': '步行',
        'survey.bicycle': '自行车',
        'survey.subway': '地铁',
        'survey.bus': '巴士',
        'survey.car': '私家车',
        'survey.train': '火车/KTX',
        'survey.walk_bicycle': '步行/自行车',
        'survey.public': '公共交通',
        'survey.car_internal': '私家车(内燃机)',
        'survey.car_electric': '电动车/混动车',
        'survey.airplane': '飞机',


        'survey.transport_q': '您打算如何前往会场({venueName})？',
        'survey.transport_desc': '将根据您的选择计算碳减排量。',
        'survey.transport_public': '公共交通 (地铁/巴士)',
        'survey.transport_train': '火车 / KTX',
        'survey.transport_airplane': '飞机',
        'survey.transport_car_internal': '私家车 (汽油/柴油)',
        'survey.transport_car_electric': '私家车 (电动/氢能/混合动力)',
        'survey.transport_walk_bicycle': '步行 / 自行车',



        'noti.title': '通知设置',
        'noti.saving': '保存中...',
        'noti.push_title': '推送通知',
        'noti.push_desc_on': '接收应用发送的通知',
        'noti.push_desc_off': '浏览器已阻止通知',
        'noti.chat': '聊天消息',
        'noti.chat_desc': '新消息到达时通知',
        'noti.meeting': '会议通知',
        'noti.meeting_desc': '会议请求、接受、日程通知',
        'noti.matching': '匹配通知',
        'noti.matching_desc': 'AI推荐企业、匹配请求',
        'noti.event': '活动通知',
        'noti.event_desc': '关注活动日程、新活动',
        'noti.carbon': '碳活动通知',
        'noti.carbon_desc': '积分获得、目标达成',
        'noti.email_title': '电子邮件通知',
        'noti.email_desc': '通过电子邮件接收通知',
        'noti.newsletter': '新闻通讯',
        'noti.newsletter_desc': '无人机行业新闻、活动消息',
        'noti.email_meeting': '会议摘要',
        'noti.email_meeting_desc': '会议确认、日程提醒',
        'noti.email_matching': '匹配报告',
        'noti.email_matching_desc': '每周AI推荐企业报告',
        'noti.email_event': '活动消息',
        'noti.email_event_desc': '关注活动更新',
        'noti.sms_title': 'SMS通知',
        'noti.sms_desc': '通过短信接收通知',
        'noti.sms_meeting': '会议提醒',
        'noti.sms_meeting_desc': '会议前30分钟SMS通知',
        'noti.dnd_title': '🌙 勿扰模式',
        'noti.dnd_desc': '设定的时间内不接收通知',
        'noti.start_time': '开始时间',
        'noti.end_time': '结束时间',
        'noti.current_setting': '当前设置',
        'noti.help_title': '通知相关帮助',
        'noti.help_1': '推送通知需要浏览器权限。',
        'noti.help_2': '电子邮件通知发送到注册时的邮箱。',
        'noti.help_3': '勿扰模式中也会收到重要通知。',
        // 🔥 [新增] 通知设置页面缺失键值
        'noti.push_section': '推送通知',
        'noti.push_desc_denied': '浏览器已阻止通知',
        'noti.push_chat': '聊天消息',
        'noti.push_chat_desc': '新消息到达时通知',
        'noti.push_meeting': '会议通知',
        'noti.push_meeting_desc': '会议请求、接受、日程通知',
        'noti.push_matching': '匹配通知',
        'noti.push_matching_desc': 'AI推荐企业、匹配请求',
        'noti.push_event': '活动通知',
        'noti.push_event_desc': '关注活动日程、新活动',
        'noti.push_carbon': '碳活动通知',
        'noti.push_carbon_desc': '积分获得、目标达成',
        'noti.email_section': '电子邮件通知',
        'noti.email_newsletter': '新闻通讯',
        'noti.email_newsletter_desc': '无人机行业新闻、活动消息',
        'noti.sms_section': 'SMS通知',
        'noti.email_section': '电子邮件',
        'noti.email_marketing_title': '同意接收广告邮件',
        'noti.email_marketing_desc': '接收活动指南、资讯等广告邮件',
        'noti.email_marketing_note': '会议、匹配等服务必需的通知邮件与本同意无关，将照常发送。您可随时撤回同意。',
        'noti.dnd_section': '勿扰模式',
        'noti.dnd_start': '开始时间',
        'noti.dnd_end': '结束时间',
        'noti.dnd_current': '当前设置: {start}至{end}期间不接收通知。',
        'noti.help_4': '设置会自动保存。',
        'noti.alert_not_supported': '此浏览器不支持通知功能。',
        'noti.alert_granted': '已获得通知权限。',
        'noti.alert_denied': '通知权限被拒绝。请在浏览器设置中更改。',
        'noti.sms_1hour_before': '1小时前提醒',
        'noti.sms_10min_before': '10分钟前提醒',
        'noti.sms_event': '活动提醒',
        'noti.sms_event_desc': '展会主要日程及活动通知',
        'noti.help_sms_1': 'SMS通知将发送到您注册的手机号码。',
        'noti.help_sms_2': '会议提醒将在预约的咨询开始前发送。',
        'noti.help_sms_3': '活动提醒会通知您展会的主要日程。',
        'sms_consent_popup.title': 'SMS通知接收同意',
        'sms_consent_popup.message': '是否希望通过SMS接收会议提醒、活动提醒等重要信息？',
        'sms_consent_popup.benefit_meeting': '会议开始前提醒',
        'sms_consent_popup.benefit_event': '活动提醒',
        'sms_consent_popup.profile_hint': '您可以随时在个人资料页面开启或关闭通知。',
        'sms_consent_popup.decline': '稍后再说',
        'sms_consent_popup.agree': '同意',

        'events.bookmarks': '关注的企业/活动',
        'events.no_bookmarks': '暂无关注的活动。',
        'events.no_official': '目前没有注册的官方活动',
        'events.coming_soon': '新活动即将推出',
        'events.date_tbd': '待定',

        // 🔥 [新增] EventsPage相关键值
        'today': '今天',
        'official_event': '官方',
        'search_events': '搜索活动...',
        'filters': '筛选',
        'grid_view': '网格',
        'category': '类别',
        'category_exhibition': '展览会',
        'category_conference': '会议',
        'category_trade_show': '贸易展',
        'category_workshop': '工作坊',
        'category_seminar': '研讨会',
        'category_networking': '社交活动',
        'category_community': '社区活动',
        'clear_filters': '清除筛选',
        'retry': '重试',
        'no_events_found': '没有找到活动。',
        
        // 🔥 [新增] EventDetailPage相关键值
        'event_not_found': '找不到活动。',
        'go_back': '返回',
        'bookmark_removed': '已取消收藏。',
        'reminder_1h': '开始前1小时。',
        'reminder_10m': '开始前10分钟！',
        'bookmark_added_with_reminder': '活动已收藏。开始前会通知您！',
        'bookmark_failed': '无法完成操作。',
        'registration_success': '注册成功。',
        'registration_failed': '注册失败。',
        'link_copied': '链接已复制。',
        'cancelled': '已取消',
        'date': '日期',
        'location': '地点',
        'participants': '参与者',
        'waiting_list': '候补名单',
        'carbon_impact': '碳影响',
        'eco_friendly_event': '环保活动',
        'description': '详细说明',
        'no_description': '暂无详细说明。',
        'show_less': '收起',
        'show_more': '展开',
        'opening_hours': '开放时间',
        'pricing': '价格信息',
        'regular_fee': '普通票价',
        'early_bird': '早鸟票',
        'organizer_info': '主办/承办',
        'host': '主办',
        'organizer': '承办',
        'contact_person': '联系人',
        'contact': '联系方式',
        'official_website': '官方网站',
        'tags': '标签',
        'gallery': '画廊',
        'history': '历史',
        'visitors': '人',
        'registering': '注册中...',
        'event_cancelled': '活动已取消',
        'register': '报名参加',
        'registered': '已注册',
        'bookmarked': '已收藏',
        'add_bookmark': '添加收藏',
        'edition_number': '第',
        'edition_suffix': '届',


        'calendar.year': '年',
        'calendar.month': '月',
        'calendar.sun': '日',
        'calendar.mon': '一',
        'calendar.tue': '二',
        'calendar.wed': '三',
        'calendar.thu': '四',
        'calendar.fri': '五',
        'calendar.sat': '六',
        'error.networkError': '请检查网络连接',
        'error.serverError': '服务器错误',
        'error.unknownError': '未知错误',
        'error.authError': '认证失败',
        'error.notFound': '页面未找到',
        'error.forbidden': '访问被拒绝',
        'error.timeout': '请求超时',
        'error.badRequest': '请求错误: {message}',
        'error.unauthorized': '需要登录,请重新登录。',
        'error.notFoundResource': '未找到请求的资源。',
        'error.unprocessable': '请检查输入信息: {message}',
        'error.tooManyRequests': '请求过多,请稍后再试。',
        'error.serverErrorRetry': '服务器错误,请稍后再试。',
        'error.unknownStatus': '服务器错误 ({status}): {message}',
        'error.save_failed': '保存失败。',
        'error.invalid_image_type': '只能上传图片文件。',
        'error.file_too_large': '文件大小必须在5MB以下。',
        'error.upload_failed': '上传失败。',
        'error.phone_format': '请输入正确的手机号码。',
        'error.sms_send_failed': '短信发送失败。',
        'error.code_length': '请输入6位验证码。',
        'error.invalid_code': '验证码不正确。',
        'error.phone_verification_required': '需要验证更改后的手机号码。',
        'sms.code_sent': '验证码已发送。',
        'sms.verified': '手机号码验证成功。',
        'sms.resend': '重新发送',
        'sms.request': '获取验证码',
        'sms.code_placeholder': '6位验证码',
        'sms.phone_verified': '手机号码验证成功。',
        'common.verifying': '验证中...',
        'myinfo.phone_change_notice': '更改手机号码时需要短信验证。',
        'myinfo.remove_business_card_title': '删除名片',


        'welcome.message': '欢迎，{name}！',
        'welcome.greeting': '您好',
        'welcome.description': '欢迎来到DSK',
        
        // Home (🔥 Added missing keys)
        'home.greeting.morning': '早上好，{name}',
        'home.greeting.afternoon': '您好，{name}',
        'home.greeting.evening': '晚上好，{name}',
        'home.activity_summary': '活动摘要',
        'home.recent_activity': '最近活动',
        'home.featured_companies': '推荐企业',
        'home.featured_events': '推荐活动',
        'home.upcoming_schedule': '即将到来的日程',
        'home.view_all': '查看全部',
        'home.more': '更多',
        'home.loading': '加载中...',
        'home.no_activity': '暂无记录的活动。',
        'home.no_activity_hint': '完成问卷或记录活动后将显示在这里。',
        'home.record_activity': '记录活动',
        // 🔥🔥🔥 [2026-01-09 新增] 首页推荐相关键值
        'home.recommended_for_you': '为您推荐',
        'home.recommended_companies': '推荐企业',


        
        'company.search': '搜索企业',
        'company.filter': '筛选企业',
        'company.details': '企业详情',
        'company.contact': '联系方式',
        'company.interests': '兴趣',
        'company.products': '产品',
        'company.services': '服务',
        'company.matchScore': '匹配分数',
        'company.requestMeeting': '请求会议',
        'company.website': '网站',
        'company.email': '邮箱',
        'company.phone': '电话',
        'company.chat_btn': '咨询聊天',
        'company.map_btn': '查看展位地图',
        'company.booth': '展位',
        'company.booth_tbd': '待定',
        'company.tab_overview': '概况',
        'company.tab_products': '产品',
        'company.video_title': '宣传视频',
        'company.video_view': '观看视频',
        'company.tab_news': '新闻',
        'company.info_employees': '员工数',
        'company.info_founded': '成立年份',
        'company.info_location': '位置',
        'company.info_industry': '行业',
        'company.service_title': '主要服务',
        'company.empty_products': '暂无产品信息',
        'company.empty_news': '暂无新闻信息',
        'company.loading': '正在加载企业信息...',
        'company.error_not_found': '找不到企业信息',
        'company.online_status': '在线',
        'company.no_description': '暂无企业介绍。',
        'company.alert_login_required': '请先登录。',
        'company.alert_chat_error': '创建聊天室失败。',


        'companies.title': '参展企业列表',
        'companies.search_placeholder': '搜索企业名称、行业、标签...',
        'companies.filter_title': '选择类别',
        'companies.filter_all': '所有类别',
        'companies.search_label': '搜索',
        'companies.sort_label': '排序',
        'companies.filter_active_count': '{count} 个类别',
        'companies.filters_active': '已应用 {count} 个筛选',
        'companies.clear_filters': '清除筛选',
        'companies.sort_name_asc': '名称 (A-Z)',
        'companies.sort_name_desc': '名称 (Z-A)',
        'companies.sort_booth_asc': '展位号 (低-高)',
        'companies.sort_booth_desc': '展位号 (高-低)',
        'companies.result_count': '共 {count} 家企业',
        'companies.empty_title': '没有注册的企业',
        'companies.empty_search': '无搜索结果',
        'companies.semantic_search': 'AI语义搜索', // 🔥 [2026-06-10 B-2] 자연어(시맨틱) 검색 라벨
        'companies.reset': '重置',
        'companies.apply': '应用',
        'companies.end_of_list': '暂无更多企业',
        'matching.title': 'AI企业匹配',
        'matching.subtitle': '寻找最佳商业伙伴',
        'matching.mode_ai': 'AI推荐',
        'matching.mode_search': '直接搜索',
        'matching.search_placeholder': '搜索技术、产品、企业名称...',
        'matching.btn_search': '搜索',
        'matching.result_count': '{count}个推荐企业',
        'matching.analyzed': '分析完成',
        'matching.empty_ai': '暂无关注的企业',
        'matching.empty_ai_desc': '请完善个人资料。',
        'matching.empty_search': '无搜索结果',
        'matching.empty_search_desc': '请尝试其他关键词。',
        'matching.section_ai': 'AI综合分析推荐',
        'matching.section_desc': '业务内容相似的企业',
        'matching.section_prod': '主要产品相似的企业',
        'matching.action_chat': '发送消息',
        'matching.booth_prefix': '展位',
        'matching.logo_alt': '徽标',
        'matching.no_description': '暂无简介。',
        'matching.action_location': '查看位置',
        'matching.top_rank': 'TOP {rank}',
        'matching.loading': 'AI分析中...',
        'matching.info_message': 'DSK AI Valentine 会分析您的个人资料和兴趣，并为您推荐最合适的合作伙伴。',
        'matching.alert_login_required': '需要登录。',
        'matching.alert_load_error': '加载失败。',
        'matching.alert_min_chars': '请输入至少2个字符。',
        'matching.alert_search_error': '搜索出错。',
        'matching.alert_chat_error': '创建聊天室失败。',
        'matching.alert_booth_location': '展位位置: {booth}',
        'matching.buyer_title': 'AI兴趣企业探索',
        'matching.buyer_subtitle': '分析您的兴趣，找到了最匹配的企业。',
        'matching.buyer_mode_ai': '兴趣匹配',
        'matching.buyer_empty_title': '没有匹配的企业',
        'matching.buyer_empty_desc': '兴趣内容更具体的话，精确度会提升。',
        'matching.buyer_analyzed': '分析完成',
        'matching.buyer_info_message': "DSK AI Valentine 会分析您的个人资料和兴趣，并为您推荐最合适的合作伙伴。",
        'matching.buyer_reason': '与您的兴趣有高度相似性。',
        'matching.section_interest': '与您兴趣相符的企业',
        'matching.section_recommended': '推荐企业',
        'matching.company_reason': 'AI推荐匹配',
        'matching.access_denied_title': '访问受限',
        'matching.access_denied_message': '该功能仅限商务会员使用。',
        'matching.btn_business_upgrade': '申请商务访客',
        'matching.alert_no_booth': '展位位置尚未指定。',
        'matching.pending_approval_title': '等待审批',
        'matching.pending_approval_message': '您的请求正在等待秘书处审批。\n审批通过后即可使用匹配功能。',

        // ─── businessUpgrade ───
        'businessUpgrade.title': '商务会员申请',
        'businessUpgrade.description': '为了使用聊天、匹配和会议功能，\n请填写以下所有信息。',
        'businessUpgrade.profile_label': '头像照片',
        'businessUpgrade.profile_required': '*必填（本人照片）',
        'businessUpgrade.profile_placeholder': '点击上传',
        'businessUpgrade.profile_error': '请上传本人照片',
        'businessUpgrade.company_placeholder': '公司/组织名称 *',
        'businessUpgrade.company_error': '请输入公司名称',
        'businessUpgrade.department_placeholder': '部门（选填）',
        'businessUpgrade.job_title_placeholder': '职位 *',
        'businessUpgrade.job_title_error': '请输入职位',
        'businessUpgrade.card_label': '名片图片',
        'businessUpgrade.card_required': '*必填',
        'businessUpgrade.card_error': '请上传名片图片',
        'businessUpgrade.notice': '提交后需要秘书处审批。审批通过后即可使用聊天、匹配和会议功能。',
        'businessUpgrade.btn_cancel': '取消',
        'businessUpgrade.btn_submit': '申请审批',
        'businessUpgrade.btn_uploading': '上传中...',
        'businessUpgrade.btn_processing': '处理中...',
        'businessUpgrade.success_title': '申请完成',
        'businessUpgrade.success_message': '商务会员申请已完成。\n秘书处审批通过后即可使用聊天、匹配和会议功能。',
        'businessUpgrade.success_confirm': '确认',
        'businessUpgrade.error_file_size': '文件大小不能超过5MB。',
        'businessUpgrade.error_file_type': '只能上传图片文件。',
        'businessUpgrade.error_profile_required': '头像照片为必填项，请上传本人照片。',
        'businessUpgrade.error_card_required': '名片图片为必填项。',
        'businessUpgrade.error_submit': '申请处理中出现错误，请重试。',
        'businessUpgrade.success_toast': '申请已完成！',

        'matching.hero_title': 'AI匹配',
        'matching.hero_subtitle': '根据您的个人资料推荐最佳商业伙伴。',
        'matching.btn_ai_analysis': '兴趣领域分析',
        'matching.btn_direct_search': '直接搜索',
        'matching.result_count_suffix': '个推荐企业',
        'matching.before_title': '匹配尚未开始',
        'matching.before_desc': '今年的匹配尚未开始。匹配开始后将为您推荐企业。',
        'matching.calculating_title': 'AI分析中...',
        'matching.calculating_desc': '正在寻找最佳商业伙伴。',
        'matching.calculating_progress': '分析进行中...',
        'matching.calculating_almost': '即将完成...',
        'matching.search_empty_title': '无搜索结果',
        'matching.search_empty_desc': '请尝试其他关键词搜索。',
        'matching.info_modal_title': 'AI企业匹配指南',
        'matching.reason_buyer': 'AI分析: 与您的兴趣相关的企业。',
        'matching.reason_default': 'AI分析: 与您的兴趣相关的企业。',
        'matching.reason_high': '与您的关注领域高度匹配的企业',
        'matching.reason_high_cat': '在"{category}"领域与您的关注高度匹配',
        'matching.reason_mid': '与您的关注领域相关的企业',
        'matching.reason_mid_cat': '"{category}"领域与您的关注相关',
        'matching.reason_low': '可能带来新商机的企业',
        'matching.reason_low_buyer': '有潜力成为您供应商的企业',
        'matching.show_more': '查看更多',
        'matching.score_explain': '匹配分数（%）综合了类别契合度、关注领域一致性和企业信息完整度，分数越高越匹配。',
        'matching.no_company_name': '无公司名称',
        'matching.no_category': '未指定类别',
        'matching.booth_tbd': '待定',
        'matching.btn_location': '查看位置',
        'matching.btn_meeting': '请求会议',
        'matching.btn_message': '消息',
        'matching.booth': '展位',
        'matching.section_title': '综合推荐',
        'matching.no_interest_title': '请输入您的兴趣领域',
        'matching.no_interest_desc': '请在问卷中输入您感兴趣的技术/产品，以便进行AI匹配。',
        'matching.no_interest_btn': '填写问卷',
        'matching.buyer_no_interest_title': '请输入您的兴趣领域',
        'matching.buyer_no_interest_desc': '请在问卷中输入您感兴趣的技术/产品，以便进行AI匹配。',
        'matching.buyer_no_interest_btn': '填写买家问卷',
        'matching.alert_min_chars': '请输入至少2个字符。',
        'matching.alert_search_error': '搜索时发生错误。',
        'matching.search_result_count_suffix': '个搜索结果',



        'meeting.schedule': '日程',
        'meeting.upcoming': '即将到来的会议',
        'meeting.past': '过去的会议',
        'meeting.cancel': '取消',
        'meeting.reschedule': '重新安排',
        'meeting.confirm': '确认',
        'meeting.location': '地点',
        'meeting.time': '时间',
        'meeting.type': '类型',
        'meeting.participants': '参与者',
        'meeting.notes': '备注',
        'meetings.title': '我的日程',
        'meetings.view_list': '列表',
        'meetings.view_calendar': '日历',
        'meetings.empty': '没有即将到来的会议。',
        'meetings.go_book': '预约会议',
        'meetings.location_tbd': '地点待定',
        'meetings.upcoming': '即将到来的会议',
        'meetings.no_schedule': '这一天没有日程',
        'meetings.no_upcoming': '没有即将到来的会议',
        'meetings.schedule_new': '预约新的会议',
        'meeting.modal_title': '提议会议',
        'meeting.target_company': '目标企业',
        'meeting.field_title': '会议主题',
        'meeting.field_date': '日期',
        'meeting.field_time': '时间',
        'meeting.field_location': '地点（可选）',
        'meeting.default_title': '商务会议',
        'meeting.booth_location': '希望在贵公司展位会面。',
        'meeting.placeholder_title': '例：产品介绍及合作洽谈',
        'meeting.placeholder_location': '例：A馆102号展位',
        'meeting.notice_proposal': '您的会议提议将发送给对方，对方接受后将自动添加到日程中。',
        'meeting.sending_proposal': '正在发送会议提议...',
        'meeting.btn_send_proposal': '发送会议提议',
        'meeting.btn_go_to_chat': '前往聊天室',
        'meeting.proposal_sent_title': '提议已发送',
        'meeting.confirm_navigate_to_chat': '会议提议已成功发送。是否前往聊天室查看对方的回复？',
        'meeting.toast_proposal_sent': '已发送会议提议。',
        'meeting.error_title_required': '请输入会议主题。',
        'meeting.error_date_required': '请选择日期。',
        'meeting.error_time_required': '请选择时间。',
        'meeting.error_no_company': '找不到企业信息。',
        'meeting.error_chat_creation': '创建聊天室失败。',
        'meeting.error_invalid_datetime': '无效的日期或时间。',
        'meeting.error_proposal_failed': '发送会议提议失败。',
        'meeting.field_notes': '会议议题及留言',
        'meeting.placeholder_notes': '请输入要传达给对方的详细议题或留言。',
        // 🔥 [2026-04-22] MeetingProposalModal 缺失键补充
        'meeting.matrix_label': '选择候选时间',
        'meeting.loading_matrix': '正在加载可用时间…',
        'meeting.no_active_event_notice': '需要有进行中的活动期间才能提议。',
        'meeting.reload_matrix_button': '重新加载时间表',
        'meeting.max_candidates_notice': '最多可选择 {count} 个时间。',
        'meeting.select_slots_required': '请选择候选时间。',
        // 🔥 [2026-04-23] ChatRoom 会议提议卡片缺失键
        'meeting.expires_at_label': '截止',
        'meeting.accept_slot_button': '接受此时间',
        // 🔥 [2026-04-22] MeetingAvailabilityGrid 缺失键补充
        'meeting.matrix_status_available': '可用',
        'meeting.matrix_status_me_busy': '与我的日程冲突',
        'meeting.matrix_status_other_busy': '与对方日程冲突',
        'meeting.matrix_status_room_full': '无可用房间',
        'meeting.matrix_status_past': '已过期',
        'meeting.matrix_status_seminar': '研讨会 (不可选)',
        'meeting.selected_slots_count': '已选候选',
        'meeting.recommended_slots_label': '推荐候选',
        'meeting.matrix_time': '时间',
        'meeting.matrix_selected': '已选',
        'meeting.available_rooms_count': '{count}个房间',
        'meeting.room_selection_title': '各候选时段会议室',
        'meeting.no_available_room_option': '暂无可用会议室',
        'meeting.room_capacity_persons': '{count}人',

        'chat.messages': '消息',
        'chat.send': '发送',
        'chat.typing': '正在输入...',
        'chat.online': '在线',
        'chat.offline': '离线',
        'chat.newMessage': '新消息',
        'chat.readReceipt': '已读',
        'chat.unread': '未读',
        'chat.read_by_other': '已读',
        'chat.unread_by_other': '未读',
        'chat.title_default': '聊天室',
        'chat.input_placeholder': '输入消息...',
        'chat.meeting_detected_title': '检测到会议关键词',
        'chat.confirm_meeting_proposal': '是否发送会议提议卡？',
        'chat.btn_send_card': '发送提议卡',
        'chat.btn_send_text': '作为文本发送',
        'chat.meeting_proposal': '会议提议',
        'chat.meeting_reschedule': '修改提议',
        'chat.btn_accept': '接受',
        'chat.btn_reschedule': '修改',
        'chat.btn_decline': '拒绝',
        'chat.status_accepted': '已接受',
        'chat.status_declined': '已拒绝',
        'chat.status_pending': '等待回复',
        'chat.status_countered': '已发送修改提议',
        'chat.leave_room': '离开聊天室',
        'chat.confirm_leave': '确定要离开聊天室吗？聊天记录将被删除。',
        'chat.alert_leave_error': '离开聊天室失败。',
        'chat.empty_messages': '没有消息',
        'chat.empty_messages_desc': '对方可能不接受或回复延迟。建议亲临现场，此功能仅作为辅助手段。',
        'chat.meeting_proposal_title': '📅 会议提议',
        'chat.meeting_reschedule_title': '🕒 日程变更提议',
        'chat.meeting_proposal_format': '{title}\n时间: {datetime}\n地点: {location}',
        'chat.meeting_reschedule_format': '日程变更提议: {title}\n变更时间: {datetime}\n地点: {location}',
        'chat.meeting_keywords': '会议,开会,见面,见个面,谈谈,商谈,日程,约会',
        // 🔥 [新增] 聊天加载＆会议关键词检测模态
        'chat.loading': '正在加载聊天...',
        'chat.ask_send_meeting_card': '检测到会议关键词。是否发送会议卡？',
        'chat.btn_no_thanks': '不用了',
        'chat.toast_meeting_sent': '已发送会议提议。',
        'chat.toast_meeting_accepted': '会议已接受。',
        'chat.toast_meeting_declined': '会议已拒绝。',
        'chat.meeting_added_to_calendar': '会议已添加到日程。',
        'chat.modal_meeting_title': '提议会议',
        'chat.modal_reschedule_title': '变更日程',
        'chat.modal_meeting_subject': '主题',
        'chat.modal_meeting_subject_placeholder': '例: 合作伙伴讨论',
        'chat.modal_date': '日期',
        'chat.modal_time': '时间',
        'chat.modal_location': '地点',
        'chat.modal_location_placeholder': '例: A馆102号展位',
        'chat.modal_original_title': '原定日程',
        'chat.modal_new_date': '变更日期',
        'chat.modal_new_time': '变更时间',
        'chat.modal_new_location': '变更地点',
        'chat.btn_send_proposal': '发送提议',
        'chat.btn_send_reschedule': '发送变更提议',
        'chat.alert_meeting_required': '主题、日期和时间为必填项。',
        'chat.alert_no_receiver': '找不到对话伙伴。',
        'chat.error_invalid_datetime': '无效的日期或时间。',
        'chat.location_tbd': '地点待定',
        'chat.alert_meeting_sent': '已发送会议提议。',
        'chat.alert_meeting_send_error': '发送会议提议失败。',
        'chat.alert_reschedule_sent': '已发送日程变更提议。',
        'chat.other_party': '对方',
        'chat.meeting_accepted_message': '已接受会议提议。',
        'chat.meeting_declined_message': '已拒绝会议提议。',
        'chat.alert_no_meeting_id': '找不到会议信息。',
        'chat.alert_meeting_accepted': '会议已接受。',
        'chat.alert_meeting_declined': '会议已拒绝。',
        'chat.alert_meeting_accept_error': '接受会议时出错: {message}',
        'chat.alert_meeting_decline_error': '拒绝会议时出错: {message}',
        'chat.alert_no_reschedule_info': '找不到原定会议信息。',
        'chat.alert_reschedule_error': '没有可变更的会议信息。',
        'chat.preview_image': '📷 照片',
        'chat.preview_file': '📎 文件',
        'chat.preview_system': '📢 系统消息',
        'chat.preview_meeting_reschedule_sent': '🕒 您发送了日程变更提议。',
        'chat.preview_meeting_reschedule_received': '🕒 收到日程变更提议。',
        'chat.preview_meeting_rescheduled_sent': '🕒 您接受了日程变更。',
        'chat.preview_meeting_rescheduled_received': '🕒 会议日程已变更。',
        'chat.preview_meeting_cancelled_sent': '🚫 您已取消会议。',
        'chat.preview_meeting_cancelled_received': '🚫 会议已取消。',
        'chat.status_message_accepted': '{name}接受了会议。',
        'chat.status_message_declined': '{name}拒绝了会议。',
        'chat.status_message_rescheduled': '{name}接受了日程变更。',
        'chat.status_message_cancelled': '{name}取消了会议。',
        'chat.status_message_time': '时间',
        'chat.status_message_location': '地点',
        'chat.status_message_reason': '原因',

        'notifications.title': '通知',
        'notifications.mark_all_read': '全部标为已读',
        'notifications.mark_all_read_success': '所有通知已标为已读。',
        'notifications.empty': '没有新通知。',
        'notifications.swipe_delete_hint': '向左滑动删除',
        'notifications.time_just_now': '刚刚',
        'notifications.time_minutes_ago': '{count}分钟前',
        'notifications.time_hours_ago': '{count}小时前',
        'notifications.new_arrived': '🔔 收到新通知！',
        'notifications.delete_failed': '删除通知失败。',
        'notifications.load_failed': '加载通知失败。',
        'notifications.default_title': '通知',
        'notifications.meeting_default_title': '会议',
        'notifications.meeting_proposed.title': '会议提议',
        'notifications.meeting_proposed.message': '{name}为{title}提出了候选时间。',
        'notifications.meeting_accepted.title': '会议已接受',
        'notifications.meeting_accepted.message': '{name}接受了{title}。{time}',
        'notifications.meeting_rejected.title': '会议已拒绝',
        'notifications.meeting_rejected.message': '{name}拒绝了{title}。{reason}',
        'notifications.meeting_rescheduled.title': '日程变更已接受',
        'notifications.meeting_rescheduled.message': '{name}接受了{title}的日程变更。{time}',
        'notifications.meeting_cancelled.title': '会议已取消',
        'notifications.meeting_cancelled.message': '{title}已取消。{reason}',
        'notifications.meeting_reminder_1day.title': '明天有会议',
        'notifications.meeting_reminder_1day.message': '{title}安排在明天。{time}',
        'notifications.meeting_reminder_1hour.title': '会议将在1小时后开始',
        'notifications.meeting_reminder_1hour.message': '{title}将在1小时后开始。{time}',
        'notifications.meeting_reminder_10min.title': '会议将在10分钟后开始',
        'notifications.meeting_reminder_10min.message': '{title}将在10分钟后开始。{time}',
        'notifications.meeting_pending_reminder.title': '会议回复待处理',
        'notifications.meeting_pending_reminder.message': '您尚未回复{title}。',
        'notifications.meeting_expired_reminder.title': '会议提议已过期',
        'notifications.meeting_expired_reminder.message': '{title}的提议已过期。',
        'notifications.survey_reminder.title': '问卷请求',
        'notifications.survey_reminder.message': '请填写{title}相关问卷。',
        'notifications.event_reminder.title': '活动提醒',
        'notifications.event_reminder.message': '{title}即将开始。{time}',

        // 🔥🔥🔥 미팅 미리보기 - 보낸사람용
        'chat.preview_meeting_sent': '📅 您发送了会议提议。',
        'chat.preview_meeting_accepted_sent': '✅ 您接受了会议。',
        'chat.preview_meeting_rejected_sent': '❌ 您拒绝了会议。',

        // 🔥🔥🔥 미팅 미리보기 - 받는사람용
        'chat.preview_meeting_received': '📅 收到会议提议。',
        'chat.preview_meeting_accepted_received': '✅ 会议已被接受。',
        'chat.preview_meeting_rejected_received': '❌ 会议已被拒绝。',

        // 기존 키 (하위 호환성)
        'chat.preview_meeting': '📅 收到会议提议。',
        'chat.preview_meeting_accepted': '✅ 会议已接受。',
        'chat.preview_meeting_rejected': '❌ 会议已拒绝。',
        'chat.preview_new_message': '📅 您有新消息。',
        'chat.pending_title': '等待审批',
        'chat.pending_message': '您的请求正在等待秘书处审批。\n审批通过后即可使用聊天功能。',
        'chat.access_denied_title': '会员专用功能',
        'chat.guest_message': '聊天功能仅限会员使用。',
        'chat.visitor_message': '聊天、匹配、会议请求功能仅限商务会员使用。\n是否申请成为商务会员？',
        'chat.alert_load_error': '加载聊天室失败。',
        'chat.alert_send_error': '发送消息失败。',
        'chat.alert_reschedule_send_error': '发送变更提议失败: {message}',
        'chat.view_profile': '查看资料',
        'chat.unknown_user': '未知用户',
        'chat.profile_title': '个人资料',
        'chat.business_card': '名片',
        'chat.click_to_enlarge': '点击放大',
        // 🔥🔥🔥 [2026-01-09 新增] 文件附件相关键值
        'chat.max_files_exceeded': '最多只能选择{count}个文件。',
        'chat.file_too_large': '文件大小超过10MB。',
        'chat.file_type_not_allowed': '不支持的文件格式。',
        'chat.upload_cancelled': '上传已取消。',
        'chat.file_sent': '文件发送成功。',
        'chat.files_sent': '已发送{count}个文件。',
        'chat.file_send_error': '文件发送失败。',
        'chat.attach_file': '附加文件',
        'chat.add_caption': '添加说明（可选）',
        'chat.drop_files_here': '将文件拖放到此处',
        'chat.max_files_info': '最多5个文件，每个不超过10MB',
        'chat.download': '下载',
        'chat.preview_not_available': '无法预览。',

        // 🔥🔥🔥 [2026-01-09 新增] 语音消息相关键值
        'chat.voice_permission_denied': '需要麦克风权限。',
        'chat.voice_message': '语音消息',
        'chat.voice_sent': '语音消息已发送。',
        'chat.voice_send_error': '语音消息发送失败。',

        // 🔥🔥🔥 [2026-01-09 新增] 视频消息相关键值
        'chat.video_permission_denied': '需要摄像头权限。',
        'chat.video_message': '视频消息',
        'chat.video_sent': '视频消息已发送。',
        'chat.video_send_error': '视频消息发送失败。',
        'chat.record_video': '录制视频',
        'chat.retake': '重新录制',
        'chat.start_recording': '开始录制',


        'activities.start_conversation': '开始对话',
        'activity.tab_chat': '聊天',
        'activity.tab_match': '匹配',
        'activity.empty_chat': '暂无聊天记录',
        'activity.empty_chat_sub': '浏览企业并开始对话吧',
        'activity.explore_btn': '浏览企业',
        'events.explore_btn': '探索活动',
        'activities.tab_chats': '聊天',
        'activities.tab_matching': '匹配',
        'activities.loading_chats': '正在加载聊天列表...',
        'activities.loading_matches': '正在分析匹配信息...',
        'activities.empty_chats': '暂无聊天记录',
        'activities.empty_chats_desc': '浏览企业并开始对话吧。',
        'activities.empty_matches': '暂无推荐企业',
        'activities.empty_matches_desc': '更新个人资料以获得更精准的推荐。',
        'activities.btn_explore': '浏览企业',
        'activities.btn_retry': '重试',
        'activities.btn_preferences': '更新偏好',
        'activities.btn_send_message': '发送消息',
        'activities.no_name': '无名称',
        'activities.no_messages': '暂无消息。',
        'activities.no_company_name': '未知企业',
        'activities.no_industry': '未知行业',
        'activities.booth_prefix': '展位',
        'activities.time_just_now': '刚刚',
        'activities.time_minutes_ago': '{minutes}分钟前',
        'activities.time_hours_ago': '{hours}小时前',
        'activities.time_days_ago': '{days}天前',
        'activities.error_load_matches': '加载匹配信息失败。',
        'activities.delete_chat': '删除聊天室',
        'activities.delete_chat_title': '退出聊天室',
        'activities.delete_chat_message': '确定要退出"{name}"吗？\n聊天记录将被删除且无法恢复。',
        'activities.confirm_delete': '确定要退出此聊天室吗？\n聊天记录将无法恢复。',
        'activities.delete_confirm': '退出',
        'activities.delete_failed': '删除聊天室失败。',



        'chatbot.title': 'Valentine',
        'chatbot.subtitle': 'DSK',
        'chatbot.welcome': '你好！👋\n\n我是DSK AI策展人。\n请询问有关公司或展位位置的信息！',
        'chatbot.placeholder': '输入消息...',
        'chatbot.suggestions_label': '推荐问题',
        'chatbot.q1': '洗手间在哪里？',
        'chatbot.q2': '推荐无人机企业',
        'chatbot.q3': '根据我的兴趣推荐企业',
        'chatbot.clear': '清除',
        'chatbot.close': '关闭',
        'chatbot.relatedCompanies': '相关企业推荐',
        'chatbot.fold': '收起',
        'chatbot.unfold': '展开',
        'chatbot.detail': '详情',
        'chatbot.booth': '展位',
        'chatbot.thinking': '思考中...',
        'chatbot.error': '发生错误。',
        'chatbot.confirmClear': '删除对话？',
        'chatbot.resetMessage': '对话已重置。',
        'chatbot.responding': '回复中...',
        'chatbot.nav_error': '抱歉，导航中发生了错误。',
        'chatbot.lang_change_ko': '한국어로 변경',
        'chatbot.lang_change_en': 'Change to English',
        'chatbot.lang_change_ja': '日本語に変更',
        'chatbot.lang_change_zh': '切换到中文',
        'chatbot.lang_accept_ko': '네, 한국어로 변경할게요',
        'chatbot.lang_accept_en': 'Yes, change to English',
        'chatbot.lang_accept_ja': 'はい、日本語に変更します',
        'chatbot.lang_accept_zh': '是的，切换到中文',
        'chatbot.lang_decline': '不，保持原样',
        'chatbot.meeting_header': '商务会议预约',
        'chatbot.meeting_reserve': '预约会议',
        'chatbot.more_companies': '其他 {count} 家企业',
        'chatbot.survey_shortcut': '前往问卷',
        'chatbot.send_failed': '发送失败',

        // 🔥 [新增] 3D展位地图相关键值
        'map.exhibition_hall_title': '{venueFullName}',
        'map.exhibition_subtitle': '{eventName}',
        'map.passable': '通道',
        'map.search_result': '搜索结果',
        'map.selected': '已选择',
        'map.no_info': '暂无信息',
        'map.more_companies': '+{count}家企业',
        'map.rotate_left': '向左旋转',
        'map.rotate_right': '向右旋转',
        'map.reset_view': '重置视图',
        'map.entrance': '入口',
        'map.exit': '出口',
        'map.toilet': '洗手间',
        'map.booths_showing': '{count}个展位显示中',
        'map.reset': '重置',
        'map.facility_desc': '为观众提供的便利设施。',
        'map.tap_to_highlight_all': '点击以显示所有位置',
        'map.places': '处',
        'map.no_company_info': '暂无注册的企业信息。',
        'map.search_placeholder': '搜索展位号或公司名...',
        'map.loading_map': '正在加载地图数据...',
        'map.loading_3d': '正在加载3D视图...',
        'map.load_failed': '地图数据加载失败',
        'map.preparing': '正在准备展位地图...',
        'map.coming_soon_title': '🗺️ 展位地图即将开放',
        'map.coming_soon_message': '此功能将于2月2日开放。',
        'map.alert_select_both': '请同时选择出发地和目的地。',
        'map.alert_no_route': '目前无法找到有效路线。',
        'map.alert_route_error': '路线搜索中发生错误。',
        'map.alert_invalid_booth': '展位号不存在，请重新确认。',
        'map.nav_title': '🧭 导航',
        'map.nav_reset': '重置',
        'map.nav_select_start': '选择出发地',
        'map.nav_select_end': '选择目的地',
        'map.nav_routing': '路线导航中',
        'map.nav_hide': '隐藏',
        'map.nav_calculating': '正在计算路线...',
        'map.nav_start': '开始导航',
        'map.nav_set_start': '出发地',
        'map.nav_set_end': '目的地',
        'map.company_detail': '企业详情',
        'map.company_count': '{count}家企业入驻',
        'map.company_detail_short': '详情',
        'map.booth_count': '{count}个展位',
        'map.company_extra': '另外{count}家',
        'map.optimal_route_active': '最优路线导航中',
        'map.booth_count_label': '{count}个展位',
        'map.approx_distance': '约{distance}m',
        'map.location_prompt_title': '📍 您现在在哪里？',
        'map.location_prompt_desc': '请输入您当前所在的展位号\n以便计算最近路线。',
        'map.location_prompt_placeholder': '例: A01, D18',
        'map.congestion_title': '实时拥挤度',
        'map.congestion_low': '空闲',
        'map.congestion_medium': '一般',
        'map.congestion_high': '拥挤',


        // 🔥 [新增] 展位地图 关注企业巡回
        'boothMap.favoriteRoute': '关注企业路线',
        'boothMap.favoriteTraverse': '关注企业巡回',
        'boothMap.noFavoriteCompanies': '没有关注的企业。',
        'boothMap.routeCalculationFailed': '无法计算路线。',
        'boothMap.routeCalculationError': '计算路线时发生错误。',
      }
    };
  }
}

// ========================================
// 싱글톤 인스턴스 생성
// ========================================
const i18nService = new I18nService();

// ========================================
// 🔥 Named Export와 Default Export를 명확히 분리
// ========================================
export const t = (key, params) => i18nService.translate(key, params);
export const getTexts = (section) => i18nService.getTexts(section);
export const changeLanguage = (lang) => i18nService.changeLanguage(lang);
export const getCurrentLanguage = () => i18nService.getCurrentLanguage();
export const formatDate = (date, opt) => i18nService.formatDate(date, opt);
export const formatNumber = (num, opt) => i18nService.formatNumber(num, opt);
export const formatCurrency = (amt, curr) => i18nService.formatCurrency(amt, curr);
// 🔥 [추가] 카테고리 번역 함수 export
export const translateCategory = (label) => i18nService.translateCategory(label);
// 🔥 [추가] 리스너 관리 함수 export
export const addLanguageListener = (cb) => i18nService.addListener(cb);
export const removeLanguageListener = (cb) => i18nService.removeListener(cb);

// 🔥 Default Export (가장 마지막에 확실하게 내보냄)
export default i18nService;
